import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Building2, Brain, Search, Globe, Loader2, Copy, ClipboardList, Mail, Trash2, ChevronDown, ChevronUp, ExternalLink, AlertCircle, AlertTriangle, User, FlaskConical, Send, DollarSign, Ban, CheckCircle, LayoutGrid, TableProperties, Share2, ArrowLeft, ChevronRight, Clock, X, Zap, Lock, ShieldOff, Truck, Pencil, MessageSquarePlus, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import SpamCheckChip, { SpamTriagePanel } from '../components/SpamCheckChip';
import { canonicalPharmaKey } from '@/utils/pharmaAliases';
import { isOutreachAllowed, findSpecialists, summarizeInterests, areasOverlap, exclusionCoversAsset } from '@/utils/interestMatch';
import { stageGateBlocks } from '@/utils/stage';
import { sharesBrand, merckFamily, filterMerckFamily } from '@/utils/companyIdentity';
import { FunnelStrip, computeFunnel, isTargetRefused, softReason as softRefusalReason } from '@/components/mapping/FunnelStrip';
import { ReinstateDialog } from '@/components/mapping/ReinstateDialog';
import { HeldBackBanner } from '@/components/mapping/HeldBackBanner';
import { FunnelListDialog } from '@/components/mapping/FunnelListDialog';
import { SkippedPanel } from '@/components/mapping/SkippedPanel';
import { isCommercialPartnerTarget } from '@/utils/commercialPartner';
import BulkSendProgress from '@/components/BulkSendProgress';
import SmartRoutingPreviewPanel from '@/components/SmartRoutingPreviewPanel';

// Split an AI-generated email blob into { subject, body }. The blob has
// "Subject: …" on the first non-empty line followed by the body. If the
// pattern doesn't match, the whole blob is treated as body.
function splitEmail(text = '') {
  const lines = text.split('\n');
  const idx = lines.findIndex(l => l.toLowerCase().startsWith('subject:'));
  if (idx === -1) return { subject: '', body: text };
  const subject = lines[idx].replace(/^subject:\s*/i, '').trim();
  const body = lines.slice(0, idx).concat(lines.slice(idx + 1)).join('\n').trim();
  return { subject, body };
}
function joinEmail(subject, body) {
  return `Subject: ${subject || ''}\n\n${body || ''}`;
}

const API = '/api';
const _stripEmailHtml = (t) => (/<\/?(p|br|div)\b/i.test(t || '') ? String(t).replace(/<\s*br\s*\/?>/gi, '\n').replace(/<\/\s*p\s*>/gi, '\n\n').replace(/<[^>]{1,120}>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\n{3,}/g, '\n\n').trim() : t);

// Region options for AI Mapping. Each item maps to the prompt's geographic
// filter (sent to backend as a comma-joined string). LATAM, Canada,
// Australia/NZ, Taiwan added per user request — these are real BD markets
// distinct from the ones we previously offered.
const REGION_OPTIONS = [
  'USA',
  'Canada',
  'Europe',
  'Japan',
  'South Korea',
  'Taiwan',
  'China',
  'India',
  'Southeast Asia',
  'Australia / New Zealand',
  'Israel',
  'Middle East',
  'LATAM',
];

// Compact label for the trigger button. "" / [] = Global.
const summarizeRegions = (sel) => {
  if (!sel || sel.length === 0) return 'Global';
  if (sel.length === 1) return sel[0];
  if (sel.length === 2) return sel.join(' + ');
  return `${sel.length} regions`;
};

// Multi-select region picker (popover + checkboxes). Tiny, replaces the
// old single-select <select> while staying inline-friendly in tight rows.
function RegionPicker({ value, onChange, testId }) {
  const sel = Array.isArray(value) ? value : (value ? [value] : []);
  const toggle = (region) => {
    if (sel.includes(region)) onChange(sel.filter(r => r !== region));
    else onChange([...sel, region]);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-7 text-[10px] border border-slate-200 rounded-md px-2 bg-white text-slate-600 hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-violet-300 flex items-center gap-1"
          data-testid={testId}
        >
          <Globe className="w-3 h-3" />
          {summarizeRegions(sel)}
          <ChevronDown className="w-2.5 h-2.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 px-1 pb-1.5">
          Geographic focus (any combo)
        </div>
        <div className="flex items-center gap-2 px-1 pb-2 border-b border-slate-100 mb-1.5">
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[10px] text-violet-700 hover:underline"
          >
            Global (clear)
          </button>
          <span className="text-slate-300 text-[10px]">·</span>
          <button
            type="button"
            onClick={() => onChange(['USA', 'Europe'])}
            className="text-[10px] text-violet-700 hover:underline"
          >
            US + EU
          </button>
        </div>
        <div className="max-h-72 overflow-y-auto pr-1">
          {REGION_OPTIONS.map(r => (
            <label
              key={r}
              className="flex items-center gap-2 py-1 px-1 rounded hover:bg-slate-50 cursor-pointer text-xs text-slate-700"
            >
              <Checkbox
                checked={sel.includes(r)}
                onCheckedChange={() => toggle(r)}
                data-testid={`region-opt-${r}`}
              />
              {r}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Curated "Indication Quick-Pick" chips for the AI Mapping page. Clicking a
// chip injects a dense synonym + acquirer list into the asset's extra_instruction
// field so Claude widens recall for that archetype (rare disease, oncology, etc.).
// Source: BD heuristics from real buyer-mapping campaigns (Foresight 2026).
const INDICATION_QUICK_PICKS = [
  {
    key: 'rare-orphan',
    label: '🧬 Rare Disease + Orphan',
    prompt:
      "Focus on pharma and biotechs with commercial-stage or late-stage clinical programs in rare diseases, orphan drugs, ultra-rare conditions, and niche genetic disorders. Surface dedicated rare-disease franchises and historical in-licensers of single-indication orphan assets: Vertex, BioMarin, Alexion (AstraZeneca Rare Disease), Ultragenyx, Sarepta, Travere, Recordati Rare Diseases, Chiesi Global Rare Diseases, Ipsen Rare Disease, Takeda Rare Genetics & Hematology, Pfizer Rare Disease, Sanofi Rare Disease, Kyowa Kirin, PTC Therapeutics, Mirum, Amicus, Horizon (Amgen Rare Disease), Jazz, Recordati, Orchard, Krystal, Ascendis. Prioritize buyers known to in-license assets with strong genetic causality, biomarker-driven trials, accelerated-approval potential, or pediatric / ultra-orphan economics.",
  },
  {
    key: 'oncology-solid',
    label: '🎗️ Oncology — Solid Tumors',
    prompt:
      "Focus on companies with deep solid-tumor oncology pipelines: TKIs, ADCs, bispecifics, T-cell engagers, immune checkpoint combinations, KRAS / EGFR / HER2 / PD-(L)1 / TROP2 / CLDN18.2 programs, radioligand therapies, neoadjuvant / perioperative trials. Surface active in-licensers and acquirers in oncology: Merck (MSD), AstraZeneca Oncology, Roche / Genentech, Bristol Myers Squibb, Pfizer Oncology, Eli Lilly Oncology, Daiichi Sankyo, Takeda Oncology, Novartis Oncology, GSK Oncology, Gilead / Kite, Amgen Oncology, Johnson & Johnson Oncology, Eisai, BeiGene, Innovent, Junshi Biosciences, Hengrui, HUTCHMED, Bayer Oncology, Boehringer Ingelheim Oncology. Include emerging mid-cap oncology consolidators with active BD.",
  },
  {
    key: 'cns-neuro',
    label: '🧠 CNS / Neurology',
    prompt:
      "Focus on pharma and biotechs in central nervous system disorders: neurodegeneration (Alzheimer's, Parkinson's, ALS, Huntington's), psychiatry (depression, schizophrenia, bipolar, anxiety, PTSD), epilepsy, migraine, neuromuscular disease (DMD, SMA, myasthenia gravis), multiple sclerosis, rare neurogenetic and pediatric-onset CNS disorders. Surface active CNS in-licensers / acquirers: Biogen, Eisai, Lundbeck, Otsuka, Sage Therapeutics, Supernus, Acadia, Neurocrine, Intra-Cellular (J&J), Roche Neuroscience, Eli Lilly Neuroscience, Novartis Neuroscience, AbbVie Neuroscience, Sumitomo Pharma, Sanofi Neurology, Takeda Neuroscience, BMS Neuroscience, Alkermes, UCB, argenx, Karuna (BMS), Cerevel (AbbVie).",
  },
  {
    key: 'cardiometabolic',
    label: '❤️ Cardiometabolic',
    prompt:
      "Focus on companies developing therapies for cardiovascular disease, heart failure, hypertension, dyslipidemia, obesity, type 2 diabetes, NASH / MASH, chronic kidney disease, and rare cardiometabolic disorders. Surface known acquirers / in-licensers: Novo Nordisk, Eli Lilly, Boehringer Ingelheim, AstraZeneca CVRM, Bayer Cardiovascular, Pfizer Internal Medicine, Merck Cardiometabolic, Madrigal, Viking, Altimmune, Structure Therapeutics, Carmot (Roche), Terns, Roche Metabolic, Amgen Cardiovascular, Esperion, Cytokinetics, Ionis, Verve, Tenax, BMS Cardiovascular, Sanofi Cardiometabolic, AbbVie Cardiometabolic. Include incumbents with active obesity / GLP-1 / amylin franchises and known appetite for tuck-in deals.",
  },
  {
    key: 'ophthalmology',
    label: '👁️ Ophthalmology',
    prompt:
      "Focus on pharma and biotechs in ophthalmology: retinal diseases (wet & dry AMD, DR, DME, geographic atrophy), inherited retinal dystrophies, dry eye disease, glaucoma, anterior-segment disease, ocular gene therapy, intravitreal sustained-release platforms, neuro-ophthalmology. Surface active acquirers / in-licensers: Apellis, Astellas Ophthalmology (Iveric Bio), Roche Ophthalmology / Genentech, Regeneron, Bayer Ophthalmology, Bausch + Lomb, Alcon, Santen, Senju, Aerie (Alcon), Spark Therapeutics (Roche), AbbVie Allergan Eye Care, Novartis Ophthalmology, Outlook Therapeutics, Ocular Therapeutix, Coherus, Kala, Oyster Point (Viatris), Glaukos.",
  },
  {
    key: 'immunology',
    label: '🛡️ Immunology / Autoimmune',
    prompt:
      "Focus on companies in immunology and autoimmune disease: rheumatology (RA, lupus, PsA, AS), dermatology (psoriasis, atopic dermatitis, vitiligo, alopecia areata), gastroenterology (IBD, UC, Crohn's, eosinophilic GI), pulmonology (asthma, COPD, eosinophilic asthma), nephrology (lupus nephritis, IgAN, FSGS), allergy, systemic vasculitis, transplant. Surface active immunology in-licensers / acquirers: AbbVie Immunology, J&J Immunology, Sanofi / Regeneron, AstraZeneca Respiratory & Immunology, GSK Specialty, Eli Lilly Immunology, Pfizer Inflammation & Immunology, Novartis Immunology, BMS Immunology, Roche Immunology, Takeda GI, UCB, Galapagos, Boehringer Ingelheim Inflammation, Amgen Inflammation, argenx, Vertex Renal / IgAN, Otsuka Renal, Chinook (Novartis), Travere.",
  },
];

const STAGE_COLORS = {
  'Discovery': 'bg-zinc-100 text-zinc-700',
  'Pre-Clinical': 'bg-slate-100 text-slate-700',
  'Pre-Clinical (IND-enabling)': 'bg-slate-100 text-slate-700',
  'IND': 'bg-sky-100 text-sky-700',
  'Phase 1': 'bg-blue-100 text-blue-700',
  'Phase 1/2': 'bg-blue-100 text-blue-700',
  'Phase 2': 'bg-violet-100 text-violet-700',
  'Phase 3': 'bg-purple-100 text-purple-700',
  'Clinical Stage': 'bg-fuchsia-100 text-fuchsia-700',
  'Commercial': 'bg-emerald-100 text-emerald-700',
  'Platform': 'bg-amber-100 text-amber-700',
};

const SIZE_COLORS = {
  'Large Cap': 'bg-blue-100 text-blue-700',
  'Mid Cap': 'bg-emerald-100 text-emerald-700',
  'Small Cap': 'bg-amber-100 text-amber-700',
  'Approved': 'bg-green-100 text-green-700',
  'Drug Delivery System': 'bg-rose-100 text-rose-700',
};

export default function AIMappingPage() {
  const { companyId: routeCompanyId } = useParams();
  const navigate = useNavigate();
  const isSingleView = !!routeCompanyId;
  const [companies, setCompanies] = useState([]);
  const [assets, setAssets] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [exclusionCounts, setExclusionCounts] = useState({});  // {asset_id: count} for the "Exclusions (N)" chip on each mapping toolbar
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null); // company id
  const [expandedAssetId, setExpandedAssetId] = useState(null); // asset id for mapping results
  const [runningIds, setRunningIds] = useState(new Set()); // asset ids being processed
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Sep 2026 · Asset detail popup (click asset name → view + copy details)
  const [assetDetail, setAssetDetail] = useState(null); // {loading, asset}
  const openAssetDetail = async (a) => {
    setAssetDetail({ loading: true, asset: { asset_name: a.asset_name } });
    try {
      const r = await axios.get(`${API}/assets/${a.id}`, { withCredentials: true });
      setAssetDetail({ loading: false, asset: r.data });
    } catch {
      setAssetDetail({ loading: false, asset: a });
    }
  };
  const ASSET_DETAIL_FIELDS = [
    ['Phase / Stage', (x) => x.phase || x.stage || x.development_stage],
    ['Therapeutic Area', (x) => x.therapeutic_area],
    ['Indication', (x) => x.indication],
    ['Modality', (x) => x.modality],
    ['Target', (x) => x.target],
    ['Mechanism of Action', (x) => x.moa || x.mechanism_of_action],
    ['Description', (x) => x.molecule_description || x.description],
    ['Out-licensing Objective', (x) => x.objectives || x.out_licensing_objective],
    ['Differentiation', (x) => x.differentiation],
    ['Notes', (x) => x.notes],
  ];
  const copyAssetDetails = () => {
    const x = assetDetail?.asset || {};
    const lines = [`Asset: ${x.asset_name || ''}`];
    ASSET_DETAIL_FIELDS.forEach(([label, get]) => {
      const v = (get(x) || '').toString().trim();
      if (v) lines.push(`${label}: ${v}`);
    });
    navigator.clipboard.writeText(lines.join('\n'))
      .then(() => toast.success('Asset details copied to clipboard'))
      .catch(() => toast.error('Copy failed'));
  };
  const [documents, setDocuments] = useState([]);
  const [generatingEmail, setGeneratingEmail] = useState({});
  const [emails, setEmails] = useState({});
  const [pharmaContacts, setPharmaContacts] = useState([]);
  const [companyProfiles, setCompanyProfiles] = useState([]);
  const [warmLeadsDialog, setWarmLeadsDialog] = useState({ open: false, label: '', contacts: [] });
  const [shareDialog, setShareDialog] = useState({ open: false, mapping: null });
  const [shareEmails, setShareEmails] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sharing, setSharing] = useState(false);

  // Bulk-exclude (paste-an-exclusion-list workflow). State scoped per-mapping
  // by stashing the mapping object inside `bulkExcludeDialog.mapping`.
  const [bulkExcludeDialog, setBulkExcludeDialog] = useState({ open: false, mapping: null });
  const [bulkExcludeNames, setBulkExcludeNames] = useState('');
  const [bulkExcludeComment, setBulkExcludeComment] = useState('');
  const [bulkExcludeRunning, setBulkExcludeRunning] = useState(false);
  const [bulkExcludeResult, setBulkExcludeResult] = useState(null);
  // When ticked, the names are saved to the asset's persistent exclusion list
  // (survives re-mapping) AND retroactively applied to every existing mapping
  // for that asset. When unticked, behaviour is the legacy per-mapping bulk
  // exclusion only.
  const [bulkExcludePersistent, setBulkExcludePersistent] = useState(false);
  const [bulkExcludeAlreadyInTouch, setBulkExcludeAlreadyInTouch] = useState(false);
  // List of persistent exclusions for the asset of the currently-open dialog,
  // shown as removable chips so the user can audit / drop names.
  const [persistentExclusions, setPersistentExclusions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [outreachDialog, setOutreachDialog] = useState({ open: false, companyId: '', companyName: '', assetId: '', assetName: '', mappingId: '' });
  // Skipped Panel state (Aug 2026 · Issue #2 · surface silent routing
  // rejections so the user sees WHY a mapped pharma isn't in the send list).
  // Each entry: { company_name, country, reason, reason_human, action_url, action_label }
  const [skippedTargets, setSkippedTargets] = useState([]);
  const [outreachPreparing, setOutreachPreparing] = useState(null);
  const [reinstateDlg, setReinstateDlg] = useState(null); // { items:[{company_name, rationale, defaultTier, why}], ctx:{biotech_company_id, asset_id, mapping_id} }
  // v33 (Feb 2026) — Explainable-AI decision-summary drawer state.
  const [decisionSummary, setDecisionSummary] = useState({ open: false, mappingId: '', companyName: '', loading: false, data: null, error: '', feedbackOpen: false, feedbackComment: '', feedbackSubmitting: false });
  const [outreachEmails, setOutreachEmails] = useState({}); // { targetCompanyName: { email, contacts: [], generating, sent: [] } }
  // Aug 2026 · Modal-level Founder Context — pasted into a textarea at the
  // TOP of the Send Outreach modal. Injected into every per-company email
  // generation call for THIS batch. Falls back to the asset's persisted
  // outreach_context when this is left empty. Persists to the asset via
  // PATCH /api/assets/{id}/outreach-context on user click of "Save & apply".
  const [outreachDialogContext, setOutreachDialogContext] = useState('');
  const [outreachContextSaving, setOutreachContextSaving] = useState(false);
  const [sendingEmails, setSendingEmails] = useState({});
  // Per-contact failure reasons from the last send attempt — persisted in the
  // row so a refused/failed email is never a silent "skip".
  const [sendErrors, setSendErrors] = useState({});
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const pollRef = useRef({});
  // Unified Investor Outreach state (combines match + email + send)
  const [investorDialog, setInvestorDialog] = useState({ open: false, companyId: '', companyName: '', assetId: '', assetName: '' });
  const [investorMatchLoading, setInvestorMatchLoading] = useState(false);
  const [investorMatchResult, setInvestorMatchResult] = useState(null);
  const [investorEmailText, setInvestorEmailText] = useState('');
  const [investorBulkTask, setInvestorBulkTask] = useState(null);
  const investorPollRef = useRef(null);
  const [regionFilter, setRegionFilter] = useState({}); // { [assetId]: 'India' }
  // Per-asset "Indication Quick-Pick" hint text — surfaced as a collapsible
  // panel beside the Run button. Stored by runKey (assetId || companyId) so
  // each asset row keeps its own hint. Passed to the backend as
  // `extra_instruction` on the next POST /api/ai/map-targets.
  const [extraInstructions, setExtraInstructions] = useState({}); // { [runKey]: 'prompt text' }
  const [openHintPanels, setOpenHintPanels] = useState(new Set()); // runKeys whose hint panel is expanded
  const [trackingStats, setTrackingStats] = useState(null);
  const [contactedCompanies, setContactedCompanies] = useState(new Set()); // lowercased company names already emailed
  const [sentHistoryFailed, setSentHistoryFailed] = useState(false); // Oct 2026 · block sends when "already sent" could not be loaded
  const [exclusionsByCompany, setExclusionsByCompany] = useState({}); // { companyNameLower: { company_name, reasons: [], details: {} } }
  const [dncContactEmails, setDncContactEmails] = useState({}); // { emailLower: { name, company, reason } } — per-contact DNC list
  // Lightweight index of EVERY biotech record (any source — conference imports,
  // bio_international, manual, etc.). Used solely by `findBiotechMatch` so AI-
  // mapping targets can be linked to biotech profiles regardless of how the
  // biotech got into the DB. Kept separate from `companies` because that list
  // intentionally filters to source=manual for the visible table/cards.
  const [allBiotechIndex, setAllBiotechIndex] = useState([]); // [{ id, company_name, contacts? }]
  // 'loading' | 'ready' | 'failed' — gates the "pharmas without contacts" count so we
  // never show an inflated number computed before the biotech contact index arrives.
  const [biotechIndexState, setBiotechIndexState] = useState('loading');
  // Per-asset "deal context" — the client's special request (e.g. co-development
  // for a preclinical asset) woven into every AI-generated email. Keyed by
  // eKey (assetId || companyId); persisted on the asset/company record.
  const [dealContext, setDealContext] = useState({});
  const [dealContextOpen, setDealContextOpen] = useState({});
  const [dealContextSaving, setDealContextSaving] = useState({});
  const [showExcludedRelationships, setShowExcludedRelationships] = useState(false); // false = auto-exclude, true = let user re-pick
  // "New companies only" filter for the outreach dialog — hides every target
  // whose name appears in `contactedCompanies` (any past outreach for this
  // biotech, across all assets). Default off so users see the full list, but
  // can be flipped to focus solely on companies that have never been emailed.
  const [showNewCompaniesOnly, setShowNewCompaniesOnly] = useState(false);
  // Companies already emailed for this asset that the user explicitly chose to expand ("Show anyway").
  const [expandedEmailed, setExpandedEmailed] = useState(new Set());
  const [jumpQuery, setJumpQuery] = useState('');
  const [jumpHighlight, setJumpHighlight] = useState('');
  // Visible card order (same filter as the card list) for "Jump to company".
  const visibleCardNames = () => Object.keys(outreachEmails).filter(k => !showNewCompaniesOnly || !contactedCompanies.has(k.trim().toLowerCase()));
  const jumpMatches = (() => {
    const q = jumpQuery.trim().toLowerCase();
    if (!q) return [];
    const names = visibleCardNames();
    if (/^\d+$/.test(q)) {
      const n = parseInt(q, 10);
      return names[n - 1] ? [{ idx: n, name: names[n - 1] }] : [];
    }
    return names.map((name, i) => ({ idx: i + 1, name })).filter(m => m.name.toLowerCase().includes(q)).slice(0, 8);
  })();
  const jumpToCompany = (name) => {
    const el = document.querySelector(`[data-testid="outreach-company-${CSS.escape(name)}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'auto', block: 'start' });
    setJumpHighlight(name);
    setJumpQuery('');
    setTimeout(() => setJumpHighlight(''), 2200);
  };
  // Excluded-list audit dialog — when the user clicks the "X excluded" pill on a
  // campaign header, this opens a dialog listing every excluded target for that
  // mapping (both manual `approval_status=rejected` and auto-exclusions) with the
  // reason / detail so the user can audit/dispute after a campaign has run.
  const [excludedDialogMapping, setExcludedDialogMapping] = useState(null);
  const [funnelDialog, setFunnelDialog] = useState(null);
  // { assetKey: exclusions_by_company } — loaded per latest mapping so the funnel
  // strip knows rule-based exclusions before the outreach dialog is opened.
  const [funnelExclusions, setFunnelExclusions] = useState({});
  // Sep 2026 · authoritative contact check for the funnel. The local index
  // misses people linked only by profile id / email domain or filed under a
  // name variant ("Qilu Pharmaceutical" vs "Qilu Pharmaceutical Co., Ltd."),
  // so "need a contact" disagreed with the outreach dialog. Every target the
  // local index cannot resolve is checked once against the server resolver.
  const [serverContactNames, setServerContactNames] = useState(() => new Set());
  const [serverCheckedKeys, setServerCheckedKeys] = useState(() => new Set());
  const serverCheckRef = useRef({});
  useEffect(() => {
    if (biotechIndexState === 'loading') return;
    const latest = {};
    (mappings || []).filter(m => m.status === 'completed').forEach(m => {
      const k = m.asset_id || m.biotech_company_id;
      if (k && (!latest[k] || new Date(m.created_at) > new Date(latest[k].created_at))) latest[k] = m;
    });
    Object.entries(latest).forEach(async ([k, m]) => {
      if (serverCheckRef.current[k]) return;
      serverCheckRef.current[k] = true;
      const unresolved = (m.target_companies || []).filter(tc => tc.company_name && findMatchingContacts(tc.company_name, tc).length === 0);
      if (!unresolved.length) { setServerCheckedKeys(prev => new Set(prev).add(k)); return; }
      try {
        const profileIds = {};
        unresolved.forEach(t => { if (t.crm_company_id || t.company_profile_id) profileIds[t.company_name] = t.crm_company_id || t.company_profile_id; });
        const { data } = await axios.post(`${API}/outreach-contacts/resolve`, { names: unresolved.map(t => t.company_name), profile_ids: profileIds }, { withCredentials: true, timeout: 45000 });
        const found = Object.entries(data?.by_name || {}).filter(([, rows]) => Array.isArray(rows) && rows.some(r => (r.email || '').trim())).map(([n]) => n.trim().toLowerCase());
        setServerContactNames(prev => { const n = new Set(prev); found.forEach(x => n.add(x)); return n; });
      } catch { /* fall back to local index only */ }
      setServerCheckedKeys(prev => new Set(prev).add(k));
    });
  }, [mappings, biotechIndexState, pharmaContacts]); // eslint-disable-line react-hooks/exhaustive-deps
  const funnelExclusionsRef = useRef({});
  useEffect(() => {
    const latest = {};
    (mappings || []).filter(m => m.status === 'completed').forEach(m => {
      const k = m.asset_id || m.biotech_company_id;
      if (!k) return;
      if (!latest[k] || new Date(m.created_at) > new Date(latest[k].created_at)) latest[k] = m;
    });
    Object.values(latest).forEach(async (m) => {
      const k = m.asset_id || m.biotech_company_id;
      if (funnelExclusionsRef.current[k]) return;
      funnelExclusionsRef.current[k] = 'loading';
    });
    // Oct 2026 · sequential, not parallel — N concurrent status calls saturated
    // the DB pool in prod and 500'd the outreach dialog's sent-history.
    (async () => {
      for (const m of Object.values(latest)) {
        const k = m.asset_id || m.biotech_company_id;
        if (funnelExclusionsRef.current[k] !== 'loading') continue;
        try {
          const { data } = await axios.get(`${API}/ai/outreach-status/${m.biotech_company_id}?asset_id=${m.asset_id || ''}`, { withCredentials: true });
          funnelExclusionsRef.current[k] = data.exclusions_by_company || {};
        } catch { funnelExclusionsRef.current[k] = {}; }
        setFunnelExclusions({ ...funnelExclusionsRef.current });
      }
    })();
  }, [mappings]);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [showHistoryAssetIds, setShowHistoryAssetIds] = useState(new Set()); // assets whose previous runs are unhidden
  const [expandedCampaignIds, setExpandedCampaignIds] = useState(new Set()); // mapping ids of individual past campaigns whose target list is expanded inline
  const [newOnlyMappingIds, setNewOnlyMappingIds] = useState(new Set()); // mapping ids whose target list is filtered to "new since previous run"

  const contactsByCompany = React.useMemo(() => {
    const map = {};
    // Profile-linked contacts are indexed under the PROFILE's name too, so a
    // contact stored as "Lunatus Marketing LLC" (or with a blank company)
    // still surfaces for the "Lunatus" target.
    const profileNameById = {};
    companyProfiles.forEach(cp => { if (cp.id && cp.company_name) profileNameById[cp.id] = cp.company_name; });
    pharmaContacts.forEach(pc => {
      const own = (pc.company_name || '').trim().toLowerCase();
      const viaProfile = (profileNameById[pc.company_profile_id] || '').trim().toLowerCase();
      const keys = new Set([own, viaProfile].filter(Boolean));
      if (!keys.size) return;
      keys.forEach(key => {
        if (!map[key]) map[key] = [];
        map[key].push(pc);
      });
    });
    // Also include company_profiles (imported pharma companies) — BUT skip
    // ones flagged as "no contacts known" stubs, since by definition they
    // have no real contacts. Including them would inflate `findMatchingContacts`
    // counts and suppress the 🚫 / 📋 badges in AI Mapping.
    companyProfiles.forEach(cp => {
      if (!cp.company_name) return;
      if (cp.no_contacts_known === true || cp.stub_source === 'user_known_no_contacts') return;
      const key = cp.company_name.trim().toLowerCase();
      if (!map[key]) map[key] = [cp];
    });
    // Also include biotech companies with their contacts — so when AI mapping
    // surfaces a target like "Junshi Biosciences" via web discovery and that
    // company already exists on the biotech side with contacts, the system
    // recognizes it and allows outreach instead of flagging it "Truly missing".
    // Uses `allBiotechIndex` (every source) so conference-imported biotechs
    // are also covered — `companies` alone is restricted to source=manual.
    //
    // Feb 2026 (v14) — a biotech is treated as "has contacts" if EITHER a
    // discrete contact record exists OR the company itself has a top-level
    // partnering / contact / bd email. Previously we only checked the
    // `contacts` array, which flagged companies like Lee's Pharmaceutical
    // Holdings (company-level `contact_email` populated, no separate
    // contact record) as "pharmas without contacts" in the AI Mapping
    // ribbon.
    const biotechPool = (allBiotechIndex && allBiotechIndex.length > 0) ? allBiotechIndex : companies;
    biotechPool.forEach(bc => {
      if (!bc?.company_name) return;
      const contacts = (bc.contacts || []).filter(c => c && (c.email || c.name));
      // Company-level email counts too (see FIX v14 above).
      const companyLevelEmail = (
        bc.has_contact_email === true ||
        !!(bc.contact_email && String(bc.contact_email).trim()) ||
        !!(bc.email && String(bc.email).trim()) ||
        !!(bc.partnering_email && String(bc.partnering_email).trim()) ||
        !!(bc.bd_email && String(bc.bd_email).trim())
      );
      if (contacts.length === 0 && !companyLevelEmail) return;
      const key = bc.company_name.trim().toLowerCase();
      if (!map[key]) {
        // Synthesize a lightweight contact entry so downstream code that
        // iterates `contacts` has something to render (name + email).
        map[key] = contacts.length > 0 ? contacts : [{
          name: bc.company_name + ' — Business Development',
          email: (bc.contact_email || bc.email || bc.partnering_email || bc.bd_email || '').trim(),
          company_name: bc.company_name,
          source: 'biotech_company_level_email',
        }];
      }
    });
    return map;
  }, [pharmaContacts, companyProfiles, companies, allBiotechIndex]);

  // Lookup set of company names the user has explicitly registered as
  // "known no-contact" — these came from the Bulk Add No-Contact Pharma flow
  // (`stub_source: "user_known_no_contacts"` or `no_contacts_known: true`).
  // We distinguish these from "truly missing" companies in the AI Mapping
  // ribbon and per-row badges so the user knows which gaps to chase vs
  // which to skip (the annual-review register).
  const knownNoContactSet = React.useMemo(() => {
    const s = new Set();
    companyProfiles.forEach(cp => {
      if (cp?.no_contacts_known === true || cp?.stub_source === 'user_known_no_contacts') {
        const k = (cp.company_name || '').trim().toLowerCase();
        if (k) s.add(k);
      }
    });
    return s;
  }, [companyProfiles]);

  const isKnownNoContact = React.useCallback((name) => {
    if (!name) return false;
    return knownNoContactSet.has(name.trim().toLowerCase());
  }, [knownNoContactSet]);

  // When the URL has a :companyId param, force-expand that one company so the
  // detail view always shows the mapping inline. Also auto-expand the first
  // asset for instant useful content.
  useEffect(() => {
    if (routeCompanyId) {
      setExpandedId(routeCompanyId);
      // Auto-expand the first asset once assets are loaded
      const firstAsset = assets.find(a => a.company_id === routeCompanyId);
      if (firstAsset && !expandedAssetId) {
        setExpandedAssetId(firstAsset.id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeCompanyId, assets]);

  // Normalize company name for matching — strips parenthetical suffixes, common variations
  const normalizeName = (name) => {
    if (!name) return '';
    return name.trim().toLowerCase()
      .replace(/\s*\(.*?\)\s*/g, ' ')          // Remove (parenthetical) like (Bausch + Lomb)
      .replace(/\s*(formerly|now|acquired by)\s+.*/i, '')  // Remove "formerly X" suffixes
      .replace(/,?\s*(inc\.?|ltd\.?|llc|co\.?|corp\.?|plc|ag|sa|gmbh|group|company|limited)$/i, '') // Legal suffixes
      .replace(/pharmaceuticals?/gi, 'pharma')  // Normalize pharmaceutical(s) -> pharma
      .replace(/&/g, 'and')                     // & -> and
      .replace(/\s+/g, ' ')                     // Collapse whitespace
      .trim();
  };

  // Lookup the BD routing policy for a given pharma target name.
  // Returns { mode, poc_email, poc_name, portal_url, portal_name, note } or null.
  const getRoutingPolicy = (targetCompanyName) => {
    if (!targetCompanyName) return null;
    const target = targetCompanyName.trim().toLowerCase();
    const normalizedTarget = normalizeName(targetCompanyName);
    for (const cp of companyProfiles) {
      const cpName = (cp.company_name || '').trim().toLowerCase();
      if (!cpName) continue;
      const cpNorm = normalizeName(cp.company_name);
      if (cpName === target || cpNorm === normalizedTarget) {
        const policy = cp.routing_policy;
        if (policy && policy.mode === 'single_poc' && policy.poc_email) {
          return policy;
        }
        if (policy && policy.mode === 'portal_only' && policy.portal_url) {
          return policy;
        }
        return null;
      }
    }
    return null;
  };

  // Biotech-industry keyword prefixes — if two company names share the same
  // distinctive base word AND have tokens that both start with one of these
  // keywords (e.g. "Bio..." or "Pharma..."), we treat them as the same family.
  // This collapses "TLC Biopharmaceuticals" / "TLC Biosciences" / "TLC Biotech"
  // into one matched company — the user shouldn't have to manually dedupe.
  const _BIOTECH_KEYWORD_PREFIXES = ['bio', 'pharma', 'sci', 'therap', 'health', 'med', 'labs', 'biotec'];
  const _hasBiotechKeywordOverlap = (a, b) => {
    const aTokens = (a || '').split(/[\s,]+/).filter(Boolean);
    const bTokens = (b || '').split(/[\s,]+/).filter(Boolean);
    for (const at of aTokens) {
      for (const bt of bTokens) {
        if (at === bt) continue; // already-equal tokens don't count as "additional" evidence
        for (const kw of _BIOTECH_KEYWORD_PREFIXES) {
          if (at.startsWith(kw) && bt.startsWith(kw)) return true;
        }
      }
    }
    return false;
  };

  // Lookup: biotech company name -> biotech doc (regardless of contacts).
  // Used to recognize when an AI-mapping target is ALREADY in the user's
  // biotech CRM but just doesn't have contacts attached yet. Distinguishes
  // "already in CRM, needs contacts" from "truly missing from CRM" so the
  // user gets a one-click path to add contacts instead of being told the
  // company is missing entirely. Uses `allBiotechIndex` (every source) so
  // conference-imported biotechs are also covered.
  const biotechByName = React.useMemo(() => {
    const map = {};
    const pool = (allBiotechIndex && allBiotechIndex.length > 0) ? allBiotechIndex : companies;
    pool.forEach(bc => {
      if (!bc?.company_name) return;
      const key = bc.company_name.trim().toLowerCase();
      if (!map[key]) map[key] = bc;
    });
    return map;
  }, [companies, allBiotechIndex]);

  const findBiotechMatch = React.useCallback((targetCompanyName) => {
    if (!targetCompanyName) return null;
    const target = targetCompanyName.trim().toLowerCase();
    if (biotechByName[target]) return biotechByName[target];
    // Fuzzy match using the same biotech-keyword-overlap rules as the
    // contact lookup so "Junshi Biosciences" matches "Junshi", "Junshi Bio",
    // or "Junshi Biopharmaceuticals" reliably.
    const normalizedTarget = normalizeName(targetCompanyName);
    const targetBase = normalizedTarget.split(/[\s,]+/)[0];
    for (const [companyKey, bc] of Object.entries(biotechByName)) {
      const normalizedKey = normalizeName(companyKey);
      const keyBase = normalizedKey.split(/[\s,]+/)[0];
      const baseMatch = targetBase.length >= 3 && targetBase === keyBase && normalizedTarget.length > 3 && normalizedKey.length > 3;
      if (
        normalizedKey === normalizedTarget ||
        normalizedKey.includes(normalizedTarget) || normalizedTarget.includes(normalizedKey) ||
        normalizedKey.startsWith(normalizedTarget + ' ') || normalizedTarget.startsWith(normalizedKey + ' ') ||
        (baseMatch && targetBase.length >= 4) ||
        (baseMatch && targetBase.length === 3 && _hasBiotechKeywordOverlap(normalizedTarget, normalizedKey))
      ) {
        return bc;
      }
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biotechByName]);

  // Matching a target against every contact bucket is O(buckets) with regex
  // work; the funnel strip asks for 100+ targets per render, so memoise per
  // (target, crm key) and reset whenever the contact index changes.
  const matchCacheRef = useRef(new Map());
  useEffect(() => { matchCacheRef.current = new Map(); }, [contactsByCompany]);
  const findMatchingContacts = (targetCompanyName, targetObj) => {
    const cacheKey = `${(targetCompanyName || '').trim().toLowerCase()}|${targetObj?.in_crm ? (targetObj.crm_company_name || '').trim().toLowerCase() : ''}`;
    const hit = matchCacheRef.current.get(cacheKey);
    if (hit) return hit;
    // Merck KGaA ≠ Merck & Co: never attach the other Merck's people.
    const res = filterMerckFamily(targetCompanyName, _findMatchingContactsRaw(targetCompanyName, targetObj));
    matchCacheRef.current.set(cacheKey, res);
    return res;
  };

  // Pre-normalised contact buckets so each target match is a single pass.
  const bucketIndex = React.useMemo(
    () => Object.keys(contactsByCompany).map(key => ({ key, norm: normalizeName(key), canon: canonicalPharmaKey(key), fam: merckFamily(key) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contactsByCompany],
  );

  const _findMatchingContactsRaw = (targetCompanyName, targetObj) => {
    if (!targetCompanyName) return [];
    // Sep 2026 (Roche case): UNION every contact bucket that is the same
    // company — exact key, CRM-matched key, alias group (Roche ↔ Genentech,
    // J&J ↔ Janssen…), brand-token match — instead of returning the first
    // hit. Previously a stub "Roche Pharma" bucket with one nameless
    // contact short-circuited and hid the 45 Roche / Genentech employees.
    const keys = new Set();
    const target = targetCompanyName.trim().toLowerCase();
    if (contactsByCompany[target]) keys.add(target);
    if (targetObj?.in_crm && targetObj?.crm_company_name) {
      const crmKey = targetObj.crm_company_name.trim().toLowerCase();
      if (contactsByCompany[crmKey]) keys.add(crmKey);
    }
    const targetCanon = canonicalPharmaKey(targetCompanyName);
    const normalizedTarget = normalizeName(targetCompanyName);
    const targetFam = merckFamily(targetCompanyName);
    for (const { key: companyKey, norm: normalizedKey, canon, fam } of bucketIndex) {
      if (keys.has(companyKey)) continue;
      // Alias-group equality is covered by `canon`; brand-token match below is
      // the cheap path (token sets are memoised). Merck families never merge.
      const famConflict = targetFam && fam && targetFam !== fam && targetFam !== 'UNRESOLVED' && fam !== 'UNRESOLVED';
      if (
        (targetCanon && canon === targetCanon) ||
        normalizedKey === normalizedTarget ||
        (!famConflict && sharesBrand(targetCompanyName, companyKey)) ||
        // 3-char brand ("TLC") + a shared biotech keyword token
        (normalizedTarget.split(/[\s,]+/)[0] === normalizedKey.split(/[\s,]+/)[0] &&
          normalizedTarget.split(/[\s,]+/)[0].length === 3 &&
          _hasBiotechKeywordOverlap(normalizedTarget, normalizedKey))
      ) keys.add(companyKey);
    }
    const seen = new Set();
    const out = [];
    for (const k of keys) {
      for (const c of contactsByCompany[k] || []) {
        const id = (c.email || '').trim().toLowerCase() || c.id || `${k}:${c.name || ''}`;
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(c);
      }
    }
    // Only people with an email address are outreach-able (user directive,
    // Sep 2026): rows without one never appear in the dialog or counts.
    return out.filter(c => (c.email || '').trim());
  };

  const fetchData = async () => {
    try {
      // Oct 2026 · allSettled: one slow/failed call must not blank the page.
      const _get = (url, extra = {}) => axios.get(`${API}${url}`, { withCredentials: true, timeout: 90000, ...extra });
      const results = await Promise.allSettled([
        _get('/biotech-companies?source=manual&limit=5000'),
        _get('/assets?source=manual&limit=5000'),
        _get('/ai/map-targets'),
        _get('/documents?entity_type=biotech'),
        _get('/pharma-contacts?limit=100000'),
        _get('/company-profiles?limit=5000'),
        _get('/ai/outreach-summary'),
        _get('/ai/asset-exclusions-counts'),
      ]);
      const labels = ['biotech companies', 'assets', 'AI mappings', 'documents', 'pharma contacts', 'company profiles', 'outreach summary', 'exclusion counts'];
      const val = (i, fallback) => (results[i].status === 'fulfilled' ? results[i].value : { data: fallback });
      const [companiesRes, assetsRes, mappingsRes, docsRes, pharmaRes, profilesRes, outreachSumRes, exclCountsRes] =
        [val(0, []), val(1, []), val(2, []), val(3, []), val(4, []), val(5, []), val(6, {}), val(7, { counts: {} })];
      const failed = results.map((r, i) => (r.status === 'rejected' && i < 6 ? labels[i] : null)).filter(Boolean);
      if (failed.length) {
        const first = results.find(r => r.status === 'rejected');
        const why = first?.reason?.response?.status ? `HTTP ${first.reason.response.status}` : (first?.reason?.code === 'ECONNABORTED' ? 'timed out' : (first?.reason?.message || 'network error'));
        toast.error(`Could not load: ${failed.join(', ')} (${why}). Showing what loaded — refresh to retry.`, { duration: 12000 });
      }
      setCompanies(companiesRes.data?.items || companiesRes.data || []);
      setAssets(assetsRes.data?.items || assetsRes.data || []);
      setMappings(Array.isArray(mappingsRes.data) ? mappingsRes.data : []);
      setDocuments(Array.isArray(docsRes.data) ? docsRes.data : []);
      setPharmaContacts(pharmaRes.data?.items || pharmaRes.data || []);
      setCompanyProfiles(profilesRes.data?.items || profilesRes.data || []);
      setOutreachSummary(outreachSumRes.data || {});
      setExclusionCounts(exclCountsRes.data?.counts || {});
      // Fire-and-forget: pull every biotech (any source) for the biotech-match
      // lookup. Uses slim=1 (~1.4MB vs ~10MB full docs) so it survives slow
      // production edges, and retries twice — a silent failure here previously
      // inflated the "pharmas without contacts" count to include companies
      // that DO have contacts in the biotech CRM.
      const loadBiotechIndex = async (attempt = 0) => {
        try {
          const r = await axios.get(`${API}/biotech-companies?limit=10000&slim=1`, { withCredentials: true, timeout: 60000 });
          setAllBiotechIndex(r.data?.items || r.data || []);
          setBiotechIndexState('ready');
        } catch {
          if (attempt < 2) {
            setTimeout(() => loadBiotechIndex(attempt + 1), 3000 * (attempt + 1));
          } else {
            setBiotechIndexState('failed');
            toast.warning('Could not load the biotech contact index — "pharmas without contacts" counts may be overstated. Refresh the page to retry.', { duration: 10000 });
          }
        }
      };
      loadBiotechIndex();
      mappingsRes.data.forEach(m => {
        if (m.status === 'processing') {
          const key = m.asset_id || m.biotech_company_id;
          setRunningIds(prev => new Set([...prev, key]));
          startPolling(m.id, key);
        }
      });
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const startPolling = useCallback((mappingId, runKey) => {
    if (pollRef.current[mappingId]) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/ai/map-targets/${mappingId}`, { withCredentials: true });
        if (res.data.status === 'completed') {
          clearInterval(interval);
          delete pollRef.current[mappingId];
          setMappings(prev => prev.map(m => m.id === mappingId ? res.data : m));
          setRunningIds(prev => { const n = new Set(prev); n.delete(runKey); return n; });
          setExpandedAssetId(runKey);
          const indiaExcluded = res.data.stats?.india_excluded_count || 0;
          const baseMsg = `Target mapping complete! Found ${res.data.target_companies?.length || 0} targets.`;
          const suffix = indiaExcluded > 0
            ? ` (${indiaExcluded} Indian pharma${indiaExcluded === 1 ? '' : 's'} excluded — asset hasn't completed Phase 2)`
            : '';
          toast.success(baseMsg + suffix, { duration: indiaExcluded > 0 ? 8000 : 4000 });
        } else if (res.data.status === 'failed') {
          clearInterval(interval);
          delete pollRef.current[mappingId];
          setMappings(prev => prev.map(m => m.id === mappingId ? res.data : m));
          setRunningIds(prev => { const n = new Set(prev); n.delete(runKey); return n; });
          const errMsg = res.data.error || 'Unknown error';
          if (errMsg.toLowerCase().includes('budget')) {
            toast.error('Universal Key budget exceeded. Go to Profile > Universal Key to add balance.', { duration: 10000 });
          } else {
            toast.error(`Target mapping failed: ${errMsg}`, { duration: 8000 });
          }
        }
      } catch { /* retry */ }
    }, 5000);
    pollRef.current[mappingId] = interval;
  }, []);

  useEffect(() => {
    fetchData();
    return () => { Object.values(pollRef.current).forEach(clearInterval); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRunTargets = async (companyId, assetId, opts = {}) => {
    const runKey = assetId || companyId;
    const sel = regionFilter[runKey];
    const selArr = Array.isArray(sel) ? sel : (sel ? [sel] : []);
    const region = selArr.join(', ');
    const extra_instruction = (extraInstructions[runKey] || '').trim();
    setRunningIds(prev => new Set([...prev, runKey]));
    try {
      const res = await axios.post(`${API}/ai/map-targets`, {
        biotech_company_id: companyId,
        asset_id: assetId || '',
        region,
        force_refresh_discovery: !!opts.forceRefresh,
        extra_instruction,
      }, { withCredentials: true, timeout: 30000 });
      setMappings(prev => [res.data, ...prev]);
      const label = selArr.length === 0 ? '' : ` (${summarizeRegions(selArr)})`;
      const refreshLabel = opts.forceRefresh ? ' — refreshing web discovery cache' : '';
      const hintLabel = extra_instruction ? ' (with indication hint)' : '';
      toast.success(`AI target search started${label}${refreshLabel}${hintLabel}. Results will appear automatically.`);
      startPolling(res.data.id, runKey);
    } catch (err) {
      setRunningIds(prev => { const n = new Set(prev); n.delete(runKey); return n; });
      toast.error(err?.response?.data?.detail || 'Failed to start target mapping.');
    }
  };

  // Sep 2026 · re-check logged signals (Emerging / Signalled Interests) on an
  // existing mapping without a full re-run — conflicts become "needs your call".
  const handleSignalAudit = async (mappingId, assetName, { quiet = false } = {}) => {
    if (!mappingId) return;
    if (!quiet) toast(`${assetName || 'Asset'}: checking logged signals…`);
    try {
      const { data } = await axios.post(`${API}/ai/map-targets/${mappingId}/signal-audit`, {}, { withCredentials: true, timeout: 120000 });
      if (quiet) {
        if (data.conflict) toast.warning(`${assetName || 'Asset'}: ${data.conflict} mapped compan${data.conflict === 1 ? 'y has' : 'ies have'} a logged signal that conflicts with this asset — ${(data.conflicts || []).slice(0, 4).join(', ')}${(data.conflicts || []).length > 4 ? '…' : ''}. Moved to "needs your call".`, { duration: 12000 });
      } else if (!data.checked) toast(`${assetName || 'Asset'}: none of the mapped companies has a logged signal yet.`);
      else toast.success(`${assetName || 'Asset'}: ${data.checked} companies with logged signals checked — ${data.conflict || 0} conflict, ${data.fits || 0} support, ${data.unclear || 0} unclear.`, { duration: 9000 });
      const fresh = await axios.get(`${API}/ai/map-targets/${mappingId}`, { withCredentials: true }).then(r => r.data).catch(() => null);
      if (fresh) setMappings(prev => prev.map(m => (m.id === fresh.id ? { ...m, ...fresh } : m)));
    } catch (err) {
      if (!quiet) toast.error(err?.response?.data?.detail || 'Signal check failed.');
    }
  };
  // Sep 2026 (LEO / NuGel): mappings created before the signal audit existed
  // — or whose companies got new signals since — are audited automatically
  // the first time the page sees them, so a conflicting company is greyed out
  // BEFORE anyone opens outreach. Never re-runs for an already audited mapping.
  const signalAuditAttemptedRef = useRef({});
  useEffect(() => {
    (mappings || []).filter(m => m.status === 'completed' && !m.signal_audited_at && (m.target_companies || []).length).forEach(m => {
      const isLatest = !(mappings || []).some(o => o.id !== m.id && o.status === 'completed' && (o.asset_id || o.biotech_company_id) === (m.asset_id || m.biotech_company_id) && new Date(o.created_at) > new Date(m.created_at));
      if (!isLatest || signalAuditAttemptedRef.current[m.id]) return;
      signalAuditAttemptedRef.current[m.id] = true;
      handleSignalAudit(m.id, m.asset_name, { quiet: true });
    });
  }, [mappings]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePurgeWeakRationale = async (mappingIds, assetName) => {
    // Accept either a single ID (legacy) or an array of IDs (all completed
    // campaigns for one asset). The UI shows a UNION across campaigns, so
    // we must purge every underlying campaign or the union list stays
    // unchanged.
    const idList = Array.isArray(mappingIds) ? mappingIds.filter(Boolean) : (mappingIds ? [mappingIds] : []);
    if (idList.length === 0) return;
    try {
      let totalDropped = 0;
      let totalKept = 0;
      let touched = 0;
      for (const mid of idList) {
        try {
          const res = await axios.post(`${API}/ai/map-targets/${mid}/purge-weak-rationale`, {}, { withCredentials: true, timeout: 30000 });
          totalDropped += res?.data?.dropped || 0;
          totalKept += res?.data?.kept || 0;
          if ((res?.data?.dropped || 0) > 0) touched += 1;
        } catch { /* per-campaign failure is non-fatal */ }
      }
      if (totalDropped > 0) {
        toast.success(`${assetName || 'Asset'}: purged ${totalDropped} weak-rationale target${totalDropped > 1 ? 's' : ''} across ${touched} campaign${touched > 1 ? 's' : ''}. ${totalKept} remain (deduplicated union).`);
        // Reload all touched mappings so the UI reflects the trimmed lists.
        try {
          const refreshed = await Promise.all(idList.map(mid =>
            axios.get(`${API}/ai/map-targets/${mid}`, { withCredentials: true }).then(r => r.data).catch(() => null)
          ));
          setMappings(prev => prev.map(m => {
            const fresh = refreshed.find(r => r && r.id === m.id);
            return fresh ? { ...m, ...fresh } : m;
          }));
        } catch { /* non-fatal */ }
      } else {
        toast(`${assetName || 'Asset'}: nothing to purge — every target across ${idList.length} campaign${idList.length > 1 ? 's' : ''} passed the rationale quality bar.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to purge weak-rationale targets.');
    }
  };

  // Seed the per-asset deal context from the fetched records so the ✓ state
  // and prefilled textarea survive page reloads.
  useEffect(() => {
    const next = {};
    assets.forEach(a => { if (a.email_guidance) next[a.id] = a.email_guidance; });
    companies.forEach(c => { if (c.email_guidance) next[c.id] = c.email_guidance; });
    if (Object.keys(next).length) setDealContext(prev => ({ ...next, ...prev }));
  }, [assets, companies]);

  const saveDealContext = async (companyId, assetId) => {
    const key = assetId || companyId;
    setDealContextSaving(prev => ({ ...prev, [key]: true }));
    try {
      await axios.put(`${API}/ai/email-guidance`, {
        biotech_company_id: companyId,
        asset_id: assetId || '',
        text: dealContext[key] || '',
      }, { withCredentials: true });
      toast.success((dealContext[key] || '').trim()
        ? 'Deal context saved — every AI-generated email for this asset will now reflect it'
        : 'Deal context cleared');
    } catch {
      toast.error('Failed to save deal context');
    } finally {
      setDealContextSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const dismissInvestorOutreach = async (assetId) => {
    // Optimistic: hide locally first
    setAssets(prev => prev.map(a => a.id === assetId ? { ...a, hide_investor_outreach: true } : a));
    try {
      await axios.put(`${API}/assets/${assetId}/toggle-investor-outreach-hidden`, {}, { withCredentials: true });
      toast.success('Investor Outreach hidden for this asset');
    } catch {
      // Revert on failure
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, hide_investor_outreach: false } : a));
      toast.error('Failed to hide Investor Outreach');
    }
  };

  const handleDelete = async (mappingId) => {
    try {
      if (pollRef.current[mappingId]) {
        clearInterval(pollRef.current[mappingId]);
        delete pollRef.current[mappingId];
      }
      await axios.delete(`${API}/ai/map-targets/${mappingId}`, { withCredentials: true });
      setMappings(prev => prev.filter(m => m.id !== mappingId));
      toast.success('Mapping deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete');
      setDeleteTarget(null);
    }
  };

  const handleGenerateEmail = async (companyId, assetId, emailType) => {
    const key = `${assetId || companyId}_${emailType}`;
    // Always regenerate fresh — clear cached version
    setEmails(prev => { const n = {...prev}; delete n[key]; return n; });
    setGeneratingEmail(prev => ({ ...prev, [key]: true }));
    try {
      const res = await axios.post(`${API}/ai/generate-email`, {
        biotech_company_id: companyId,
        asset_id: assetId || '',
        email_type: emailType,
      }, { withCredentials: true, timeout: 60000 });
      setEmails(prev => ({ ...prev, [key]: res.data.email }));
      toast.success(`${emailType === 'pharma' ? 'Pharma' : 'Investor'} outreach email generated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate email');
    } finally {
      setGeneratingEmail(prev => ({ ...prev, [key]: false }));
    }
  };

  const copyEmail = (key) => {
    const text = emails[key];
    if (!text) return;
    const plainText = text.replace(/\*\*/g, '');
    const htmlBody = text.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br>');
    const html = `<div style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 11pt; color: #000000; line-height: 1.6;">${htmlBody}</div>`;
    try {
      navigator.clipboard.write([new ClipboardItem({ 'text/html': new Blob([html], { type: 'text/html' }), 'text/plain': new Blob([plainText], { type: 'text/plain' }) })]);
    } catch { navigator.clipboard.writeText(plainText); }
    toast.success('Email copied to clipboard');
  };

  const openWarmLeads = (label, mappingTargets) => {
    const allMatches = [];
    (mappingTargets || []).forEach(tc => {
      const matched = findMatchingContacts(tc.company_name, tc);
      matched.forEach(pc => {
        if (!allMatches.find(m => m.id === pc.id)) {
          allMatches.push({ ...pc, matched_company: tc.company_name });
        }
      });
    });
    setWarmLeadsDialog({ open: true, label, contacts: allMatches });
  };

  const copyWarmLeads = () => {
    const contacts = warmLeadsDialog.contacts;
    if (!contacts.length) return;
    const html = `<table style="border-collapse:collapse;font-family:Arial,sans-serif;width:100%">
      <thead><tr style="background:#f0fdf4">
        <th style="padding:8px 12px;border:1px solid #d1d5db;text-align:left;font-size:11px;color:#166534;text-transform:uppercase">Name</th>
        <th style="padding:8px 12px;border:1px solid #d1d5db;text-align:left;font-size:11px;color:#166534;text-transform:uppercase">Designation</th>
        <th style="padding:8px 12px;border:1px solid #d1d5db;text-align:left;font-size:11px;color:#166534;text-transform:uppercase">Company</th>
        <th style="padding:8px 12px;border:1px solid #d1d5db;text-align:left;font-size:11px;color:#166534;text-transform:uppercase">Email</th>
      </tr></thead>
      <tbody>${contacts.map(c => `<tr>
        <td style="padding:6px 12px;border:1px solid #e5e7eb;font-size:13px;font-weight:600">${c.name || ''}</td>
        <td style="padding:6px 12px;border:1px solid #e5e7eb;font-size:13px">${c.designation || ''}</td>
        <td style="padding:6px 12px;border:1px solid #e5e7eb;font-size:13px">${c.company_name || c.matched_company || ''}</td>
        <td style="padding:6px 12px;border:1px solid #e5e7eb;font-size:13px;color:#0369a1">${c.email || ''}</td>
      </tr>`).join('')}</tbody></table>`;
    const plain = ['Name\tDesignation\tCompany\tEmail', ...contacts.map(c =>
      `${c.name || ''}\t${c.designation || ''}\t${c.company_name || c.matched_company || ''}\t${c.email || ''}`
    )].join('\n');
    const blob = new Blob([html], { type: 'text/html' });
    const textBlob = new Blob([plain], { type: 'text/plain' });
    navigator.clipboard.write([new ClipboardItem({ 'text/html': blob, 'text/plain': textBlob })]).then(() => {
      toast.success(`Copied ${contacts.length} warm lead contacts as table`);
    }).catch(() => toast.error('Failed to copy'));
  };

  // Group assets by company
  const assetsByCompany = {};

  // Open outreach dialog for a company. The preparation below is long and
  // async; a single thrown error used to leave the button a silent no-op
  // (user report Sep 2026: "email window doesn't open"). Wrap it so the user
  // always gets feedback, and show a preparing state on the button.
  const [coverageGaps, setCoverageGaps] = useState([]);
  const openOutreach = async (companyId, companyName, assetId, assetName, targets, mappingId = '') => {
    axios.get(`${API}/coverage-gaps?biotech_company_id=${companyId}`, { withCredentials: true })
      .then(r => setCoverageGaps(r.data?.gaps || [])).catch(() => setCoverageGaps([]));
    if (outreachPreparing) return;
    setOutreachPreparing(assetId || companyId);
    try {
      await _openOutreachInner(companyId, companyName, assetId, assetName, targets, mappingId);
    } catch (e) {
      console.error('openOutreach failed', e);
      toast.error(`Could not open the outreach window: ${e?.response?.data?.detail || e?.message || 'unexpected error'}`, { duration: 8000 });
    } finally {
      setOutreachPreparing(null);
    }
  };
  const _openOutreachInner = async (companyId, companyName, assetId, assetName, targets, mappingId = '') => {
    // Skipped Panel accumulator — every mapped pharma that gets silently
    // filtered out lands here so the modal can surface WHY.
    const skipped = [];
    // Fetch outreach status — which companies/contacts have already been emailed,
    // PLUS the cross-asset relationship exclusions (replied, meeting, DNC, partner, my company)
    let sentEmailsAll = new Set(); // every email with an outreach row for this biotech × asset (company-agnostic)
    let exclusions = {};
    let contactedCompanyNamesLc = new Set();
    let statusData = null;
    for (let attempt = 0; attempt < 3 && !statusData; attempt++) {
      try {
        const r = await axios.get(`${API}/ai/outreach-status/${companyId}?asset_id=${assetId || ''}`, { withCredentials: true, timeout: 60000 });
        statusData = r.data || {};
      } catch (e) {
        if (attempt < 2) await new Promise(res => setTimeout(res, 2000 * (attempt + 1)));
        else {
          setContactedCompanies(new Set());
          setExclusionsByCompany({});
          setDncContactEmails({});
          setSentHistoryFailed(true);
          toast.error(`Could not load sent history — sending is locked until it loads. ${e?.response?.data?.detail || e?.message || ''}`, { duration: 12000 });
        }
      }
    }
    if (statusData) {
      const data = statusData;
      setSentHistoryFailed(false);
      contactedCompanyNamesLc = new Set((data.contacted_company_names || []).map(n => (n || '').trim().toLowerCase()));
      setContactedCompanies(contactedCompanyNamesLc);
      (data.contacted || []).forEach(c => {
        (c.sent_emails || []).forEach(e => { const em = (e || '').trim().toLowerCase(); if (em) sentEmailsAll.add(em); });
      });
      exclusions = data.exclusions_by_company || {};
      setExclusionsByCompany(exclusions);
      setDncContactEmails(data.dnc_contact_emails || {});
    }

    // Alias-aware exclusion lookup: a target named "Janssen" should still be flagged
    // even if the exclusion key is "Johnson & Johnson".
    const _lookupExclusion = (targetName) => {
      const key = (targetName || '').trim().toLowerCase();
      if (!key) return null;
      if (exclusions[key]) return exclusions[key];
      const canon = canonicalPharmaKey(targetName);
      if (canon) {
        for (const [k, v] of Object.entries(exclusions)) {
          if (canonicalPharmaKey(k) === canon) return v;
        }
      }
      // Word-boundary-safe fallback for variant suffixes ("Menarini" vs
      // "Menarini Group"). Raw substring matching caused false positives like
      // "Sun Pharma" ⊂ "Fosun Pharma", so we only match whole-token runs.
      const keyTokens = key.split(/\s+/);
      for (const [k, v] of Object.entries(exclusions)) {
        if (k.length <= 5 || key.length <= 5) continue;
        const kTokens = k.split(/\s+/);
        const [short, long] = keyTokens.length <= kTokens.length ? [keyTokens, kTokens] : [kTokens, keyTokens];
        for (let i = 0; i + short.length <= long.length; i++) {
          if (short.every((t, j) => t === long[i + j])) return v;
        }
      }
      return null;
    };

    // Group targets with their warm lead contacts
    const grouped = {};
    const autoExcludedSet = new Set();
    // Resolve the asset's TA once — used to match against per-pharma BD routing rules.
    const _activeAsset = (assets || []).find(a => a.id === assetId) || {};
    const _assetTA = (_activeAsset.therapeutic_area || '').trim().toLowerCase();
    // Resolve the asset's TA + indication (un-lowercased) for the
    // stated-interests filter helper, which normalizes itself.
    // Sep 2026 fix (HB0017/AbbVie): indication MUST be included — a
    // "Dermatology" psoriasis mAb belongs to Immunology specialists.
    // Comma-joined so the matcher splits TA and indication into separate
    // tokens (" · " is not a split char and dropped the TA itself).
    const _assetTAraw = [_activeAsset.therapeutic_area, _activeAsset.indication]
      .filter(Boolean).join(', ');
    const _assetStage = _activeAsset.stage || _activeAsset.phase || '';
    // Server fallback (Lunatus case): any target with no locally-indexed
    // contact is resolved with the SAME employee lookup the profile page
    // uses, so outreach never says "No BD contact" while the profile lists
    // people (stale index, variant company names, blank company field…).
    // Server lookup for EVERY target (Lunatus case, Sep 2026): the local index
    // only keys contacts by company name / profile link, so people matched via
    // email domain (bdm@lunatus.com with a blank company) were invisible here
    // while the profile page listed them. Server rows are unioned with local.
    const _serverContacts = {};
    const _serverDiag = {};
    const _unresolved = [...new Set((targets || []).map(tc => tc.company_name).filter(Boolean))];
    if (_unresolved.length) {
      try {
        const _profileIds = {};
        (targets || []).forEach(t => { if (t.company_name && (t.crm_company_id || t.company_profile_id)) _profileIds[t.company_name] = t.crm_company_id || t.company_profile_id; });
        const { data } = await axios.post(`${API}/outreach-contacts/resolve`, { names: _unresolved, profile_ids: _profileIds }, { withCredentials: true, timeout: 45000 });
        Object.entries(data?.by_name || {}).forEach(([n, rows]) => {
          if (Array.isArray(rows) && rows.length) _serverContacts[n.trim().toLowerCase()] = filterMerckFamily(n, rows);
        });
        Object.entries(data?.diag || {}).forEach(([n, d]) => { _serverDiag[n.trim().toLowerCase()] = d; });
      } catch (e) {
        _unresolved.forEach(n => { _serverDiag[n.trim().toLowerCase()] = { error: e?.response?.status ? `server ${e.response.status}` : 'server unreachable' }; });
      }
    }
    const _getTargetContacts = (tc) => {
      const local = findMatchingContacts(tc.company_name, tc);
      const server = (_serverContacts[(tc.company_name || '').trim().toLowerCase()] || []).filter(c => (c.email || '').trim());
      if (!server.length) return local;
      const seen = new Set();
      const out = [];
      [...local, ...server].forEach(c => {
        const k = (c.email || '').trim().toLowerCase() || c.id || `${(c.name || '').trim().toLowerCase()}`;
        if (!k || seen.has(k)) return;
        seen.add(k);
        out.push(c);
      });
      return out;
    };
    (targets || []).forEach(tc => {
      // Per-contact HARD stage gate (mirrors backend preferred_stage_mismatch).
      let contacts = _getTargetContacts(tc).map(c =>
        stageGateBlocks(c.preferred_stages, _assetStage)
          ? { ...c, __stage_gate: c.preferred_stages, __asset_stage: _assetStage }
          : c,
      );

      // ── Specialist routing (company-level) ────────────────────────────
      // Sep 2026 (user directive, Pfizer/Tingting): individual stated
      // preferences route the INDIVIDUAL, not the company.
      //   • tagged for the asset's TA            → receives (specialist)
      //   • no stated preferences yet            → receives (until they tell us)
      //   • stated preferences exclude the TA    → hidden
      //   • own excluded_therapeutic_areas cover → hidden
      // Tagged colleagues are no longer a lock that hides the rest of the org
      // (Allison at Pfizer forwarded to Tingting — that is how untagged
      // recipients behave). Explicit BD Routing Rules (the company told us
      // who covers a TA) remain a hard whitelist below.
      const hiddenByInterest = [];
      const _tcProfile = (companyProfiles || []).find(p => (p.company_name || '').trim().toLowerCase() === (tc.company_name || '').trim().toLowerCase() || (tc.crm_company_id && p.id === tc.crm_company_id));
      const _isCommercialPartner = isCommercialPartnerTarget(tc, _tcProfile);
      const specialists = _isCommercialPartner ? [] : findSpecialists(contacts, _assetTAraw);
      const _specEmails = new Set(specialists.map(s => (s.email || '').toLowerCase()));
      // Sep 2026 (Takeda/HB0056): word-level + all-indications rule, same as the
      // backend gate — a bare substring test hid Mike Jones because "RA" ⊂ "respiRAtory".
      const _exclCovers = (c) => (Array.isArray(c.excluded_therapeutic_areas) ? c.excluded_therapeutic_areas : [])
        .some(x => exclusionCoversAsset(x, _activeAsset.therapeutic_area, _activeAsset.indication));
      if (_isCommercialPartner) {
        contacts = contacts.map(c => ({ ...c, __commercial_partner: true }));
      } else {
        contacts = contacts.filter(c => {
          const stated = Array.isArray(c.interest_areas) ? c.interest_areas : [];
          // Sep 2026 · bounced / left-company addresses are never emailed again;
          // they surface in the hidden panel so the gap is visible, not silent.
          if (c.email_status === 'bounced' || c.email_status === 'left_company') {
            hiddenByInterest.push({ ...c, __interest_mismatch: true, __stated_interests: stated, __unreachable: c.email_status });
            return false;
          }
          if (_exclCovers(c)) {
            hiddenByInterest.push({ ...c, __interest_mismatch: true, __stated_interests: stated, __excluded_ta: true });
            return false;
          }
          if (_specEmails.has((c.email || '').toLowerCase())) return true;
          // Sep 2026 (user decision, Astellas/Thomas Bryce case) · SPECIALIST LOCK
          // restored as a UI default: when ≥1 colleague has a stated interest
          // covering this asset's TA, untagged colleagues are skipped (greyed,
          // "Send anyway" available) instead of receiving by default. Generalists
          // only receive when nobody at the pharma has claimed the TA.
          if (!stated.length) {
            if (specialists.length > 0) {
              hiddenByInterest.push({ ...c, __interest_mismatch: true, __stated_interests: [], __specialist_routing: true, __specialists: specialists });
              return false;
            }
            return true;
          }
          const { allowed } = isOutreachAllowed(stated, _assetTAraw);
          if (!allowed) {
            hiddenByInterest.push({ ...c, __interest_mismatch: true, __stated_interests: stated });
            return false;
          }
          return true;
        }).map(c => (_specEmails.has((c.email || '').toLowerCase()) ? { ...c, __specialist: true } : c));
      }

      // ── BD Routing Rules: per-TA HARD WHITELIST ───────────────────────
      // When an active rule exists for this asset's TA at this pharma, only
      // the route_to contacts are reachable for THIS TA. Everyone else at
      // this pharma is implicitly suppressed for this asset (whether or not
      // they're in the rule's explicit `bypass` list). Rationale (per Amit
      // 2026-06-08): "if we have just one person with a stated preference
      // from a particular company in a particular therapeutic area, then we
      // need not send email to all". When NO rule exists for this TA,
      // behavior stays open (all contacts reachable).
      let activeRule = null;
      let suppressedByRule = [];
      if (_assetTA) {
        const _profile = (companyProfiles || []).find(p =>
          (p.company_name || '').trim().toLowerCase() === (tc.company_name || '').trim().toLowerCase()
        );
        const _rules = (_profile?.bd_routing_rules || []).filter(r => r.active !== false);
        // Match on TA + indication (same expansion as the backend send gate), so a
        // "Dermatology" psoriasis asset triggers Sanofi's Immunology recipient rule.
        const _specific = _rules.filter(r => !(r.therapeutic_areas || []).includes('*') && !r.catch_all);
        activeRule = _specific.find(r =>
          (r.therapeutic_areas || []).some(t => (t || '').trim().toLowerCase() === _assetTA) ||
          areasOverlap(r.therapeutic_areas, _assetTAraw)
        ) || _rules.find(r => (r.therapeutic_areas || []).includes('*') || r.catch_all) || null;
      }
      if (activeRule) {
        const r2list = Array.isArray(activeRule.route_to)
          ? activeRule.route_to
          : (activeRule.route_to && (activeRule.route_to.name || activeRule.route_to.email) ? [activeRule.route_to] : []);
        const routeEmails = new Set(r2list.map(r => (r.email || '').toLowerCase()).filter(Boolean));
        const routeIds = new Set(r2list.map(r => r.contact_id).filter(Boolean));

        // Additive rule: contacts whose stated preference matches the asset stay
        // reachable alongside the routed address; only non-specialists are hidden.
        const _additive = (activeRule.mode || 'exclusive') === 'additive';
        const _keepLc = new Set(_additive
          ? findSpecialists(contacts, _assetTAraw).map(c => (c.email || '').toLowerCase())
          : []);
        // Snapshot everyone we're about to suppress so the banner can show it.
        suppressedByRule = contacts
          .filter(c =>
            !routeEmails.has((c.email || '').toLowerCase()) &&
            !routeIds.has(c.id) &&
            !_keepLc.has((c.email || '').toLowerCase()),
          )
          .map(c => ({ name: c.name, email: c.email, designation: c.designation || '' }));

        // Build the new contact list strictly from route_to. Reuse existing
        // contact rows where present (preserves stored designation / id);
        // otherwise inject a synthetic stub so the send path can resolve it.
        const finalContacts = [];
        for (const r2 of r2list) {
          const routeEmailLc = (r2.email || '').toLowerCase();
          const existing = contacts.find(c =>
            (c.id && r2.contact_id && c.id === r2.contact_id) ||
            (routeEmailLc && (c.email || '').toLowerCase() === routeEmailLc)
          );
          if (existing) {
            finalContacts.push({ ...existing, __routed: true });
          } else if (r2.name || r2.email) {
            finalContacts.push({
              id: r2.contact_id || `routed_${Math.random().toString(36).slice(2)}`,
              name: r2.name,
              email: r2.email,
              designation: r2.designation || '',
              company_name: tc.company_name,
              __routed: true,
              __synthetic: !r2.contact_id,
            });
          }
        }
        if (_additive) {
          const _have = new Set(finalContacts.map(c => (c.email || '').toLowerCase()));
          contacts.forEach(c => {
            const lc = (c.email || '').toLowerCase();
            if (_keepLc.has(lc) && !_have.has(lc)) { finalContacts.push(c); _have.add(lc); }
          });
        }
        contacts = finalContacts;
      }

      // A company whose contacts were ALL hidden by the stated-interest filter
      // (Lunatus case) or a routing rule still has people — render the card
      // with the "N contacts hidden — Send anyway" expander, never "No BD contact".
      if (contacts.length > 0 || hiddenByInterest.length > 0 || suppressedByRule.length > 0) {
        // Pre-populate sent array with emails that were already sent in previous sessions.
        // Email-based (case-insensitive) across the whole asset — an outreach row
        // logged under "Roche" must still mark the contact sent on a "Genentech" card.
        const sentEmails = contacts.filter(c => sentEmailsAll.has((c.email || '').trim().toLowerCase())).map(c => c.email);
        const exMatch = _lookupExclusion(tc.company_name);
        const exReasons = exMatch ? [...(exMatch.reasons || [])] : [];
        // Also treat user-rejected targets (via Exclude List / individual reject)
        // as an exclusion so they are pre-deselected from outreach.
        if (tc.approval_status === 'rejected' && !exReasons.includes('manually_excluded')) {
          exReasons.push('manually_excluded');
        }        // Feb 2026 (v6) — `already_contacted_this_asset` is a WEAK signal:
        // it means at least one contact at this pharma has received a
        // prior email, but the pharma may still have many uncontacted BD
        // contacts. Don't let it force a company into the "auto-excluded"
        // banner; the per-contact skip in sendAllForCompany already
        // prevents any double-send. HARD exclusions (replied historically,
        // dnc_flagged, my_company, client_already_in_touch, commercialization_
        // partner, manually_excluded) still auto-exclude — those are
        // relationship-level "don't email at all" signals.
        const HARD_EXCLUSION_REASONS = new Set([
          'replied_historically',
          'meeting_logged',
          'dnc_flagged',
          'commercialization_partner',
          'my_company',
          'client_already_in_touch',
          'not_buyer_eligible',
          'manually_excluded',
        ]);
        const hardExReasons = exReasons.filter(r => HARD_EXCLUSION_REASONS.has(r));
        if (hardExReasons.length > 0) autoExcludedSet.add(tc.company_name);
        // Sep 2026 · soft quality-gate verdicts ("needs your call") stay in the
        // list, deselected, with a Reinstate action — never silently dropped.
        const _soft = isTargetRefused(tc) ? '' : softRefusalReason(tc);
        if (_soft) autoExcludedSet.add(tc.company_name);
        // A tick must mean "an email will be sent": hard-refused targets
        // (exclude-from-outreach, hard block, tech/service partner) and
        // portal-only companies start unticked (user, Sep 9 2026).
        const _hardRefusal = isTargetRefused(tc);
        const _portalOnly = (() => { try { const pol = getRoutingPolicy(tc.company_name); return !!(pol && pol.mode === 'portal_only'); } catch { return false; } })();
        if (_hardRefusal || _portalOnly) autoExcludedSet.add(tc.company_name);
        grouped[tc.company_name] = {
          hardRefusal: _hardRefusal,
          softReason: _soft,
          signalAudit: tc.signal_audit || null,
          profileId: tc.crm_company_id || tc.company_profile_id || _tcProfile?.id || '',
          reinstated: !!(tc.manual_override && tc.manual_override.overridden === true),
          reinstatedTier: tc.manual_override?.tier_label || '',
          targetRole: tc.target_role || '',
          fitCategory: tc.fit_category || '',
          contacts,
          hiddenByInterest,
          rationale: tc.rationale || tc.match_reason || '',
          country: tc.country || '',
          email: null,
          generating: false,
          sent: sentEmails,
          // Sep 2026 · snapshot of what was ALREADY sent when the dialog opened —
          // only prior-session sends collapse a card; sends made in this session
          // (incl. the server queue) keep the card in place with green Sent badges.
          priorSent: [...sentEmails],
          exclusionReasons: exReasons,
          exclusionDetails: {
            ...(exMatch ? exMatch.details || {} : {}),
            ...(tc.approval_status === 'rejected' ? { manually_excluded: tc.approval_comment || 'Excluded via Exclude List' } : {}),
          },
          bdRoutingRule: activeRule || null,
          bdRoutingSuppressed: suppressedByRule,
        };
        // Also surface hard-excluded / DNC / policy-blocked companies in
        // the Skipped Panel — they DO have contacts but the user can't
        // send to them without lifting the exclusion.
        if (hardExReasons.length > 0) {
          const _reason = hardExReasons[0];
          const _human = {
            'replied_historically': 'Contact already replied — send blocked to avoid duplicate outreach',
            'meeting_logged': 'Meeting logged with this pharma — send blocked',
            'dnc_flagged': 'Contact is flagged Do-Not-Contact',
            'commercialization_partner': 'Already a commercialization partner',
            'my_company': "This is your own biotech's record",
            'client_already_in_touch': 'Client is already in touch with this pharma',
            'not_buyer_eligible': 'Handshake OFF — not buyer-eligible, no email will be generated',
            'manually_excluded': 'Manually excluded from your Exclude List',
          }[_reason] || `Blocked (${_reason})`;
          skipped.push({
            company_name: tc.company_name,
            country: tc.country || '',
            reason: _reason,
            reason_human: _human,
            action_url: tc.id ? `/company-profile/${tc.id}` : `/pharma?search=${encodeURIComponent(tc.company_name)}`,
            action_label: 'Review',
          });
        }
      } else {
        // No BD contact in CRM — silent drop that the user needs to see.
        // Deep-link straight to the pharma-contacts add flow so they can
        // add the missing contact and re-run outreach.
        // Weak / parked targets (failed the quality gate) are not worth hunting a
        // contact for — the funnel strip already lists them under "parked (weak)".
        if (isTargetRefused(tc)) return;
        const _d = _serverDiag[(tc.company_name || '').trim().toLowerCase()];
        const _diagText = !_d ? '' : _d.error ? `Lookup failed (${_d.error})`
          : !_d.profile_found ? 'No company profile with this exact name in the CRM'
          : _d.raw_matches === 0 ? `Profile "${_d.profile_name}" found (${_d.linked_to_profile} contacts linked to it) but no contact record matched by profile link, name or email domain`
          : _d.kept === 0 ? `${_d.raw_matches} candidate contacts found but all belong to other companies (e.g. ${(_d.dropped_samples || []).map(x => `${x.name} @ ${x.company_name}`).join('; ')})`
          : `${_d.kept} contacts found, ${_d.with_email} with an email`;
        const _isBio = tc.source === 'biotech_inlicenser';
        skipped.push({
          company_name: tc.company_name,
          country: tc.country || '',
          reason: 'no_bd_contact',
          reason_human: 'No BD contact in CRM — add one to include this pharma in outreach',
          diag: _diagText,
          kind: _isBio ? 'Biotech in-licenser (your database)' : 'Pharma',
          tier: tc.target_role || tc.outreach_bucket || tc.priority_tier || '',
          confidence: tc.confidence || '',
          fit_score: tc.fit_score ?? '',
          rationale: tc.rationale || '',
          website: tc.website || tc.source_url || '',
          action_url: _isBio && tc.crm_company_id ? `/biotech/${tc.crm_company_id}` : `/pharma?add=${encodeURIComponent(tc.company_name)}`,
          action_label: _isBio ? 'Add email' : 'Add contact',
        });
      }
    });
    // Attach any server-side noise-buyer drops from the mapping's
    // exclusions envelope so the Skipped Panel is a single source of
    // truth (CRO/diagnostics/consumer-goods removed by the pharma noise
    // filter surface here too, marked with reason='noise_buyer').
    try {
      const _latestMap = (mappingsByKey[assetId] || mappingsByKey[companyId] || [])[0];
      const _noise = (_latestMap?.exclusions?.noise_buyers) || [];
      _noise.forEach(n => {
        skipped.push({
          company_name: n.company_name,
          country: n.country || '',
          reason: 'noise_buyer',
          reason_human: n.reason_human || 'Removed as non-buyer noise (CRO / diagnostics / consumer / imaging vendor)',
          action_url: '',
          action_label: '',
        });
      });
      // Aug 2026 · Also surface company-level stage-gate drops so BD
      // leads see exactly which pharmas were excluded due to the
      // Phase 3+ / Phase 2+ org-wide rule. Deep-links to the Company
      // Profile so the gate can be adjusted if the exclusion is wrong.
      const _stageGate = (_latestMap?.exclusions?.stage_gate) || [];
      _stageGate.forEach(n => {
        skipped.push({
          company_name: n.company_name,
          country: n.country || '',
          reason: 'company_stage_gate',
          reason_human: n.reason_human || `Removed by ${n.company_name} company stage gate (only accepts ${n.min_stage}+ assets)`,
          action_url: `/pharma?search=${encodeURIComponent(n.company_name)}`,
          action_label: 'Review gate',
        });
      });
      // Sep 2026 · decline memory — companies dropped at mapping time by the
      // rationale-quality gate (previously log-only) now surface with WHY.
      ((_latestMap?.exclusions?.rationale_gate) || []).forEach(n => {
        skipped.push({
          company_name: n.company_name,
          country: n.country || '',
          reason: 'rationale_gate_dropped',
          reason_human: n.reason_human || n.reason || 'Rationale not strong enough',
          diag: n.reason || '',
          action_url: '',
          action_label: '',
        });
      });
      // Sep 2026 · biotech in-licensers pruned by the tightened handshake rule.
      ((_latestMap?.exclusions?.handshake_pruned) || []).forEach(n => {
        skipped.push({
          company_name: n.company_name,
          country: n.country || '',
          reason: 'handshake_pruned',
          reason_human: n.reason_human || n.reason || 'No longer qualifies as a biotech in-licenser',
          diag: n.reason || '',
          action_url: '',
          action_label: '',
        });
      });
    } catch { /* silent — skipped panel is best-effort */ }
    setSkippedTargets(skipped);
    // Restore saved drafts (generated or hand-edited) so a page reload never
    // forces regeneration. Oct 2026 · retried 3×; if it still fails we flag it
    // so the auto-generate sweep does NOT silently regenerate everything.
    draftsRestoreFailedRef.current = false;
    let dd = null;
    for (let attempt = 0; attempt < 3 && !dd; attempt++) {
      try {
        const r = await axios.get(`${API}/ai/email-drafts?biotech_company_id=${companyId}&asset_id=${assetId || ''}`, { withCredentials: true, timeout: 60000 });
        dd = r.data || { drafts: {} };
      } catch {
        if (attempt < 2) await new Promise(res => setTimeout(res, 2000 * (attempt + 1)));
      }
    }
    if (!dd) {
      draftsRestoreFailedRef.current = true;
      toast.error('Saved emails could not be loaded (database slow). Auto-generation is paused so nothing is regenerated — reopen this dialog or click "Generate Emails" when ready.', { duration: 15000 });
    } else {
      const byLc = {};
      // Sep 2026 · a saved draft with no body (subject only) is NOT a draft —
      // drop it so the card shows Generate again instead of a blank email.
      const _hasBody = (email) => { const parts = String(email || '').trim().split(/\n\s*\n/); const body = parts.length > 1 ? parts.slice(1).join(' ') : (/^subject:/i.test(String(email || '').trim()) ? '' : String(email || '')); return body.trim().split(/\s+/).filter(Boolean).length >= 25; };
      const _stripHtml = (t) => (/<\/?(p|br|div)\b/i.test(t || '') ? String(t).replace(/<\s*br\s*\/?>/gi, '\n').replace(/<\/\s*p\s*>/gi, '\n\n').replace(/<[^>]{1,120}>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\n{3,}/g, '\n\n').trim() : t);
      Object.entries(dd?.drafts || {}).forEach(([name, email]) => { if (email && _hasBody(email)) byLc[name.toLowerCase()] = _stripHtml(email); });
      Object.keys(grouped).forEach(k => {
        const saved = byLc[k.toLowerCase()];
        if (saved && !grouped[k].email) grouped[k] = { ...grouped[k], email: saved, restoredDraft: true };
      });
    }
    setOutreachEmails(grouped);
    // Default selection: only companies that are (1) NOT auto-excluded by an
    // established-relationship reason (replied / meeting / DNC / partner /
    // my company) AND (2) have not received any past outreach for this
    // biotech across any asset. The latter prevents accidental re-spam when
    // the user runs a fresh AI mapping that overlaps with the previous run.
    const previouslyContactedLc = contactedCompanyNamesLc;
    const safeToSelect = Object.keys(grouped).filter(k => {
      if (autoExcludedSet.has(k)) return false;
      // Sep 2026 · companies already emailed for THIS asset start unticked and
      // greyed out ("Emailed already"). The user can still expand + tick them
      // to resend to remaining contacts; the per-contact skip in
      // sendAllForCompany prevents double-sends.
      if ((grouped[k].exclusionReasons || []).includes('already_contacted_this_asset')) return false;
      if ((grouped[k].sent || []).length > 0) return false;
      return true;
    });
    setSelectedCompanies(new Set(safeToSelect));
    setExpandedEmailed(new Set());
    setShowExcludedRelationships(false);
    setShowNewCompaniesOnly(false); // reset filter — show full list by default
    setOutreachDialog({ open: true, companyId, companyName, assetId, assetName, mappingId: mappingId || '', targets });
    // Aug 2026 · Pre-load any persisted asset.outreach_context so the
    // Founder Context box at the top of the modal auto-fills from prior
    // sessions.
    setOutreachDialogContext('');
    if (assetId) {
      axios.get(`${API}/assets/${assetId}`, { withCredentials: true })
        .then((r) => { setOutreachDialogContext(r.data?.outreach_context || ''); })
        .catch(() => { /* silent — user can paste from scratch */ });
    }
  };

  // v33 (Feb 2026) — Explainable-AI: open the Decision Summary drawer
  // for a mapping row. Fetches from the backend endpoint that assembles
  // the summary from existing row data (no fresh LLM call).
  const openDecisionSummary = async (mappingId, companyName) => {
    if (!mappingId || !companyName) return;
    setDecisionSummary({ open: true, mappingId, companyName, loading: true, data: null, error: '', feedbackOpen: false, feedbackComment: '', feedbackSubmitting: false });
    try {
      const { data } = await axios.get(
        `${API}/ai/mapping/${mappingId}/target/${encodeURIComponent(companyName)}/decision-summary`,
        { withCredentials: true }
      );
      setDecisionSummary(prev => ({ ...prev, loading: false, data }));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load decision summary';
      setDecisionSummary(prev => ({ ...prev, loading: false, error: typeof msg === 'string' ? msg : JSON.stringify(msg) }));
    }
  };

  // Sep 2026 · every override now goes through the Reinstate flow (tier +
  // mandatory rationale) — replaces the old one-click override.
  const submitOverride = () => {
    const { mappingId, companyName } = decisionSummary;
    if (!mappingId || !companyName) return;
    setDecisionSummary(prev => ({ ...prev, open: false }));
    openReinstateFor(companyName, outreachEmailsRef.current?.[companyName] || outreachEmails[companyName]);
  };

  // Sep 2026 · Reinstate flow from the outreach dialog (single company).
  const openReinstateFor = (companyName, info) => {
    setReinstateDlg({
      items: [{ company_name: companyName, rationale: info?.rationale || '', why: info?.softReason || info?.error || '', defaultTier: /regional|distributor|commercial/i.test(`${info?.targetRole || ''} ${info?.fitCategory || ''}`) ? 'regional_partner' : 'targeted' }],
      ctx: { biotech_company_id: outreachDialog.companyId, asset_id: outreachDialog.assetId, mapping_id: outreachDialog.mappingId || '' },
    });
  };
  const onReinstated = (rows) => {
    const names = new Set(rows.map(r => r.company_name));
    const tierLabel = { targeted: 'Targeted', regional_partner: 'Regional partner', exploratory: 'Exploratory' };
    setOutreachEmails(prev => {
      const next = { ...prev };
      rows.forEach(r => { if (next[r.company_name]) next[r.company_name] = { ...next[r.company_name], softReason: '', reinstated: true, reinstatedTier: tierLabel[r.tier] || r.tier, error: null, rationale: (r.rationale || '').trim() || next[r.company_name].rationale }; });
      return next;
    });
    setSelectedCompanies(prev => { const n = new Set(prev); names.forEach(x => n.add(x)); return n; });
    setFunnelDialog(prev => {
      if (!prev) return prev;
      const rows = (prev.rows || []).filter(r => !names.has(r.name));
      return { ...prev, rows, title: String(prev.title || '').replace(/^\d+/, String(rows.length)) };
    });
    fetchData();
  };

  const submitFeedback = async () => {
    const { mappingId, companyName, feedbackComment } = decisionSummary;
    if (!mappingId || !companyName || !feedbackComment.trim()) {
      toast.warning('Please describe what the AI got wrong');
      return;
    }
    setDecisionSummary(prev => ({ ...prev, feedbackSubmitting: true }));
    try {
      await axios.post(
        `${API}/ai/mapping-feedback`,
        { mapping_id: mappingId, company_name: companyName, disposition: 'incorrect_exclusion', comment: feedbackComment },
        { withCredentials: true }
      );
      toast.success('Feedback recorded — thank you');
      setDecisionSummary(prev => ({ ...prev, feedbackOpen: false, feedbackComment: '', feedbackSubmitting: false }));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Feedback failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setDecisionSummary(prev => ({ ...prev, feedbackSubmitting: false }));
    }
  };

  const generateCompanyEmail = async (targetCompanyName) => {
    setOutreachEmails(prev => ({ ...prev, [targetCompanyName]: { ...prev[targetCompanyName], generating: true } }));
    try {
      const info = outreachEmails[targetCompanyName];
      const res = await axios.post(`${API}/ai/generate-company-email`, {
        biotech_company_id: outreachDialog.companyId,
        asset_id: outreachDialog.assetId || '',
        target_company_name: targetCompanyName,
        rationale: info?.rationale || '',
        country: info?.country || '',
        // Aug 2026 · Injected modal-level founder context — highest-priority
        // "honor this" block in the LLM prompt.
        custom_context: outreachDialogContext || '',
      }, { withCredentials: true, timeout: 60000 });
      const _cleanEmail = _stripEmailHtml(res.data.email);
      setOutreachEmails(prev => ({ ...prev, [targetCompanyName]: { ...prev[targetCompanyName], email: _cleanEmail, generating: false } }));
      axios.put(`${API}/ai/email-drafts`, { biotech_company_id: outreachDialog.companyId, asset_id: outreachDialog.assetId || '', target_company_name: targetCompanyName, email: _cleanEmail }, { withCredentials: true }).catch(() => {});
    } catch (err) {
      // Feb 2026 v18 · surface the actual moat-gate rejection reason
      // (422) instead of the generic "Failed to generate". Otherwise the
      // BD lead sees a mystery failure and can't tell that they just
      // need to click Re-run on the mapping.
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' && detail
        ? detail
        : (detail && typeof detail === 'object' && detail.message)
          ? detail.message
          : `Failed to generate email for ${targetCompanyName}`;
      toast.error(msg, { duration: 10000 });
      setOutreachEmails(prev => ({ ...prev, [targetCompanyName]: { ...prev[targetCompanyName], generating: false, error: msg } }));
      // A company whose email could not be generated must not stay ticked —
      // the tick means "will be sent" (user, Sep 9 2026).
      setSelectedCompanies(prev => { const n = new Set(prev); n.delete(targetCompanyName); return n; });
    }
  };

  const [genAllProgress, setGenAllProgress] = useState(null); // { done, total, ok } while bulk generation runs
  const generateAllEmails = async () => {
    if (genAllProgress) return;
    const pool = outreachEmailsRef.current;
    // Feb 2026 (v12) — same hard-guard as sendAllCompanies: skip
    // previously-contacted when the "New companies only" toggle is on.
    const selected = Object.keys(pool).filter(k => {
      if (!selectedCompanies.has(k)) return false;
      if (showNewCompaniesOnly && contactedCompanies.has(k.trim().toLowerCase())) return false;
      return true;
    });
    const missing = selected.filter(k => !pool[k].email);
    if (missing.length === 0) {
      toast.info(`All ${selected.length} selected companies already have generated emails — review them below or hit Send All.`, { duration: 6000 });
      return;
    }
    setGenAllProgress({ done: 0, total: missing.length, ok: 0 });
    let done = 0, ok = 0;
    // Concurrency 3 — each LLM generation takes ~10-15s; sequential was ~20min
    // for 99 emails with no visible progress (looked broken).
    const queue = [...missing];
    const failed = [];
    const worker = async () => {
      while (queue.length > 0) {
        const companyName = queue.shift();
        if (!companyName) break;
        // Feb 2026 (v5) — retry up to 3 attempts per company with expo
        // backoff (1s, 3s) before giving up. Previously ONE transient
        // error (network blip, LLM cold-start, 502) permanently marked
        // the row as "not generated", producing the "78 of 94" gap the
        // user reported.
        let attempt = 0;
        let succeeded = false;
        while (attempt < 3 && !succeeded) {
          try {
            const info = outreachEmailsRef.current[companyName];
            const res = await axios.post(`${API}/ai/generate-company-email`, {
              biotech_company_id: outreachDialog.companyId,
              asset_id: outreachDialog.assetId || '',
              target_company_name: companyName,
              rationale: info?.rationale || '',
              country: info?.country || '',
              // Aug 2026 · Batch-level founder context injected into every
              // email in this Send Outreach modal.
              custom_context: outreachDialogContext || '',
            }, { withCredentials: true, timeout: 90000 });
            const _cleanEmail = _stripEmailHtml(res.data.email);
            setOutreachEmails(prev => ({ ...prev, [companyName]: { ...prev[companyName], email: _cleanEmail, generating: false, error: null } }));
            axios.put(`${API}/ai/email-drafts`, { biotech_company_id: outreachDialog.companyId, asset_id: outreachDialog.assetId || '', target_company_name: companyName, email: _cleanEmail }, { withCredentials: true }).catch(() => {});
            ok += 1;
            succeeded = true;
          } catch (err) {
            // Feb 2026 v18 · if the server returned a 422 (rationale
            // gate rejection), that's a permanent state — no point
            // retrying, the stored rationale will never suddenly pass
            // on a re-attempt. Capture the reason and short-circuit.
            // v33 (Feb 2026) — same rule for 403 (email_generation_
            // blocked from the mapping-quality gate). Previously
            // the loop retried 3× and then reported them as
            // "transient failures", which hid the real refusal
            // reason from BD leads.
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 422 && typeof detail === 'string') {
              setOutreachEmails(prev => ({ ...prev, [companyName]: { ...prev[companyName], generating: false, error: detail } }));
              break;  // skip the remaining retries for this row
            }
            if (status === 403 && detail && typeof detail === 'object') {
              // Surface the plain-English `message` (or `reason`)
              // from the QC gate so the BD lead sees WHY.
              const gateMsg = detail.message || detail.reason || 'Email generation blocked by mapping quality gate.';
              setOutreachEmails(prev => ({ ...prev, [companyName]: { ...prev[companyName], generating: false, error: gateMsg } }));
              break;
            }
            attempt += 1;
            if (attempt < 3) {
              // Backoff: 1s, then 3s. Keeps the total retry budget under
              // ~4s per row so a batch of 16 failures adds < 1 min max.
              await new Promise(r => setTimeout(r, attempt === 1 ? 1000 : 3000));
            }
          }
        }
        if (!succeeded) failed.push(companyName);
        done += 1;
        setGenAllProgress({ done, total: missing.length, ok });
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, missing.length) }, worker));
    setGenAllProgress(null);
    const failedCount = missing.length - ok;
    // Feb 2026 v18 · classify failures — rationale-gate rejections
    // (422) vs quality-control gate refusals (403) vs transient errors.
    // Rejections need a mapping re-run, not another email retry.
    const poolAfter = outreachEmailsRef.current;
    const gateRejected = missing.filter(k => (poolAfter[k]?.error || '').includes('Rationale quality gate rejected'));
    // v33 · plain-English QC gate refusals — anything with an .error
    // set that isn't a rationale-gate rejection is a quality-control
    // refusal (Alcon-style hard block, low confidence, exploratory,
    // etc.). We surface these separately so the user knows the
    // real headline reason (e.g. "Alcon only operates in eye care").
    const qcRefused = missing.filter(k => {
      const e = poolAfter[k]?.error || '';
      return e && !e.includes('Rationale quality gate rejected');
    });
    const transientCount = failedCount - gateRejected.length - qcRefused.length;
    if (failedCount === 0) {
      toast.success(`Generated all ${ok} emails`);
    } else if (gateRejected.length === failedCount) {
      toast.warning(
        `Generated ${ok}. ${gateRejected.length} rejected by the rationale gate — the stored rationales for those targets are too weak. Click "Re-run" on this mapping tile to regenerate rationales, then try again.`,
        { duration: 15000 },
      );
    } else if (qcRefused.length === failedCount) {
      // Every failure is a QC-gate refusal — surface the top reason.
      const sampleName = qcRefused[0];
      const sampleReason = (poolAfter[sampleName]?.error || '').trim();
      toast.warning(
        `Generated ${ok}. ${qcRefused.length} refused by the quality-control gate — click any row to see the specific reason. Example (${sampleName}): "${sampleReason.slice(0, 160)}${sampleReason.length > 160 ? '…' : ''}"`,
        { duration: 18000 },
      );
    } else {
      const parts = [];
      if (gateRejected.length) parts.push(`${gateRejected.length} need mapping re-run`);
      if (qcRefused.length) parts.push(`${qcRefused.length} refused by quality gate (see row for reason)`);
      if (transientCount > 0) parts.push(`${transientCount} transient: ${failed.slice(0, 3).join(', ')}${failed.length > 3 ? `, +${failed.length - 3} more` : ''}`);
      toast.warning(`Generated ${ok} of ${missing.length}. ${parts.join(' · ')}.`, { duration: 15000 });
    }
  };

  // Shared payload for single sends and the server-side queue.
  const buildSendPayload = (targetCompanyName, contact, info) => {
    const lines = (info?.email || '').split('\n');
    const subjectLine = lines.find(l => l.toLowerCase().startsWith('subject:'));
    const subject = subjectLine ? subjectLine.replace(/^subject:\s*/i, '').trim() : 'Partnership Opportunity';
    const body = lines.filter(l => !l.toLowerCase().startsWith('subject:')).join('\n').trim();
    return {
      to_email: contact.email,
      to_name: contact.name,
      subject,
      email_body: body,
      biotech_company_id: outreachDialog.companyId,
      biotech_company_name: outreachDialog.companyName,
      asset_id: outreachDialog.assetId || '',
      asset_name: outreachDialog.assetName || '',
      pharma_company_name: targetCompanyName,
      designation: contact.designation || '',
      override_interest_mismatch: !!contact.__override_interest_mismatch,
      override_specialist_routing: !!contact.__override_specialist_routing,
    };
  };

  const sendEmailToContact = async (targetCompanyName, contact) => {
    const info = outreachEmails[targetCompanyName];
    if (!info?.email) return;
    const key = `${targetCompanyName}_${contact.email}`;
    setSendingEmails(prev => ({ ...prev, [key]: true }));
    setSendErrors(prev => { if (!prev[key]) return prev; const n = { ...prev }; delete n[key]; return n; });
    let failReason = '';
    const fail = (msg) => { failReason = msg; };
    try {
      const res = await axios.post(`${API}/ai/send-outreach-email`, buildSendPayload(targetCompanyName, contact, info), { withCredentials: true });
      setOutreachEmails(prev => ({
        ...prev,
        [targetCompanyName]: { ...prev[targetCompanyName], sent: [...(prev[targetCompanyName]?.sent || []), contact.email] }
      }));
      if (res.data?.already_sent) toast(res.data.message || `Already emailed ${contact.name} — marked as sent`, { icon: '✅' });
      else toast.success(`Email sent to ${contact.name}`);
      // Mark mapping as "in_progress" if not already marked complete
      const eKey = outreachDialog.assetId || outreachDialog.companyId;
      const latestMapping = (mappingsByKey[eKey] || [])[0];
      if (latestMapping && latestMapping.email_sent_pharma !== 'complete' && latestMapping.email_sent_pharma !== true) {
        axios.patch(`${API}/ai/map-targets/${latestMapping.id}/email-status`, { email_type: 'pharma', status: 'in_progress' }, { withCredentials: true }).catch(() => {});
        setMappings(prev => prev.map(m => m.id === latestMapping.id ? { ...m, email_sent_pharma: 'in_progress' } : m));
      }
      return { sent: true };
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        // Gmail per-user sending rate limit — signal the caller so bulk
        // loops can pause + auto-resume instead of hammering more 429s.
        const retryAfter = (detail && typeof detail === 'object' && detail.retry_after) || '';
        toast.error(`Gmail rate limit reached — sending paused${retryAfter ? ` until ~${new Date(retryAfter).toLocaleTimeString()}` : ''}.`, { duration: 8000 });
        return { rateLimited: true, retryAfter };
      }
      if ([400, 403].includes(err?.response?.status) && detail && typeof detail === 'object' && detail.error === 'routing_policy_violation') {
        fail(detail.message || `Blocked by routing policy (${detail.policy || 'policy'})`);
        if (detail.policy === 'specialist_routing') {
          const specs = (detail.specialists || []).map(s => s.name || s.email).filter(Boolean).join(', ');
          toast.error(
            `🎯 ${detail.pharma_company} routes ${detail.asset_therapeutic_area || 'this TA'} to ${specs}. Use "Send anyway" to override.`,
            { duration: 8000 }
          );
        } else if (detail.policy === 'interest_area_mismatch') {
          toast.error(
            `🎯 ${detail.contact_name}'s stated interests (${(detail.stated_interests || []).join(', ')}) don't include ${detail.asset_therapeutic_area || 'this TA'}. Use "Send anyway" to override.`,
            { duration: 8000 }
          );
        } else if (detail.policy === 'sub_ta_mismatch') {
          toast.error(
            `🎯 ${detail.contact_name} focuses on ${(detail.stated_interests || []).join(', ')} — this asset (${detail.asset_therapeutic_area || detail.asset_indication || 'unknown'}) is a different sub-area within the same broad TA. Use "Send anyway" to override.`,
            { duration: 10000 }
          );
        } else if (detail.policy === 'not_buyer_eligible') {
          toast.error(
            `🚫 ${detail.company_name} is not buyer-eligible — the handshake indicator is OFF${detail.licensing_category && detail.licensing_category !== 'unknown' ? ` (declared licensing direction: ${detail.licensing_category})` : ''}. Toggle handshake ON at the company profile if this changed, or use routing override to bypass this send.`,
            { duration: 12000 }
          );
        } else if (detail.policy === 'bd_routing_rule') {
          const targets = (detail.rule_recipients || []).map(r => r.email).filter(Boolean).join(', ');
          toast.error(
            `📋 ${detail.pharma_company} has a per-TA routing rule for ${detail.asset_therapeutic_area || 'this TA'}: send to ${targets} instead.`,
            { duration: 8000 }
          );
        } else if (detail.policy === 'contact_company_mismatch') {
          toast.error(`🏢 ${detail.message}`, { duration: 12000 });
        } else if (detail.policy === 'preferred_stage_mismatch') {
          toast.error(
            `⛔ ${detail.contact_name} at ${detail.contact_company || 'this pharma'} only accepts ${(detail.contact_preferred_stages || []).join(', ')} assets — this asset is ${detail.asset_stage || 'earlier stage'}. Hard gate: edit the contact's Preferred Stages to change.`,
            { duration: 12000 }
          );
        } else if (detail.policy === 'company_stage_gate') {
          toast.error(
            `⛔ ${detail.contact_company || 'This pharma'} has a company-wide stage gate (${detail.company_min_stage}+ only) — this asset is ${detail.asset_stage || 'earlier stage'}. Lift it on the Company Profile page.`,
            { duration: 12000 }
          );
        } else if (detail.mode === 'portal_only' && detail.portal_url) {
          toast.error(`🌐 ${detail.pharma_company} accepts submissions via portal only — click to open`, {
            duration: 8000,
            action: { label: 'Open Portal', onClick: () => window.open(detail.portal_url, '_blank', 'noopener') },
          });
        } else {
          const who = detail.pharma_company || detail.company_name || detail.contact_company;
          const via = detail.poc_name || detail.poc_email;
          toast.error(
            detail.message || (who && via ? `🔒 Blocked by routing policy: ${who} routes via ${via} only.` : '🔒 Blocked by routing policy.'),
            { duration: 8000 }
          );
        }
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || detail?.error || `Failed to send to ${contact.name}`);
        toast.error(msg);
        fail(msg);
      }
      return { failed: true, reason: failReason };
    } finally {
      if (failReason) setSendErrors(prev => ({ ...prev, [key]: failReason }));
      setSendingEmails(prev => ({ ...prev, [key]: false }));
    }
  };

  const sendAllForCompany = async (targetCompanyName) => {
    const info = outreachEmailsRef.current[targetCompanyName] || outreachEmails[targetCompanyName];
    if (!info?.email) return { done: true };
    const policy = getRoutingPolicy(targetCompanyName);
    // Portal-only mode: never send, open portal instead
    if (policy && policy.mode === 'portal_only') {
      if (policy.portal_url) window.open(policy.portal_url, '_blank', 'noopener');
      toast(`${targetCompanyName} accepts submissions via portal only — opening now`, { icon: '🌐' });
      return { done: true };
    }
    const pocEmail = policy ? (policy.poc_email || '').toLowerCase() : '';
    let sentCount = 0;
    let failedCount = 0;
    for (const contact of info.contacts) {
      // Respect single-POC routing policy: skip non-POC contacts silently (they're greyed in UI)
      if (policy && policy.mode === 'single_poc' && (contact.email || '').toLowerCase() !== pocEmail) continue;
      // Skip contacts flagged Do-Not-Contact at the contact level
      if (dncContactEmails[(contact.email || '').trim().toLowerCase()]) continue;
      // Skip contacts whose preferred stages exclude this asset (backend hard-blocks anyway)
      if (contact.__stage_gate) continue;
      // Fresh sent-list read (via ref) so auto-resume after a rate-limit
      // pause never re-sends emails delivered before the pause.
      const sentNow = (outreachEmailsRef.current[targetCompanyName]?.sent) || info.sent || [];
      if (!sentNow.includes(contact.email)) {
        const res = await sendEmailToContact(targetCompanyName, contact);
        if (res?.rateLimited) return { ...res, sentCount, failedCount };
        if (res?.sent) sentCount += 1;
        if (res?.failed) failedCount += 1;
        await new Promise(r => setTimeout(r, 1500)); // pace to stay under Gmail's rate limits
      }
    }
    return { done: true, sentCount, failedCount };
  };

  // ── Gmail rate-limit pause + auto-resume ──────────────────────────────
  const [rateLimitPause, setRateLimitPause] = useState(null); // { until: Date }
  const rateLimitTimerRef = useRef(null);
  const scheduleRateLimitResume = (res, resumeFn) => {
    const retryMs = res.retryAfter ? (new Date(res.retryAfter).getTime() - Date.now()) : 0;
    // At least 60s; +30s buffer past Gmail's stated retry time; cap at 1h.
    const waitMs = Math.min(Math.max(retryMs + 30000, 60000), 3600000);
    const until = new Date(Date.now() + waitMs);
    setRateLimitPause({ until });
    toast.warning(`Gmail rate limit — sending paused. Auto-resumes at ${until.toLocaleTimeString()}. Keep this tab open; already-sent emails won't repeat.`, { duration: 15000 });
    if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current);
    rateLimitTimerRef.current = setTimeout(() => {
      setRateLimitPause(null);
      toast.info('Gmail limit window passed — resuming email sending…');
      resumeFn();
    }, waitMs);
  };
  useEffect(() => () => { if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current); }, []);

  const sendAllForCompanyWithResume = async (targetCompanyName) => {
    const res = await sendAllForCompany(targetCompanyName);
    if (res?.rateLimited) scheduleRateLimitResume(res, () => sendAllForCompanyWithResume(targetCompanyName));
  };

  const [sendingAll, setSendingAll] = useState(false);
  const [sendAllProgress, setSendAllProgress] = useState(null); // { done, total, sent } while Send All runs
  const [activeQueue, setActiveQueue] = useState(null); // server-side send queue being polled
  const queuePollRef = useRef(null);

  // Apply a queue snapshot to the dialog: sent → green, failed → red reason.
  const applyQueueSnapshot = (q) => {
    if (!q) return;
    setActiveQueue(q);
    setSendAllProgress({ done: q.counts.sent + q.counts.failed, total: q.counts.total, sent: q.counts.sent });
    const sentByTarget = {};
    const failed = {};
    q.items.forEach(it => {
      if (it.status === 'sent') (sentByTarget[it.target_company_name] = sentByTarget[it.target_company_name] || []).push(it.to_email);
      if (it.status === 'failed') failed[`${it.target_company_name}_${it.to_email}`] = it.reason || 'refused';
    });
    if (Object.keys(sentByTarget).length) {
      setOutreachEmails(prev => {
        const next = { ...prev };
        Object.entries(sentByTarget).forEach(([t, emails]) => {
          if (!next[t]) return;
          const cur = next[t].sent || [];
          const add = emails.filter(e => !cur.includes(e));
          // Queue sends always count as "this session" (even after a reopen) so the
          // card stays visible with green badges instead of collapsing.
          const prior = (next[t].priorSent || []).filter(e => !emails.includes(e));
          if (add.length || prior.length !== (next[t].priorSent || []).length) next[t] = { ...next[t], sent: [...cur, ...add], priorSent: prior };
        });
        return next;
      });
    }
    if (Object.keys(failed).length) setSendErrors(prev => ({ ...prev, ...failed }));
  };

  const stopQueuePolling = () => { if (queuePollRef.current) { clearInterval(queuePollRef.current); queuePollRef.current = null; } };

  const finishQueue = async (q) => {
    stopQueuePolling();
    setSendingAll(false);
    setSendAllProgress(null);
    setActiveQueue(null);
    if (q.status === 'cancelled') { toast.info(`Batch cancelled — ${q.counts.sent} sent before stopping`); return; }
    if (q.counts.failed > 0) toast.error(`${q.counts.failed} email${q.counts.failed !== 1 ? 's' : ''} could not be sent — each failed contact row shows the reason in red with a Retry button.`, { duration: 15000 });
    toast.success(q.counts.sent > 0 ? `Sent ${q.counts.sent} email${q.counts.sent !== 1 ? 's' : ''}` : 'Nothing new was sent — every queued contact was refused (see red rows)');
    try {
      if (q.counts.sent > 0) {
        const eKey = outreachDialog.assetId || outreachDialog.companyId;
        const latestMapping = (mappingsByKey[eKey] || [])[0];
        if (latestMapping && latestMapping.email_sent_pharma !== 'complete' && latestMapping.email_sent_pharma !== true) {
          const freshPool = outreachEmailsRef.current;
          const anyRemaining = Object.entries(freshPool).some(([name, info]) => {
            if (!selectedCompanies.has(name) || !info?.email) return false;
            const contacts = info.contacts || [];
            return contacts.length > 0 && contacts.some(c => !(info.sent || []).includes(c.email));
          });
          const nextStatus = anyRemaining ? 'in_progress' : 'complete';
          await axios.patch(`${API}/ai/map-targets/${latestMapping.id}/email-status`, { email_type: 'pharma', status: nextStatus }, { withCredentials: true });
          setMappings(prev => prev.map(m => m.id === latestMapping.id ? { ...m, email_sent_pharma: nextStatus } : m));
        }
      }
    } catch (e) { /* non-fatal */ }
  };

  const startQueuePolling = (queueId) => {
    stopQueuePolling();
    setSendingAll(true);
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/ai/send-queue/${queueId}`, { withCredentials: true });
        applyQueueSnapshot(data);
        if (['done', 'cancelled', 'failed'].includes(data.status)) await finishQueue(data);
      } catch { /* transient — keep polling */ }
    };
    tick();
    queuePollRef.current = setInterval(tick, 3000);
  };

  const cancelActiveQueue = async () => {
    if (!activeQueue) return;
    try { await axios.post(`${API}/ai/send-queue/${activeQueue.id}/cancel`, {}, { withCredentials: true }); } catch { /* polling will settle */ }
  };

  useEffect(() => () => stopQueuePolling(), []);

  // Reopening the dialog while a server batch is still running → re-attach.
  useEffect(() => {
    if (!outreachDialog.open || !outreachDialog.companyId) { stopQueuePolling(); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/ai/send-queue/active?biotech_company_id=${outreachDialog.companyId}&asset_id=${outreachDialog.assetId || ''}`, { withCredentials: true });
        if (data?.queue) startQueuePolling(data.queue.id);
      } catch { /* none */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachDialog.open, outreachDialog.companyId, outreachDialog.assetId]);
  const [outreachSummary, setOutreachSummary] = useState({}); // { assetId: { pharma_sent: N, investor_sent: N } }
  // Live mirror of outreachEmails so paused/resumed bulk sends always read
  // fresh sent-lists instead of a stale closure snapshot.
  const outreachEmailsRef = useRef({});
  useEffect(() => { outreachEmailsRef.current = outreachEmails; }, [outreachEmails]);

  // Feb 2026 — user request: every selected recipient's email must be
  // pre-generated by the time the Send Outreach modal is usable, so the
  // "39 not generated yet" state never appears. We auto-fire the same
  // parallel generator the "Generate Emails" button uses, but only once
  // per modal-open (guarded via a ref keyed on the dialog target).
  const autoGenFiredRef = useRef('');
  const autoGenRetryCountRef = useRef(0);
  const draftsRestoreFailedRef = useRef(false);
  useEffect(() => {
    if (!outreachDialog.open) {
      autoGenFiredRef.current = '';
      autoGenRetryCountRef.current = 0;
      return;
    }
    if (selectedCompanies.size === 0) return;
    // Oct 2026 · never auto-regenerate when saved drafts could not be loaded.
    if (draftsRestoreFailedRef.current) return;
    const gate = `${outreachDialog.companyId}::${outreachDialog.assetId || ''}`;
    const pool = outreachEmailsRef.current;
    const missing = Array.from(selectedCompanies).filter(
      (k) => pool[k] && !pool[k].email && !pool[k].generating,
    );
    if (missing.length === 0) return;
    // Feb 2026 (v5) — allow up to 2 auto-generate sweeps per modal open
    // (initial + one automatic top-up), so if the first pass leaves any
    // stragglers (network blip, LLM cold-start) they get retried without
    // the user having to click "Generate Emails" a second time.
    if (autoGenFiredRef.current === gate && autoGenRetryCountRef.current >= 1) return;
    autoGenFiredRef.current = gate;
    autoGenRetryCountRef.current += 1;
    // Small delay so the modal renders (and the user sees the progress
    // pill on the "Generate Emails" button) before we kick off.
    const t = setTimeout(() => { generateAllEmails(); }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachDialog.open, outreachDialog.companyId, outreachDialog.assetId, selectedCompanies, outreachEmails]);

  const sendAllCompanies = async () => {
    setSendingAll(true);
    try {
      // True one-click flow: if some selected companies don't have generated
      // emails yet, generate those first (with progress), THEN send — so the
      // user never has to click per-company "Send to All" buttons.
      const missingCount = Object.keys(outreachEmailsRef.current).filter(k => selectedCompanies.has(k) && !outreachEmailsRef.current[k].email).length;
      if (missingCount > 0) {
        toast.info(`Generating ${missingCount} missing email${missingCount !== 1 ? 's' : ''} before sending…`);
        await generateAllEmails();
      }
      const pool = outreachEmailsRef.current;
      // Sep 2026 · server-side queue: build every eligible (target, contact)
      // pair with the same skip rules as sendAllForCompany, enqueue once, and
      // poll. The server keeps sending after this tab/laptop is closed.
      const items = [];
      Object.entries(pool).forEach(([name, info]) => {
        if (!selectedCompanies.has(name)) return;
        if (showNewCompaniesOnly && contactedCompanies.has(name.trim().toLowerCase())) return;
        if (!info.email) return;
        const policy = getRoutingPolicy(name);
        if (policy && policy.mode === 'portal_only') return;
        const pocEmail = policy ? (policy.poc_email || '').toLowerCase() : '';
        (info.contacts || []).forEach(contact => {
          if (!contact.email) return;
          if (policy && policy.mode === 'single_poc' && (contact.email || '').toLowerCase() !== pocEmail) return;
          if (dncContactEmails[(contact.email || '').trim().toLowerCase()]) return;
          if (contact.__stage_gate) return;
          if ((info.sent || []).includes(contact.email)) return;
          items.push({ target_company_name: name, payload: buildSendPayload(name, contact, info) });
        });
      });
      if (!items.length) {
        toast.success('Nothing new to send — all selected contacts were already emailed');
        setSendingAll(false);
        return;
      }
      const { data: q } = await axios.post(`${API}/ai/send-queue`, {
        biotech_company_id: outreachDialog.companyId,
        asset_id: outreachDialog.assetId || '',
        items,
      }, { withCredentials: true });
      toast.success(`${items.length} email${items.length !== 1 ? 's' : ''} queued — sending continues on the server even if you close this tab`, { duration: 8000 });
      startQueuePolling(q.id);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not start the send batch');
      setSendingAll(false);
      setSendAllProgress(null);
    }
  };

  // --- Unified Investor Outreach: auto-match + email + send ---
  const openInvestorOutreach = (companyId, companyName, assetId, assetName) => {
    setInvestorEmailText('');
    setInvestorBulkTask(null);
    setInvestorMatchResult(null);
    setInvestorMatchLoading(true);
    setTrackingStats(null);
    setInvestorDialog({ open: true, companyId, companyName, assetId, assetName });
    // Auto-trigger investor matching (no prompt needed)
    handleInvestorMatch(companyId, assetId);
  };

  const handleInvestorMatch = async (companyId, assetId) => {
    setInvestorMatchLoading(true);
    setInvestorMatchResult(null);
    try {
      const res = await axios.post(`${API}/ai/match-investors`, {
        biotech_company_id: companyId || investorDialog.companyId,
        asset_id: assetId || investorDialog.assetId || '',
        prompt: '',
      }, { withCredentials: true, timeout: 120000 });
      setInvestorMatchResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to match investors');
    } finally {
      setInvestorMatchLoading(false);
    }
  };

  const generateInvestorEmailForDialog = async (companyId, assetId) => {
    const key = `${assetId || companyId}_investor`;
    setGeneratingEmail(prev => ({ ...prev, [key]: true }));
    try {
      const res = await axios.post(`${API}/ai/generate-email`, {
        biotech_company_id: companyId,
        asset_id: assetId || '',
        email_type: 'investor',
      }, { withCredentials: true, timeout: 60000 });
      setInvestorEmailText(res.data.email.replace('Dear Sir/Madam', 'Dear [Investor Name]').replace('Dear investor', 'Dear [Investor Name]').replace('Dear Investor', 'Dear [Investor Name]').replace('Dear Sir or Madam', 'Dear [Investor Name]'));
      setEmails(prev => ({ ...prev, [key]: res.data.email }));
      toast.success('Investor email generated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate investor email');
    } finally {
      setGeneratingEmail(prev => ({ ...prev, [key]: false }));
    }
  };

  const sendBulkInvestorEmails = async () => {
    if (!investorEmailText.trim() || !investorMatchResult?.investors?.length) return;
    const lines = investorEmailText.split('\n');
    const subjectLine = lines.find(l => l.toLowerCase().startsWith('subject:'));
    const subject = subjectLine ? subjectLine.replace(/^subject:\s*/i, '').trim() : 'Investment Opportunity';
    const body = lines.filter(l => !l.toLowerCase().startsWith('subject:')).join('\n').trim();
    // Only send to matched investor IDs
    const matchedIds = investorMatchResult.investors.map(inv => inv.id);
    try {
      const res = await axios.post(`${API}/ai/send-bulk-investor-emails`, {
        subject,
        email_body: body,
        biotech_company_id: investorDialog.companyId,
        biotech_company_name: investorDialog.companyName,
        asset_id: investorDialog.assetId || '',
        asset_name: investorDialog.assetName || '',
        investor_ids: matchedIds,
      }, { withCredentials: true });
      setInvestorBulkTask(res.data);
      toast.success(`Sending to ${res.data.total} matched investors...`);
      startInvestorPoll(res.data.task_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start bulk send');
    }
  };

  const startInvestorPoll = (taskId) => {
    if (investorPollRef.current) clearInterval(investorPollRef.current);
    investorPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/ai/bulk-email-status/${taskId}`, { withCredentials: true });
        setInvestorBulkTask(res.data);
        if (res.data.status !== 'running') {
          clearInterval(investorPollRef.current);
          investorPollRef.current = null;
          if (res.data.status === 'completed') {
            toast.success(`Bulk send complete! ${res.data.sent} emails sent.`);
            fetchTrackingStats(taskId);
            // Mark investor email as sent in mappings
            const eKey = investorDialog.assetId || investorDialog.companyId;
            const latestMapping = (mappingsByKey[eKey] || [])[0];
            if (latestMapping) {
              await axios.patch(`${API}/ai/map-targets/${latestMapping.id}/email-status`, { email_type: 'investor' }, { withCredentials: true }).catch(() => {});
              setMappings(prev => prev.map(m => m.id === latestMapping.id ? { ...m, email_sent_investor: true } : m));
            }
          }
        }
      } catch { /* retry */ }
    }, 2000);
  };

  const cancelBulkSend = async () => {
    if (!investorBulkTask?.task_id) return;
    try {
      await axios.post(`${API}/ai/bulk-email-cancel/${investorBulkTask.task_id}`, {}, { withCredentials: true });
      toast.info('Cancellation requested');
    } catch { toast.error('Failed to cancel'); }
  };

  const fetchTrackingStats = async (taskId) => {
    try {
      const res = await axios.get(`${API}/email-tracking/stats?task_id=${taskId}`, { withCredentials: true });
      setTrackingStats(res.data);
    } catch { /* ignore */ }
  };

  // Cleanup investor poll on unmount
  useEffect(() => {
    return () => { if (investorPollRef.current) clearInterval(investorPollRef.current); };
  }, []);
  const STAGE_ORDER = { 'Commercial': 0, 'Phase 3': 1, 'Phase 2': 2, 'Phase 1/2': 3, 'Phase 1': 3, 'Clinical Stage': 4, 'IND': 5, 'Pre-Clinical (IND-enabling)': 6, 'Pre-Clinical': 6, 'Discovery': 7, 'Platform': 8 };
  assets.forEach(a => {
    if (a.is_hidden) return; // Skip assets the user has hidden from outreach
    if (!assetsByCompany[a.company_id]) assetsByCompany[a.company_id] = [];
    assetsByCompany[a.company_id].push(a);
  });
  // Sort each company's assets by stage (most advanced first)
  Object.values(assetsByCompany).forEach(arr => arr.sort((a, b) => (STAGE_ORDER[a.stage] ?? 9) - (STAGE_ORDER[b.stage] ?? 9)));

  // Group mappings by asset_id (or company_id for legacy)
  const mappingsByKey = {};
  mappings.forEach(m => {
    const key = m.asset_id || m.biotech_company_id;
    if (!mappingsByKey[key]) mappingsByKey[key] = [];
    mappingsByKey[key].push(m);
  });

  // Builds a synthesized "primary" mapping for an asset that represents the
  // CUMULATIVE target universe across every completed run. Latest run wins on
  // dedup, so its tier / rationale / fit_score / approval_status are kept; a
  // target that only appears in an older run is preserved with its older-run
  // metadata. This mirrors the public-status-page union (share.py) so the
  // CRM-internal AI Mapping view shows the same total a buyer would see.
  // The wrapper retains the latest mapping's id / created_at / region /
  // email_sent_* / share_token / status so per-run actions (delete, share,
  // re-run, outreach, refresh discovery) keep working unchanged.
  const buildUnionMapping = (assetMappings) => {
    const latest = (assetMappings || [])[0];
    if (!latest) return null;
    const completed = (assetMappings || []).filter(
      mm => mm?.status === 'completed' && Array.isArray(mm?.target_companies)
    );
    if (completed.length <= 1) return latest; // Single (or zero) completed run — nothing to union
    const seen = new Set();
    const unioned = [];
    // Iterate latest-first so latest run's metadata wins on collision
    for (const mm of completed) {
      for (const tc of (mm.target_companies || [])) {
        const k = (tc.company_name || '').trim().toLowerCase();
        if (!k || seen.has(k)) continue;
        seen.add(k);
        unioned.push(tc);
      }
    }
    return {
      ...latest,
      target_companies: unioned,
      _is_union: true,
      _runs_count: completed.length,
    };
  };

  const companiesWithNCD = new Set();
  documents.forEach(d => {
    if (d.doc_type === 'Non-Confidential Deck' && !d.is_deleted) {
      companiesWithNCD.add(d.entity_id);
    }
  });

  const renderMappingResults = (m, emailKey, companyId, assetId, label, opts = {}) => {
    const eKey = assetId || companyId;
    const previousTargetNames = opts.previousTargetNames || null; // Set<string lowercase> or null
    const isLatest = !!opts.isLatest;
    const runsCount = opts.runsCount || 0;
    const isUnion = !!m._is_union;
    const campaignNumber = opts.campaignNumber || null; // 1-indexed (oldest=1) for previous-run rows
    const totalCampaigns = opts.totalCampaigns || 0;
    // Helper — is this target net-new vs the previous run?
    const isNewTarget = (tc) => {
      if (!previousTargetNames) return false;
      const nm = (tc.company_name || '').trim().toLowerCase();
      return !previousTargetNames.has(nm);
    };
    const showNewOnly = newOnlyMappingIds.has(m.id);
    // Each individual past campaign defaults to collapsed (only the header bar
    // is visible). User can expand a single campaign's target list without
    // affecting any sibling. The synthesized "Combined view" row (isLatest)
    // stays expanded by default since it's the primary view.
    const bodyExpanded = isLatest || expandedCampaignIds.has(m.id);
    const canCollapseBody = !isLatest;
    return (
      <div key={m.id} className="border-b border-slate-50 last:border-b-0">
        {isLatest && m.status === 'completed' && m.stats?.ta_unresolved && (
          <div className="mx-5 my-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 flex items-start gap-2" data-testid={`ta-unresolved-banner-${m.id}`}>
            <svg className="w-3.5 h-3.5 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.75-2.97l-7.07-12a2 2 0 00-3.5 0l-7.07 12A2 2 0 004.93 19z" /></svg>
            <div className="leading-relaxed">
              <span className="font-semibold">Thin results warning:</span> this asset&apos;s TA / indication didn&apos;t match any canonical
              category, so only the web-discovery lane ran. Result may be sparse.{' '}
              <a href="/data-ops/ta-audit" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900">
                See all such assets →
              </a>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between px-5 py-2 bg-slate-50/50">
          <div className="flex items-center gap-2 flex-wrap">
            {isLatest && isUnion ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-700" title="This row is NOT a single campaign — it's the deduplicated combined target list across every campaign you've ever run for this asset.">
                🔄 Combined view across all {runsCount || m._runs_count} campaigns
              </span>
            ) : isLatest ? (
              <span className="text-[11px] font-semibold text-slate-700" title="Latest campaign run for this asset.">
                Latest campaign · {new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            ) : (
              <span className="text-[11px] font-semibold text-slate-700" title={`Campaign #${campaignNumber} of ${totalCampaigns} — sequence number is based on creation order (oldest = #1).`}>
                Campaign #{campaignNumber}{totalCampaigns ? ` of ${totalCampaigns}` : ''} · {new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {m.status === 'completed' && !isLatest && <Badge className="bg-emerald-100 text-emerald-700 text-[10px] font-semibold" title={isLatest ? "Total pharmas in this asset's target list (deduplicated across all campaigns)" : "Pharmas this single campaign produced"}>📦 {m.target_companies?.length || 0} in target list</Badge>}
            {m.region && <Badge className="bg-orange-100 text-orange-700 text-[10px]">{m.region}</Badge>}
            {/*
              Per-asset cumulative stats (net-new vs last run, already emailed,
              contacts to source) only make sense on the LATEST union row. We
              intentionally suppress them on `Previous run` history rows because
              those numbers reflect the live cumulative state, not the state at
              the time of that specific run — surfacing them on history rows
              was confusing the user with duplicate / misleading counts.
            */}
            {isLatest && m.status === 'completed' && m.target_companies?.length > 0 && (() => {
              const newCount = previousTargetNames ? (m.target_companies || []).filter(isNewTarget).length : 0;
              const trulyMissingCount = (m.target_companies || []).filter(tc =>
                !isTargetRefused(tc) && !softRefusalReason(tc) && findMatchingContacts(tc.company_name, tc).length === 0 && !serverContactNames.has((tc.company_name || '').trim().toLowerCase())
              ).length;
              return (
                <>
                  {previousTargetNames && newCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setNewOnlyMappingIds(prev => {
                        const next = new Set(prev);
                        next.has(m.id) ? next.delete(m.id) : next.add(m.id);
                        return next;
                      })}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold transition-colors ${showNewOnly ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}
                      title="Click to filter the list to only the companies that are net-new compared to the previous run"
                      data-testid={`new-since-last-badge-${eKey}`}
                    >
                      🆕 +{newCount} net-new
                    </button>
                  )}
                  {false ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-500 animate-pulse"
                      data-testid={`missing-leads-loading-${eKey}`}
                    >
                      Checking contacts…
                    </span>
                  ) : false && trulyMissingCount > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-amber-100 text-amber-700"
                      title="Pharmas in this list that have NO contacts in your CRM yet. Click 'Copy pharmas without contacts' to copy these names — paste into LinkedIn / Apollo to find the right BD person, then add them as contacts."
                      data-testid={`missing-leads-badge-${eKey}`}
                    >
                      ⚠️ {trulyMissingCount} pharmas without contacts
                    </span>
                  )}
                </>
              );
            })()}
            {m.status === 'processing' && <Badge className="bg-violet-100 text-violet-600 text-[10px] animate-pulse">Processing...</Badge>}
            {m.status === 'failed' && <Badge className="bg-red-100 text-red-600 text-[10px]">Failed</Badge>}
            {isLatest && (() => {
              const s = outreachSummary[eKey] || {};
              const sent = (s.pharma_sent || 0) + (s.investor_sent || 0);
              const totalContacts = s.contacts_emailed || 0;
              // Count manually rejected (Exclude List) + auto-excluded targets for this mapping.
              // The same name can appear in both buckets — we union them by name (case-insensitive).
              const rejectedNames = new Set(
                (m.target_companies || [])
                  .filter(tc => tc.approval_status === 'rejected')
                  .map(tc => (tc.company_name || '').trim().toLowerCase())
                  .filter(Boolean)
              );
              const autoExcludedNames = new Set(
                Object.keys(outreachEmails || {})
                  .filter(k => (outreachEmails[k]?.exclusionReasons || []).length > 0)
                  .map(k => (outreachEmails[k]?.company_name || k).trim().toLowerCase())
                  .filter(Boolean)
              );
              const excludedTotal = new Set([...rejectedNames, ...autoExcludedNames]).size;
              return null; // superseded by the funnel strip (Sep 2026)
              return (
                <div className="inline-flex items-center gap-1.5 text-[10px] font-medium ml-1" data-testid={`deliverability-strip-${eKey}`}>
                  {sent > 0 && (
                    <span className="px-2 py-1 rounded-md text-[10px] font-semibold bg-sky-100 text-sky-700" title={`Pharma companies emailed for this asset${totalContacts ? ` — across ${totalContacts} individual contacts` : ''}`}>✉️ {sent} emailed</span>
                  )}
                  {(s.bounced || 0) > 0 && (
                    <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-100" title="Pharma companies where at least one address bounced">{s.bounced} bounced</span>
                  )}
                  {excludedTotal > 0 && (
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); setExcludedDialogMapping(m); }}
                      className="px-2 py-1 rounded-md text-[10px] font-semibold bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors cursor-pointer"
                      title="Click to view every excluded pharma in this campaign with the reason for each"
                      data-testid={`view-excluded-${m.id}`}
                    >
                      🚫 {excludedTotal} excluded
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-1">
            {canCollapseBody && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-[10px] text-slate-500 hover:text-slate-700 hover:bg-slate-100 cursor-pointer"
                onClick={() => setExpandedCampaignIds(prev => {
                  const next = new Set(prev);
                  next.has(m.id) ? next.delete(m.id) : next.add(m.id);
                  return next;
                })}
                data-testid={`toggle-campaign-${m.id}`}
                title={bodyExpanded ? "Hide this campaign's target list" : "Show this campaign's target list"}
              >
                {bodyExpanded ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
                {bodyExpanded ? 'Hide' : 'Show targets'}
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => setDeleteTarget(m)} data-testid={`delete-mapping-${m.id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
        {m.status === 'failed' && (
          <div className="px-5 py-3 bg-red-50/50"><p className="text-xs text-red-600">{m.error || 'Analysis failed. Try running again.'}</p></div>
        )}
        {isLatest && m.status === 'completed' && (m.target_companies || []).length > 0 && (() => {
          const funnel = computeFunnel({
            targetCompanies: m.target_companies,
            summary: outreachSummary[eKey],
            outreachEmails,
            ruleExclusions: typeof funnelExclusions[eKey] === 'object' ? funnelExclusions[eKey] : {},
            contactsReady: serverCheckedKeys.has(eKey) || biotechIndexState === 'ready',
            hasContacts: (tc) => findMatchingContacts(tc.company_name, tc).length > 0 || serverContactNames.has((tc.company_name || '').trim().toLowerCase()),
            isKnownNoContact,
          });
          const outreachCta = {
            label: `Open outreach for these ${funnel.remaining.length}`,
            onClick: () => {
              setFunnelDialog(null);
              const c = companies.find(x => x.id === companyId);
              const a = (assets || []).find(x => x.id === assetId);
              openOutreach(companyId, c?.company_name || '', assetId, a?.asset_name || '', m.target_companies, m.id);
            },
          };
          const onReinstate = (rows) => {
            // Keep the list open underneath: reinstated rows drop out of it
            // (see onReinstated) so several can be done in one sitting.
            setReinstateDlg({
              items: rows.map(r => ({ company_name: r.name, rationale: r.tc?.rationale || r.tc?.match_reason || '', why: r.note || '', defaultTier: /regional|distributor|commercial/i.test(`${r.tc?.target_role || ''} ${r.tc?.fit_category || ''}`) ? 'regional_partner' : 'targeted' })),
              ctx: { biotech_company_id: companyId, asset_id: assetId, mapping_id: m.id },
            });
          };
          return <FunnelStrip eKey={eKey} funnel={funnel} contactsState={biotechIndexState} onOpenList={setFunnelDialog} outreachCta={outreachCta} onReinstate={onReinstate} onRetryContacts={() => { setBiotechIndexState('loading'); fetchData(); }} />;
        })()}
        {bodyExpanded && m.status === 'completed' && m.target_companies?.length > 0 && (
          <>
            {/* Aug 2026 · Deck reinforcement summary banner — shows when
                any target in this mapping is backed by an uploaded corporate
                deck. Informational only; the per-target chip does the
                actual signaling on each row. */}
            {(() => {
              const reinforced = (m.target_companies || []).filter(tc => tc.deck_reinforced);
              const hardBlocked = (m.target_companies || []).filter(tc => tc.deck_hard_blocked);
              if (reinforced.length === 0 && hardBlocked.length === 0) return null;
              const pharmas = [...new Set(reinforced.map(tc => tc.deck_reinforcement?.pharma).filter(Boolean))];
              return (
                <div className="px-5 py-2.5 bg-blue-50/60 border-b border-blue-100" data-testid={`deck-reinforcement-banner-${m.id}`}>
                  <div className="flex items-start gap-2 text-[11px] text-blue-900">
                    <span className="text-base leading-none">📄</span>
                    <div className="flex-1">
                      {reinforced.length > 0 && (
                        <div>
                          <span className="font-semibold">{reinforced.length}</span>
                          {' target'}{reinforced.length === 1 ? '' : 's'}
                          {' reinforced by uploaded corporate deck'}{pharmas.length === 1 ? '' : 's'}
                          {pharmas.length > 0 && (
                            <>
                              {': '}
                              <span className="font-medium">{pharmas.slice(0, 5).join(', ')}</span>
                              {pharmas.length > 5 && <span className="text-blue-700"> +{pharmas.length - 5} more</span>}
                            </>
                          )}
                          <span className="text-blue-700">. Rationales include verbatim citations from the deck.</span>
                        </div>
                      )}
                      {hardBlocked.length > 0 && (
                        <div className="mt-1 text-rose-800">
                          <span className="font-semibold">⚠ {hardBlocked.length}</span>
                          {' target'}{hardBlocked.length === 1 ? '' : 's'}
                          {' hit a deck exclusion (excluded modality or indication) and were demoted.'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="flex items-center justify-end px-5 py-2 bg-slate-50 border-b border-slate-100">
              <div className="flex items-center gap-1.5 shrink-0">
                {(() => {
                  // "Copy Missing" copies only TRULY missing companies (no contacts AND not on
                  // the user's annual-review register). Known gaps are excluded because they
                  // already have a tracked register — re-copying them clutters the outreach.
                  // Same set as the funnel's "need a contact": ELIGIBLE targets only —
                  // refused / needs-your-call companies do not need a contact sourced.
                  const trulyMissing = (m.target_companies || []).filter(tc =>
                    !isTargetRefused(tc) && !softRefusalReason(tc) && findMatchingContacts(tc.company_name, tc).length === 0 && !serverContactNames.has((tc.company_name || '').trim().toLowerCase())
                  );
                  return (
                    <>
                      {trulyMissing.length > 0 && biotechIndexState !== 'loading' && (
                        <Button variant="outline" size="sm" className="h-7 text-[10px] text-amber-600 border-amber-200 hover:bg-amber-50"
                          data-testid={`copy-missing-leads-${m.id}`}
                          onClick={() => {
                            const sorted = [...trulyMissing].sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));
                            const text = sorted.map((tc, i) => `${i+1}. ${tc.company_name}, ${tc.country}`).join('\n');
                            navigator.clipboard.writeText(text);
                            toast.success(`Copied ${trulyMissing.length} pharmas without contacts — paste into LinkedIn / Apollo to find decision-makers`);
                          }}
                          title="Copies pharmas with no CRM contacts. Paste into LinkedIn / Apollo to find the right BD person."
                        >
                          Copy eligible pharmas without contacts ({trulyMissing.length})
                        </Button>
                      )}
                    </>
                  );
                })()}
                {(() => {
                  const exclCount = (m.asset_id && exclusionCounts[m.asset_id]) || 0;
                  // When there are exclusions, replace the "Add" button with a
                  // count-aware split button: clicking the chip copies the list
                  // (parallels "Copy pharmas without contacts"), and a small ✎
                  // sibling button opens the manage dialog for add/edit/remove.
                  if (exclCount > 0) {
                    return (
                      <div className="inline-flex items-stretch h-7" data-testid={`exclusion-actions-${m.id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-[10px] text-rose-700 border-rose-200 hover:bg-rose-50 rounded-r-none pr-2"
                          data-testid={`copy-exclusions-${m.id}`}
                          onClick={async () => {
                            try {
                              const { data } = await axios.get(`${API}/ai/asset-exclusions/${m.asset_id}`, { withCredentials: true });
                              const names = (data?.names || []).map(n => n.name).filter(Boolean);
                              if (!names.length) {
                                toast.info('No exclusions to copy');
                                return;
                              }
                              await navigator.clipboard.writeText(names.join('\n'));
                              toast.success(`Copied ${names.length} excluded name${names.length === 1 ? '' : 's'} to clipboard`);
                            } catch {
                              toast.error('Failed to copy exclusions');
                            }
                          }}
                          title={`Copy the ${exclCount} excluded pharma name${exclCount === 1 ? '' : 's'} for this asset to clipboard.`}
                        >
                          <Ban className="w-3 h-3 mr-1" />
                          Exclusions ({exclCount})
                          <ClipboardList className="w-3 h-3 ml-1.5 opacity-70" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[10px] text-rose-700 border-rose-200 border-l-0 hover:bg-rose-50 rounded-l-none"
                          data-testid={`manage-exclusions-${m.id}`}
                          onClick={async () => {
                            setBulkExcludeDialog({ open: true, mapping: m });
                            setBulkExcludeNames('');
                            setBulkExcludeComment('');
                            setBulkExcludeResult(null);
                            setBulkExcludePersistent(false);
                            setBulkExcludeAlreadyInTouch(false);
                            setPersistentExclusions([]);
                            if (m.asset_id) {
                              try {
                                const { data } = await axios.get(`${API}/ai/asset-exclusions/${m.asset_id}`, { withCredentials: true });
                                setPersistentExclusions(data.names || []);
                              } catch { /* fine */ }
                            }
                          }}
                          title="Add, edit, or remove exclusions for this asset"
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      </div>
                    );
                  }
                  // No exclusions yet — original "Add Exclusions" CTA
                  return (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-[10px] text-rose-600 border-rose-200 hover:bg-rose-50"
                      data-testid={`bulk-exclude-${m.id}`}
                      onClick={async () => {
                        setBulkExcludeDialog({ open: true, mapping: m });
                        setBulkExcludeNames('');
                        setBulkExcludeComment('');
                        setBulkExcludeResult(null);
                        setBulkExcludePersistent(false);
                        setBulkExcludeAlreadyInTouch(false);
                        setPersistentExclusions([]);
                        if (m.asset_id) {
                          try {
                            const { data } = await axios.get(`${API}/ai/asset-exclusions/${m.asset_id}`, { withCredentials: true });
                            setPersistentExclusions(data.names || []);
                          } catch { /* asset not found / new asset — empty list is fine */ }
                        }
                      }}
                      title="Paste a pharma's exclusion list — names are alias-resolved (Janssen → J&J, Vifor → CSL) and bulk-rejected in one click"
                    >
                      <Ban className="w-3 h-3 mr-1" />
                      Add Exclusions
                    </Button>
                  );
                })()}
                <Button variant="outline" size="sm" className="h-7 text-[10px] text-violet-600 border-violet-200 hover:bg-violet-50"
                  data-testid={`copy-all-targets-${m.id}`}
                  onClick={() => {
                    const tierOrder = { 'Tier 1': 0, 'Tier 2': 1, 'Tier 3': 2, 'Strategic Signal': 2.5, 'Tier 4': 3, 'Tier 5': 4 };
                    const sorted = [...m.target_companies].sort((a, b) => {
                      const aTier = tierOrder[a.priority_tier] ?? 5;
                      const bTier = tierOrder[b.priority_tier] ?? 5;
                      if (aTier !== bTier) return aTier - bTier;
                      return (b.fit_score || 0) - (a.fit_score || 0);
                    });
                    const text = sorted.map(tc => tc.company_name).filter(Boolean).join('\n');
                    navigator.clipboard.writeText(text);
                    toast.success(`Copied ${m.target_companies.length} company names`);
                  }}
                >
                  <ClipboardList className="w-3 h-3 mr-1" /> Copy All Names
                </Button>
                {/* Aug 2026 · Copy Generated / Copy Refused — split the target list
                    by whether the QC gate allowed email generation. Same tier /
                    fit_score sort. Refused list includes the block reason so the
                    BD lead knows why each was held back. */}
                {(() => {
                  const isRefused = isTargetRefused;
                  const tierOrder = { 'Tier 1': 0, 'Tier 2': 1, 'Tier 3': 2, 'Strategic Signal': 2.5, 'Tier 4': 3, 'Tier 5': 4 };
                  const sorted = [...(m.target_companies || [])].sort((a, b) => {
                    const aTier = tierOrder[a.priority_tier] ?? 5;
                    const bTier = tierOrder[b.priority_tier] ?? 5;
                    if (aTier !== bTier) return aTier - bTier;
                    return (b.fit_score || 0) - (a.fit_score || 0);
                  });
                  // Same set as the funnel's Eligible (hard-refused AND needs-your-call excluded).
                  const generated = sorted.filter(tc => !isRefused(tc) && !softRefusalReason(tc));
                  const refused = sorted.filter(isRefused);
                  const refusalReason = (tc) => {
                    if (tc?.hard_block_reason) return tc.hard_block_reason;
                    if (tc?.exclude_from_outreach === true && tc?.rationale_quality_reason) return tc.rationale_quality_reason;
                    if ((tc?.confidence || '').toLowerCase() === 'low') return 'Low-confidence rationale';
                    if ((tc?.target_role || '').toLowerCase() === 'exploratory') return 'Exploratory target role';
                    if ((tc?.target_role || '').toLowerCase() === 'tech_service_partner') return 'Tech / service partner';
                    if ((tc?.rationale_quality || '') === 'weak_language') return 'Weak-fit hedge language in rationale';
                    if (tc?.exclude_from_outreach === true) return 'Excluded from outreach';
                    return 'Held for manual review';
                  };
                  return (
                    <>
                      <Button variant="outline" size="sm" className="h-7 text-[10px] text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100"
                        data-testid={`copy-generated-${m.id}`}
                        title={`Only the ${generated.length} pharmas that passed the QC gate and got emails generated`}
                        disabled={generated.length === 0}
                        onClick={() => {
                          const text = generated.map(tc => tc.company_name).filter(Boolean).join('\n');
                          navigator.clipboard.writeText(text);
                          toast.success(`Copied ${generated.length} generated pharma names`);
                        }}
                      >
                        <ClipboardList className="w-3 h-3 mr-1" /> Copy Eligible ({generated.length})
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 text-[10px] text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100"
                        data-testid={`copy-refused-${m.id}`}
                        title={`The ${refused.length} pharmas the QC gate refused. Copied as "Pharma — reason" per line.`}
                        disabled={refused.length === 0}
                        onClick={() => {
                          const text = refused.map(tc => `${tc.company_name} — ${refusalReason(tc)}`).filter(Boolean).join('\n');
                          navigator.clipboard.writeText(text);
                          toast.success(`Copied ${refused.length} refused pharma names + reasons`);
                        }}
                      >
                        <ClipboardList className="w-3 h-3 mr-1" /> Copy Refused ({refused.length})
                      </Button>
                    </>
                  );
                })()}
                <Button variant="outline" size="sm"
                  className={`h-7 text-[10px] ${(dealContext[eKey] || '').trim() ? 'text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100' : 'text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  data-testid={`deal-context-btn-${m.id}`}
                  onClick={() => setDealContextOpen(prev => ({ ...prev, [eKey]: !prev[eKey] }))}
                  title="Client's special request (e.g. seeking a co-development partner for a preclinical asset). Saved per asset and woven into every AI-generated email — single and bulk."
                >
                  <MessageSquarePlus className="w-3 h-3 mr-1" /> Deal Context{(dealContext[eKey] || '').trim() ? ' ✓' : ''}
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-[10px] text-[#00709B] border-[#00709B]/30 hover:bg-sky-50"
                  data-testid={`share-mapping-${m.id}`}
                  onClick={() => {
                    // Pre-fill with biotech contacts
                    const biotech = companies.find(c => c.id === m.biotech_company_id);
                    const contactEmails = [...new Set((biotech?.contacts || []).filter(c => c.email).map(c => c.email.toLowerCase().trim()))].join(', ');
                    setShareEmails(contactEmails);
                    setShareMessage('');
                    setShareDialog({ open: true, mapping: m });
                  }}
                >
                  <Share2 className="w-3 h-3 mr-1" /> Share for Review
                </Button>
                {m.share_token && (() => {
                  const approved = (m.target_companies || []).filter(t => t.approval_status === 'approved').length;
                  const rejected = (m.target_companies || []).filter(t => t.approval_status === 'rejected').length;
                  const pending = (m.target_companies || []).length - approved - rejected;
                  const biotechForLink = companies.find(c => c.id === m.biotech_company_id);
                  const isDncRevoked = !!(biotechForLink?.do_not_contact?.flagged);
                  return (
                    <div className="flex items-center gap-1.5">
                      {isDncRevoked ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 h-7 rounded text-[10px] font-medium bg-rose-50 text-rose-700 border border-rose-200 cursor-not-allowed"
                          data-testid={`view-shared-revoked-${m.id}`}
                          title={`Share link revoked — ${biotechForLink?.company_name || 'this biotech'} is marked Do Not Contact${biotechForLink?.do_not_contact?.reason ? ': ' + biotechForLink.do_not_contact.reason : ''}. Remove the DNC flag on the biotech page to auto-restore.`}
                        >
                          <Lock className="w-3 h-3" /> Link revoked (DNC)
                        </span>
                      ) : (
                        <a
                          href={`/shared/mapping/${m.share_token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 h-7 rounded text-[10px] font-medium bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 transition-colors"
                          data-testid={`view-shared-link-${m.id}`}
                          onClick={e => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3 h-3" /> View Shared
                        </a>
                      )}
                      {(approved > 0 || rejected > 0) && (
                        <span className="text-[9px] text-slate-500">
                          {approved > 0 && <span className="text-emerald-600 font-semibold">{approved} approved</span>}
                          {approved > 0 && rejected > 0 && ' · '}
                          {rejected > 0 && <span className="text-rose-600 font-semibold">{rejected} excluded</span>}
                          {' · '}{pending} pending
                        </span>
                      )}
                      {!isDncRevoked && approved === 0 && rejected === 0 && (
                        <span className="text-[9px] text-amber-600">No response yet</span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            {dealContextOpen[eKey] && (
              <div className="px-5 py-3 bg-emerald-50/40 border-b border-emerald-100" data-testid={`deal-context-panel-${m.id}`}>
                <p className="text-[11px] font-semibold text-emerald-800 mb-1">Client's special request / deal context</p>
                <p className="text-[10px] text-slate-500 mb-2">E.g. "Preclinical asset — client specifically wants a co-development partner, not a straight out-license." Saved per asset and woven into every AI-generated email (single + bulk outreach).</p>
                <Textarea rows={3} className="text-xs bg-white"
                  value={dealContext[eKey] || ''}
                  onChange={e => setDealContext(prev => ({ ...prev, [eKey]: e.target.value }))}
                  placeholder="What is the client specifically looking for in this deal?"
                  data-testid={`deal-context-input-${m.id}`}
                />
                <div className="flex justify-end gap-2 mt-2">
                  <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={() => setDealContextOpen(prev => ({ ...prev, [eKey]: false }))} data-testid={`deal-context-close-${m.id}`}>Close</Button>
                  <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={dealContextSaving[eKey]}
                    onClick={() => saveDealContext(companyId, assetId)}
                    data-testid={`deal-context-save-${m.id}`}
                  >
                    {dealContextSaving[eKey] ? 'Saving…' : 'Save context'}
                  </Button>
                </div>
              </div>
            )}
            <div className="divide-y divide-slate-50 max-h-[400px] overflow-y-auto">
              {[...m.target_companies].sort((a, b) => {
                // Sort by priority tier first (Tier 1 > Tier 2 > Tier 3 > Strategic Signal > Tier 4), then by fit_score descending
                const tierOrder = { 'Tier 1': 0, 'Tier 2': 1, 'Tier 3': 2, 'Strategic Signal': 2.5, 'Tier 4': 3, 'Tier 5': 4 };
                const aTier = tierOrder[a.priority_tier] ?? 3;
                const bTier = tierOrder[b.priority_tier] ?? 3;
                if (aTier !== bTier) return aTier - bTier;
                return (b.fit_score || 0) - (a.fit_score || 0);
              }).filter(tc => !showNewOnly || isNewTarget(tc)).map((tc, idx) => {
                const matchCount = findMatchingContacts(tc.company_name, tc).length;
                // Known-gap is authoritative: even if a base-word matcher accidentally
                // links the name to an unrelated profile, the user has explicitly said
                // "no contacts available" — respect that.
                const knownGap = isKnownNoContact(tc.company_name);
                const hasMatch = !knownGap && matchCount > 0;
                // If there are no matching contacts but the AI-discovered target
                // already exists in the biotech CRM (with zero contacts), surface
                // it as "In Biotech CRM" so the user can click through and add
                // contacts — distinct from "Not in CRM at all".
                const biotechMatch = (!hasMatch && !knownGap) ? findBiotechMatch(tc.company_name) : null;
                const newSinceLast = isNewTarget(tc);
                return (
                  <div key={idx} className={`flex items-start gap-2 px-5 py-3 hover:bg-slate-50/50 group ${hasMatch ? 'bg-emerald-50/30' : knownGap ? 'bg-slate-100/40 border-l-2 border-slate-300' : 'bg-rose-50/20 border-l-2 border-rose-300'} ${newSinceLast ? 'border-l-4 border-l-emerald-500' : ''}`} data-testid={`target-row-${m.id}-${idx}`}>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-900">{tc.company_name}</p>
                        {newSinceLast && (
                          <Badge className="bg-emerald-500 text-white text-[9px] px-1.5 py-0 shrink-0" title="Net-new target — did not appear in the previous run" data-testid={`new-since-last-row-${idx}`}>
                            🆕 NEW
                          </Badge>
                        )}
                        {tc.approval_status && tc.approval_status !== 'pending' && (
                          <Badge
                            className={`text-[9px] ${tc.approval_status === 'approved' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}
                            title={tc.approval_status === 'rejected' ? (tc.approval_comment || 'Excluded — no reason recorded') : (tc.approval_comment || 'Approved')}
                            data-testid={`approval-badge-${idx}`}
                          >
                            {tc.approval_status === 'approved' ? 'Approved' : 'Excluded'}
                          </Badge>
                        )}
                        {hasMatch ? (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-100 text-emerald-700 shrink-0">
                            <User className="w-2.5 h-2.5" />{matchCount}
                          </span>
                        ) : isKnownNoContact(tc.company_name) ? (
                          <Badge
                            className="bg-slate-200 text-slate-600 border border-slate-300 text-[10px] font-semibold px-1.5 py-0 shrink-0 inline-flex items-center gap-1"
                            title="Already on your 'No-Contact Pharma' annual-review register. Tracked for future review — not actionable right now."
                            data-testid={`known-gap-flag-${idx}`}
                          >
                            📋 Known data gap
                          </Badge>
                        ) : biotechMatch ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/biotech/${biotechMatch.id}`)}
                            className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border border-indigo-300 rounded-md text-[10px] font-semibold px-1.5 py-0 shrink-0 inline-flex items-center gap-1 cursor-pointer transition-colors"
                            title={`Already in your Biotech CRM as "${biotechMatch.company_name}" — click to open and add contacts so you can email them.`}
                            data-testid={`in-biotech-crm-${idx}`}
                          >
                            🧬 In Biotech CRM · Add contacts →
                          </button>
                        ) : (tc.in_crm && tc.source !== 'web_discovered') ? (
                          // v33 (Feb 2026) — When the backend flagged
                          // this row as in_crm (company exists in the
                          // imported CRM table) but no pharma_contacts
                          // row is linked yet, we previously showed a
                          // MISLEADING red "Not in CRM" chip alongside
                          // the green "✓ In CRM" chip on the same row.
                          // This unified indigo chip communicates the
                          // truth: the company IS in your CRM — you
                          // just need to add contacts before you can
                          // email them.
                          <Badge
                            className="bg-indigo-100 text-indigo-700 border border-indigo-300 text-[10px] font-semibold px-1.5 py-0 shrink-0 inline-flex items-center gap-1"
                            title="Company exists in your CRM (out-licensers / imports) but no pharma_contacts have been added yet. Add at least one contact to enable outreach."
                            data-testid={`in-crm-no-contacts-${idx}`}
                          >
                            🧩 In CRM · Add contacts
                          </Badge>
                        ) : (
                          <Badge
                            className="bg-rose-100 text-rose-700 border border-rose-300 text-[10px] font-semibold px-1.5 py-0 shrink-0 inline-flex items-center gap-1"
                            title="Not found in any CRM table (pharma, biotech, or imported companies). Add the company + at least one contact to enable outreach, or add it to the No-Contact register if it's a known data gap."
                            data-testid={`no-contacts-flag-${idx}`}
                          >
                            ⚠️ Not in CRM
                          </Badge>
                        )}
                        <span className="text-[10px] text-slate-400">{tc.country}</span>
                        <Badge className={`text-[10px] ${SIZE_COLORS[tc.company_size] || 'bg-slate-100 text-slate-600'}`}>{tc.company_size || '—'}</Badge>
                        {tc.fit_category && (
                          <Badge className={`text-[10px] ${
                            tc.fit_category === 'Strategic' ? 'bg-emerald-100 text-emerald-700' :
                            tc.fit_category === 'Adjacent' ? 'bg-blue-100 text-blue-700' :
                            tc.fit_category === 'Opportunistic' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>{tc.fit_category}</Badge>
                        )}
                        {tc.priority_tier && (
                          <Badge className={`text-[10px] ${
                            tc.priority_tier === 'Tier 1' ? 'bg-emerald-600 text-white' :
                            tc.priority_tier === 'Tier 2' ? 'bg-blue-500 text-white' :
                            tc.priority_tier === 'Tier 3' ? 'bg-amber-500 text-white' :
                            tc.priority_tier === 'Tier 4' ? 'bg-purple-500 text-white' :
                            tc.priority_tier === 'Strategic Signal' ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                            'bg-slate-400 text-white'
                          }`}>{tc.priority_tier}</Badge>
                        )}
                        {tc.source === 'commercial_partner' && (
                          <Badge
                            className="text-[10px] bg-violet-100 text-violet-800 border border-violet-300"
                            title={`Regional Commercial Partner${tc.territory ? ` · ${tc.territory}` : ''}${(tc.channels || []).length ? ` · ${tc.channels.join(', ')}` : ''}`}
                            data-testid={`commercial-partner-badge-${tc.company_name}`}
                          >
                            🚚 Commercial Partner{tc.territory ? ` · ${tc.territory}` : ''}
                          </Badge>
                        )}
                        {/* v27 · Strength chip — visible on EVERY target so users can filter Strong-only before sending */}
                        {(() => {
                          const q = tc.rationale_quality || (tc.rationale ? 'strong' : 'needs-enrichment');
                          const src = tc.rationale_source || '';
                          const isQuarantine = q === 'needs-enrichment' || q === 'gate-failed' || src === 'needs_enrichment' || src === 'gate_failed';
                          const isStrong = q === 'strong' && !isQuarantine;
                          const chipCls = isQuarantine
                            ? 'bg-rose-100 text-rose-800 border border-rose-300'
                            : isStrong
                            ? (src === 'matchmaker'
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                : 'bg-emerald-50 text-emerald-700 border border-emerald-200')
                            : 'bg-amber-100 text-amber-800 border border-amber-300';
                          const label = isQuarantine ? '🔴 Do not send — needs enrichment'
                            : isStrong ? (src === 'matchmaker' ? '🟢 Strong · matchmaker' : '🟢 Strong')
                            : `🟡 ${q.replace('-', ' ')}`;
                          return (
                            <Badge
                              className={`text-[10px] ${chipCls}`}
                              title={tc.rationale_quality_reason || (isStrong ? 'Rationale is evidence-anchored and safe to send' : 'Weak rationale — quarantined from outreach')}
                              data-testid={`strength-chip-${tc.company_name}`}
                            >
                              {label}
                            </Badge>
                          );
                        })()}
                        {tc.signal_audit?.verdict && (
                          <Badge
                            className={`text-[10px] ${tc.signal_audit.verdict === 'conflict' ? 'bg-rose-100 text-rose-800 border border-rose-300' : tc.signal_audit.verdict === 'fits' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}
                            title={tc.signal_audit.reason || ''}
                            data-testid={`signal-audit-chip-${tc.company_name}`}
                          >
                            {tc.signal_audit.verdict === 'conflict' ? '⚠ Logged signal conflicts' : tc.signal_audit.verdict === 'fits' ? '✓ Logged signal supports' : '· Logged signal unclear'}
                          </Badge>
                        )}
                        {tc.prior_decline && (
                          <Badge
                            className={`text-[10px] border ${tc.prior_decline.similarity === 'same_asset' ? 'bg-rose-100 text-rose-800 border-rose-300' : 'bg-orange-50 text-orange-800 border-orange-300'}`}
                            title={`${tc.prior_decline.status || 'Declined'} ${tc.prior_decline.asset_name ? `“${tc.prior_decline.asset_name}” ` : ''}(${tc.prior_decline.similarity_human})${tc.prior_decline.declined_at ? ` on ${String(tc.prior_decline.declined_at).slice(0, 10)}` : ''}${tc.prior_decline.reason ? `\nReason: ${tc.prior_decline.reason}` : '\nNo reason recorded'}${tc.priority_tier_before_decline ? `\nTier downgraded from ${tc.priority_tier_before_decline}` : ''}${tc.prior_decline.count > 1 ? `\n${tc.prior_decline.count} declines on record` : ''}`}
                            data-testid={`prior-decline-${tc.company_name}`}
                          >
                            ⛔ Declined before{tc.prior_decline.similarity === 'same_asset' ? ' (this asset)' : ''}{tc.prior_decline.reason ? `: ${tc.prior_decline.reason.slice(0, 60)}${tc.prior_decline.reason.length > 60 ? '…' : ''}` : ''}
                          </Badge>
                        )}

                        {tc.crm_evidence_added && (
                          <Badge
                            className="text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-200"
                            title="Added by MOAT layer — this company surfaced from enriched CRM evidence (pipeline mechanism or therapeutic interest match)"
                            data-testid={`moat-add-badge-${tc.company_name}`}
                          >
                            ◆ CRM-evidence
                          </Badge>
                        )}
                        {tc.archetype_warning && (
                          <Badge
                            className="text-[10px] bg-amber-50 text-amber-800 border border-amber-300"
                            title={tc.archetype_warning}
                            data-testid={`archetype-warning-${tc.company_name}`}
                          >
                            ⓘ archetype downweight
                          </Badge>
                        )}
                        {tc.fit_classification && (
                          <Badge
                            className="text-[10px] bg-violet-50 text-violet-700 border border-violet-200"
                            title="Why this match works (multi-indication / mechanism / commercial / regional / etc.)"
                            data-testid={`fit-classification-${tc.company_name}`}
                          >
                            {tc.fit_classification}
                          </Badge>
                        )}
                        {tc.fit_strength && (
                          <Badge
                            className={`text-[10px] border ${
                              /primary/i.test(tc.fit_strength) ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              /secondary/i.test(tc.fit_strength) ? 'bg-sky-50 text-sky-700 border-sky-200' :
                              /regional|commercial/i.test(tc.fit_strength) ? 'bg-teal-50 text-teal-700 border-teal-200' :
                              'bg-slate-50 text-slate-500 border-slate-200'
                            }`}
                            title="Fit strength for this company (Primary / Secondary / Regional-commercial / Weak)"
                            data-testid={`fit-strength-${tc.company_name}`}
                          >
                            {tc.fit_strength}
                          </Badge>
                        )}
                        {tc.lead_indication && (
                          <Badge
                            className="text-[10px] bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200"
                            title="The indication this rationale is positioned around for this company"
                            data-testid={`lead-indication-${tc.company_name}`}
                          >
                            ↳ {tc.lead_indication}
                          </Badge>
                        )}
                        {tc.has_emerging_signal && (
                          <Badge className="text-[10px] bg-amber-50 text-amber-800 border border-amber-300" title={tc.emerging_signals?.map(s => `${s.area} via ${s.source_contact_name}`).join('; ')} data-testid={`emerging-signal-badge-${tc.company_name}`}>
                            🔭 Signal
                          </Badge>
                        )}
                        {tc.source === 'web_discovered' && (
                          <Badge className="text-[10px] bg-sky-50 text-sky-800 border border-sky-300" title="Found via web discovery — not yet in your CRM" data-testid={`web-discovered-badge-${tc.company_name}`}>
                            🌐 Web Discovered
                          </Badge>
                        )}
                        {tc.in_crm && tc.source !== 'web_discovered' && (
                          <Badge className="text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-300" title="In your CRM">
                            ✓ In CRM
                          </Badge>
                        )}
                        {/* Aug 2026 · Deck-reinforced chip. When the target
                            pharma has an uploaded corporate deck whose
                            granular signals (specific indications / priority
                            targets / scientific prefs) were hit by this
                            asset, the backend flags deck_reinforced=true
                            and attaches a hit-report. The chip surfaces
                            that reinforcement so BD leads can spot
                            deck-backed matches at a glance. */}
                        {tc.deck_reinforced && (
                          <Badge
                            className="text-[10px] bg-blue-100 text-blue-800 border border-blue-300"
                            title={(() => {
                              const r = tc.deck_reinforcement || {};
                              const hits = r.hits || {};
                              const parts = [];
                              if (hits.therapeutic_areas_specific?.length) parts.push(`Indications: ${hits.therapeutic_areas_specific.slice(0,2).join(', ')}`);
                              if (hits.priority_targets_moas?.length) parts.push(`Targets: ${hits.priority_targets_moas.slice(0,2).join(', ')}`);
                              if (hits.scientific_preferences?.length) parts.push(`Sci: ${hits.scientific_preferences.slice(0,2).join(', ')}`);
                              return `Reinforced by ${r.pharma}'s ${r.deck_year || ''} deck — ${parts.join(' · ')}`;
                            })()}
                            data-testid={`deck-reinforced-badge-${tc.company_name}`}
                          >
                            📄 Reinforced by {tc.deck_reinforcement?.deck_year || ''} deck
                          </Badge>
                        )}
                        {tc.deck_hard_blocked && (
                          <Badge
                            className="text-[10px] bg-rose-100 text-rose-800 border border-rose-300"
                            title={(tc.deck_hard_block_reasons || []).join(' · ')}
                            data-testid={`deck-hard-block-badge-${tc.company_name}`}
                          >
                            🚫 Deck exclusion
                          </Badge>
                        )}
                        {tc.fit_score != null && (
                          <span
                            className="text-[10px] text-slate-500 font-mono font-semibold"
                            data-testid={`fit-score-${tc.company_name}`}
                            title="AI-computed strategic fit for this asset (0–100)"
                          >
                            {Math.round(tc.fit_score)}/100
                          </span>
                        )}
                        {tc.confidence && (
                          <span className={`text-[9px] font-semibold ${
                            tc.confidence === 'High' ? 'text-emerald-600' :
                            tc.confidence === 'Medium' ? 'text-amber-600' :
                            'text-slate-400'
                          }`}>{tc.confidence}</span>
                        )}
                        {tc.learning_adjustment && tc.learning_adjustment.adjustment !== 0 && (
                          <span
                            data-testid="learning-badge"
                            title={(tc.learning_adjustment.reasons || [])
                              .map(r => `${r.dimension}=${r.value} (${r.delta > 0 ? '+' : ''}${r.delta})`)
                              .join(' · ')
                              + ` — from ${tc.learning_adjustment.signal_count} prior outcome${tc.learning_adjustment.signal_count === 1 ? '' : 's'}`}
                            className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${
                              tc.learning_adjustment.adjustment > 0
                                ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                                : 'text-rose-700 bg-rose-50 border-rose-200'
                            }`}
                          >
                            {tc.learning_adjustment.adjustment > 0 ? '▲' : '▼'} learning {tc.learning_adjustment.adjustment > 0 ? '+' : ''}{tc.learning_adjustment.adjustment}
                          </span>
                        )}
                      </div>
                      {(tc.buyer_archetype || tc.deal_type) && (
                        <div className="flex items-center gap-2 text-[10px] text-slate-400">
                          {tc.buyer_archetype && <span>{tc.buyer_archetype}</span>}
                          {tc.buyer_archetype && tc.deal_type && <span>·</span>}
                          {tc.deal_type && <span className="text-[#00709B]">{tc.deal_type}</span>}
                        </div>
                      )}
                      <p className="text-xs text-slate-500 leading-relaxed">{tc.rationale}</p>
                      {tc.approval_status === 'rejected' && (
                        <div className="text-[11px] bg-rose-50 border-l-2 border-rose-300 px-2 py-1 rounded-r flex items-start gap-1.5" data-testid={`exclusion-reason-${idx}`}>
                          <span className="text-rose-500">🚫</span>
                          <span className="text-rose-700">
                            <span className="font-semibold">Excluded:</span> {tc.approval_comment || 'No reason recorded'}
                          </span>
                        </div>
                      )}
                      {tc.source === 'web_discovered' && (() => {
                        // Detect if this discovered company was already added to CRM via discovery
                        const nameLc = (tc.company_name || '').trim().toLowerCase();
                        const discoveredProfile = companyProfiles.find(cp =>
                          (cp.company_name || '').trim().toLowerCase() === nameLc &&
                          cp.source === 'web_discovery'
                        );
                        return (
                          <div className="mt-1.5 text-[11px] bg-sky-50 border border-sky-200 rounded-md p-2 flex items-start gap-2" data-testid={`web-discovered-block-${tc.company_name}`}>
                            <div className="flex-1 min-w-0">
                              {tc.evidence && <p className="text-sky-900 italic mb-1">“{tc.evidence}”</p>}
                              {tc.source_url && (
                                <a href={tc.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-sky-700 hover:underline break-all">{tc.source_url}</a>
                              )}
                            </div>
                            {discoveredProfile ? (
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Remove ${tc.company_name} from CRM? This deletes the company profile that was just added via discovery.`)) return;
                                  try {
                                    await axios.delete(`${API}/company-profiles/${discoveredProfile.id}`, { withCredentials: true });
                                    toast.success(`Removed ${tc.company_name} from CRM`);
                                    const pr = await axios.get(`${API}/company-profiles`, { withCredentials: true });
                                    setCompanyProfiles(pr.data?.items || pr.data || []);
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Failed to remove');
                                  }
                                }}
                                className="shrink-0 inline-flex items-center gap-1 bg-emerald-50 hover:bg-rose-50 text-emerald-700 hover:text-rose-700 border border-emerald-300 hover:border-rose-300 text-[10px] font-semibold px-2 py-1 rounded-md transition-colors group/undo"
                                data-testid={`undo-add-to-crm-btn-${tc.company_name}`}
                                title="Delete the CRM profile that was just added via discovery"
                              >
                                <span className="group-hover/undo:hidden">✓ Added · Undo</span>
                                <span className="hidden group-hover/undo:inline">✕ Remove from CRM</span>
                              </button>
                            ) : (
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await axios.post(`${API}/company-profiles/from-discovery`, {
                                      company_name: tc.company_name,
                                      country: tc.country,
                                      buyer_archetype: tc.buyer_archetype,
                                      therapeutic_area: m.therapeutic_area || '',
                                      rationale: tc.rationale,
                                      evidence: tc.evidence,
                                      source_url: tc.source_url,
                                    }, { withCredentials: true });
                                    if (res.data?.already_exists) {
                                      toast(`${tc.company_name} already in CRM`, { icon: 'ℹ️' });
                                    } else {
                                      toast.success(`Added ${tc.company_name} to CRM — enriching in background`);
                                    }
                                    // Reload company profiles cache
                                    const pr = await axios.get(`${API}/company-profiles`, { withCredentials: true });
                                    setCompanyProfiles(pr.data?.items || pr.data || []);
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Failed to add');
                                  }
                                }}
                                className="shrink-0 bg-sky-600 hover:bg-sky-700 text-white text-[10px] font-semibold px-2 py-1 rounded-md transition-colors"
                                data-testid={`add-to-crm-btn-${tc.company_name}`}
                              >
                                + Add to CRM
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      {tc.has_emerging_signal && (tc.emerging_signals || []).map((sig, si) => (
                        <div key={si} className="mt-1.5 text-[11px] bg-amber-50 border border-amber-200 rounded-md p-2" data-testid={`emerging-signal-${tc.company_name}-${si}`}>
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="text-amber-700">🔭</span>
                            <span className="font-semibold text-amber-900">Emerging Signal — {sig.area}</span>
                            {sig.source_contact_name && (
                              <span className="text-amber-700">via {sig.source_contact_name}</span>
                            )}
                          </div>
                          {sig.evidence && <p className="text-amber-800 italic leading-relaxed">“{sig.evidence}”</p>}
                        </div>
                      ))}
                      {(tc.official_therapeutic_areas || tc.active_therapeutic_focus || tc.bd_behavior || tc.modality_preference || tc.areas_they_avoid) && (
                        <details className="mt-1.5">
                          <summary className="text-[10px] font-semibold text-[#00709B] cursor-pointer hover:underline">BD Intelligence</summary>
                          <div className="mt-1.5 space-y-1 text-[10px] text-slate-500 bg-slate-50 rounded-md p-2.5 border border-slate-100">
                            {tc.official_therapeutic_areas && <p><span className="font-semibold text-slate-600">Official TAs:</span> {tc.official_therapeutic_areas}</p>}
                            {tc.active_therapeutic_focus && <p><span className="font-semibold text-slate-600">Active Focus:</span> {tc.active_therapeutic_focus}</p>}
                            {tc.bd_behavior && <p><span className="font-semibold text-slate-600">BD Behavior:</span> {tc.bd_behavior}</p>}
                            {tc.modality_preference && <p><span className="font-semibold text-slate-600">Modality:</span> {tc.modality_preference}</p>}
                            {tc.areas_they_avoid && <p><span className="font-semibold text-rose-500">Avoids:</span> {tc.areas_they_avoid}</p>}
                          </div>
                        </details>
                      )}
                    </div>
                    <button className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-slate-100 shrink-0 mt-0.5"
                      onClick={() => {
                        const tier = tc.priority_tier ? `[${tc.priority_tier}]` : '';
                        const text = `${tc.company_name}${tier ? ' ' + tier : ''}\n${tc.rationale}`;
                        navigator.clipboard.writeText(text);
                        toast.success(`Copied: ${tc.company_name} with rationale`);
                      }}
                      title="Copy company, tier & rationale"
                    >
                      <Copy className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    {hasMatch && (
                      <button
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded hover:bg-sky-100 shrink-0 mt-0.5"
                        onClick={async () => {
                          const [cName, aName] = (label || '').split(' — ');
                          await openOutreach(companyId, cName || '', assetId, aName || '', [tc], (m && m.id) || '');
                          // Auto-trigger generation for this single pharma
                          setTimeout(() => generateCompanyEmail(tc.company_name), 100);
                        }}
                        title={`Generate email just for ${tc.company_name}`}
                        data-testid={`generate-single-${tc.company_name}`}
                      >
                        <Brain className="w-3.5 h-3.5 text-sky-500" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Outreach Emails */}
            {(emails[`${eKey}_pharma`] || emails[`${eKey}_investor`]) && (
              <div className="border-t border-slate-200 px-5 py-4 bg-slate-50/30">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-4 h-4 text-sky-500" />
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Outreach Emails</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {emails[`${eKey}_pharma`] && (
                    <div className="border border-sky-200 rounded-lg bg-white">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-sky-100 bg-sky-50/50 rounded-t-lg">
                        <span className="text-[10px] font-semibold text-sky-700 uppercase tracking-wider">Pharma BD Email</span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-sky-600 hover:bg-sky-100 px-2" onClick={() => copyEmail(`${eKey}_pharma`)}>
                            <Copy className="w-3 h-3 mr-1" /> Copy
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-slate-500 hover:bg-slate-100 px-2" onClick={() => handleGenerateEmail(companyId, assetId, 'pharma')} disabled={generatingEmail[`${eKey}_pharma`]}>
                            Regenerate
                          </Button>
                        </div>
                      </div>
                      <pre className="px-3 py-3 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed max-h-[300px] overflow-y-auto" style={{ fontFamily: 'Public Sans, sans-serif' }}>{emails[`${eKey}_pharma`]}</pre>
                    </div>
                  )}
                  {emails[`${eKey}_investor`] && (
                    <div className="border border-violet-200 rounded-lg bg-white">
                      <div className="flex items-center justify-between px-3 py-2 border-b border-violet-100 bg-violet-50/50 rounded-t-lg">
                        <span className="text-[10px] font-semibold text-violet-700 uppercase tracking-wider">Investor Email</span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-violet-600 hover:bg-violet-100 px-2" onClick={() => copyEmail(`${eKey}_investor`)}>
                            <Copy className="w-3 h-3 mr-1" /> Copy
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-slate-500 hover:bg-slate-100 px-2" onClick={() => handleGenerateEmail(companyId, assetId, 'investor')} disabled={generatingEmail[`${eKey}_investor`]}>
                            Regenerate
                          </Button>
                        </div>
                      </div>
                      <pre className="px-3 py-3 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed max-h-[300px] overflow-y-auto" style={{ fontFamily: 'Public Sans, sans-serif' }}>{emails[`${eKey}_investor`]}</pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Regional Commercial Partners (Path D) are now merged into the
            main target list above with a violet 🚚 Commercial Partner pill,
            so they flow through the same outreach + contacts + bulk-email +
            exclusions plumbing as pharma targets. Kept the block below
            commented out for reference in case we ever want to reinstate a
            dedicated filter view. */}
        {false && bodyExpanded && m.status === 'completed' && (m.commercial_partners || []).length > 0 && (
          <div className="mt-4 border-t border-amber-100 pt-4" data-testid="commercial-partners-section">
            <div className="flex items-center gap-2 mb-3">
              <Truck className="w-4 h-4 text-amber-600" />
              <h4 className="text-sm font-bold text-amber-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Regional Commercial Partners</h4>
              <Badge className="bg-amber-100 text-amber-700 text-[10px] font-semibold" data-testid="commercial-partners-count">{m.commercial_partners.length}</Badge>
              <span className="text-[11px] text-slate-400">Evaluated separately from pharma — territory · stage · TA · portfolio fit</span>
            </div>
            <div className="space-y-2">
              {[...m.commercial_partners].sort((a, b) => (b.fit_score || 0) - (a.fit_score || 0)).map((cp, i) => (
                <div key={i} className="border border-amber-100 rounded-lg p-3 bg-amber-50/40" data-testid={`commercial-partner-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900">{cp.company_name}</span>
                        {cp.territory && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800">📍 {cp.territory}</Badge>}
                        <Badge className={`text-[10px] ${cp.confidence === 'High' ? 'bg-emerald-100 text-emerald-700' : cp.confidence === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{cp.confidence} · {cp.fit_score}%</Badge>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{cp.why_fit}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-[11px] text-slate-500">
                        {cp.matched_therapeutic_area && <span>TA: <span className="text-slate-700 font-medium">{cp.matched_therapeutic_area}</span></span>}
                        {cp.matched_stage_or_product && <span>Stage/Product: <span className="text-slate-700 font-medium">{cp.matched_stage_or_product}</span></span>}
                        {(cp.channels || []).length > 0 && <span>Channels: {cp.channels.join(', ')}</span>}
                      </div>
                      {cp.bd_requirements && <p className="text-[11px] text-slate-500 mt-1"><span className="font-semibold text-slate-600">BD requirements:</span> {cp.bd_requirements}</p>}
                      {cp.limitation && <p className="text-[11px] text-rose-600 mt-1">⚠ {cp.limitation}</p>}
                    </div>
                    <div className="shrink-0 text-right">
                      {cp.contact_email ? (
                        <span className="text-[11px] text-sky-600 flex items-center gap-1"><Mail className="w-3 h-3" />{cp.contact_name ? `${cp.contact_name} · ` : ''}{cp.contact_email}</span>
                      ) : (
                        <span className="text-[11px] text-slate-400">No direct email</span>
                      )}
                      {cp.id && <a href={`/company-profile/${cp.id}`} className="block text-[11px] text-slate-500 hover:text-slate-700 mt-1">View profile →</a>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    );
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="ai-mapping-page">
      {isSingleView && (() => {
        const c = companies.find(c => c.id === routeCompanyId);
        return (
          <div className="mb-4" data-testid="single-view-header">
            <Link to="/ai-mapping" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-sky-700 mb-2" data-testid="back-to-list">
              <ArrowLeft className="w-3 h-3" /> All AI Mappings
            </Link>
            {c && (
              <h2 className="text-lg font-semibold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {c.company_name}
              </h2>
            )}
          </div>
        );
      })()}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isSingleView ? 'AI Mapping' : 'AI Target Mapping'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isSingleView
              ? 'Per-asset target mapping, outreach status, and warm leads for this biotech'
              : 'Run AI-powered target search per molecule to find potential pharma partners or investors'}
          </p>
        </div>
        {!isSingleView && (
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="view-mode-toggle">
          <button onClick={() => setViewMode('table')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400 hover:text-slate-600'}`} data-testid="view-mode-table" title="Table view">
            <TableProperties className="w-4 h-4" />
          </button>
          <button onClick={() => setViewMode('card')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'card' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-400 hover:text-slate-600'}`} data-testid="view-mode-card" title="Card view">
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : companies.length === 0 ? (
        <Card className="border border-slate-200"><CardContent className="py-16 text-center"><Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500 text-sm">No biotech companies yet.</p></CardContent></Card>
      ) : (() => {
        // Compute company-level status
        const getCompanyStatus = (c) => {
          const companyAssets = assetsByCompany[c.id] || [];
          const allKeys = [...companyAssets.map(a => a.id), c.id];
          const hasCompletedMapping = allKeys.some(k => {
            return (mappingsByKey[k] || []).some(m => m?.status === 'completed' && m?.target_companies?.length > 0);
          });
          // Also check outreach summary for sent emails (even without mapping)
          const hasOutreach = allKeys.some(k => {
            const o = outreachSummary[k] || {};
            return (o.pharma_sent || 0) > 0 || (o.investor_sent || 0) > 0;
          });
          if (!hasCompletedMapping && !hasOutreach) return 'not_run';
          const pharmaStatus = (() => {
            for (const k of allKeys) {
              const ms = mappingsByKey[k] || [];
              const o = outreachSummary[k] || {};
              if (ms.some(m => m?.email_sent_pharma === 'complete' || m?.email_sent_pharma === true) || (o.pharma_sent || 0) > 0) return 'complete';
            }
            for (const k of allKeys) {
              const ms = mappingsByKey[k] || [];
              if (ms.some(m => m?.email_sent_pharma === 'in_progress')) return 'in_progress';
            }
            return 'none';
          })();
          const investorStatus = allKeys.some(k => {
            const ms = mappingsByKey[k] || [];
            const o = outreachSummary[k] || {};
            return ms.some(m => m?.email_sent_investor) || (o.investor_sent || 0) > 0;
          });
          if (pharmaStatus === 'complete' && investorStatus) return 'completed';
          if (pharmaStatus === 'complete' || investorStatus) return 'completed';
          if (pharmaStatus === 'in_progress') return 'in_progress';
          if (hasCompletedMapping) return 'mapped';
          return 'not_run';
        };

        const STATUS_BORDER = {
          not_run: 'border-l-4 border-l-slate-300',
          mapped: 'border-l-4 border-l-orange-400',
          in_progress: 'border-l-4 border-l-amber-500',
          completed: 'border-l-4 border-l-green-500',
        };
        const STATUS_LABEL = {
          not_run: 'Not Run',
          mapped: 'Mapped',
          in_progress: 'In Progress',
          completed: 'Completed',
        };
        const STATUS_DOT = {
          not_run: 'bg-slate-300',
          mapped: 'bg-orange-400',
          in_progress: 'bg-amber-500',
          completed: 'bg-green-500',
        };

        const companiesWithStatus = companies.map(c => ({ ...c, _status: getCompanyStatus(c) }));
        const counts = { all: companiesWithStatus.length, not_run: 0, mapped: 0, in_progress: 0, completed: 0 };
        companiesWithStatus.forEach(c => counts[c._status]++);

        // In single-company route mode, filter to just that company
        const filteredCompanies = isSingleView
          ? companiesWithStatus.filter(c => c.id === routeCompanyId)
          : (statusFilter === 'all' ? companiesWithStatus : companiesWithStatus.filter(c => c._status === statusFilter));

        return (
        <>
          {!isSingleView && (
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="status-filter-bar">
            {[
              { key: 'all', label: 'All', count: counts.all, color: 'bg-slate-100 text-slate-700 hover:bg-slate-200' },
              { key: 'not_run', label: 'Not Run', count: counts.not_run, color: 'bg-slate-50 text-slate-600 hover:bg-slate-100' },
              { key: 'mapped', label: 'Mapped', count: counts.mapped, color: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
              { key: 'in_progress', label: 'In Progress', count: counts.in_progress, color: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
              { key: 'completed', label: 'Completed', count: counts.completed, color: 'bg-green-50 text-green-700 hover:bg-green-100' },
            ].map(f => (
              <button key={f.key}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${statusFilter === f.key ? f.color + ' ring-1 ring-offset-1 ring-slate-300' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'}`}
                onClick={() => setStatusFilter(f.key)}
                data-testid={`filter-${f.key}`}
              >
                {f.label} ({f.count})
              </button>
            ))}
          </div>
          )}

          {viewMode === 'table' ? (
          /* ═══ TABLE VIEW ═══ */
          <div className={`${isSingleView ? '' : 'border border-slate-200 rounded-lg overflow-hidden'}`} data-testid="company-mapping-table">
            {!isSingleView && (
            <div className="grid grid-cols-[2fr_1.2fr_0.6fr_0.6fr_1fr_0.5fr] px-4 py-2.5 bg-slate-50 border-b border-slate-200 gap-3">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Company</span>
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Therapeutic Area</span>
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-center">Assets</span>
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-center">Targets</span>
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Status</span>
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-center">Action</span>
            </div>
            )}
            <div className="divide-y divide-slate-100" data-testid="company-mapping-list">
              {filteredCompanies.map((c) => {
                const companyStatus = c._status;
                const isExpanded = expandedId === c.id;
                const companyAssets = assetsByCompany[c.id] || [];
                const hasNCD = companiesWithNCD.has(c.id);
                const totalTargets = companyAssets.reduce((sum, a) => {
                  const aMappings = mappingsByKey[a.id] || [];
                  const latest = buildUnionMapping(aMappings);
                  return sum + (latest?.status === 'completed' ? (latest.target_companies?.length || 0) : 0);
                }, 0);
                const legacyMappings = mappingsByKey[c.id] || [];
                const legacyLatest = buildUnionMapping(legacyMappings);
                const legacyTargets = legacyLatest?.status === 'completed' ? (legacyLatest.target_companies?.length || 0) : 0;

                return (
                  <div key={c.id} data-testid={`company-card-${c.id}`}>
                    {!isSingleView && (
                    <div className={`grid grid-cols-[2fr_1.2fr_0.6fr_0.6fr_1fr_0.5fr] px-4 py-3 items-center gap-3 cursor-pointer hover:bg-slate-50/70 transition-colors border-l-4 ${STATUS_BORDER[companyStatus]?.replace('border-l-4 ', '') || ''}`}
                      onClick={() => {
                        navigate(`/ai-mapping/${c.id}`);
                      }}
                      data-testid={`company-row-${c.id}`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[companyStatus]}`} />
                        <p className="text-sm font-medium text-slate-900 truncate">{c.company_name}</p>
                        <a href={`/biotech/${c.id}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-sky-500 hover:text-sky-700 shrink-0">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{companyAssets.map(a => a.asset_name).join(', ') || '—'}</p>
                      <p className="text-xs text-slate-600 text-center font-medium">{companyAssets.length}</p>
                      <p className="text-xs text-slate-600 text-center font-medium">{(totalTargets + legacyTargets) || '—'}</p>
                      <div>
                        <Badge className={`text-[9px] ${
                          companyStatus === 'completed' ? 'bg-green-100 text-green-700'
                          : companyStatus === 'in_progress' ? 'bg-amber-100 text-amber-700'
                          : companyStatus === 'mapped' ? 'bg-orange-100 text-orange-700'
                          : 'bg-slate-100 text-slate-600'
                        }`}>{STATUS_LABEL[companyStatus]}</Badge>
                      </div>
                      <div className="flex justify-center">
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                    )}
                    {(isExpanded || isSingleView) && (
                      <div className="border-t border-slate-100 bg-slate-50/30">
                        {companyAssets.length === 0 && legacyMappings.length === 0 ? (
                          <div className="px-5 py-6 text-center">
                            <FlaskConical className="w-7 h-7 text-slate-300 mx-auto mb-2" />
                            <p className="text-xs text-slate-500">No assets added yet. <a href={`/biotech/${c.id}`} className="text-sky-600 hover:underline">Add molecules</a> to run per-asset target mapping.</p>
                          </div>
                        ) : (
                          <>
                            {companyAssets.map(a => {
                              const assetMappings = mappingsByKey[a.id] || [];
                              const latestMapping = buildUnionMapping(assetMappings);
                              const isRunning = runningIds.has(a.id);
                              const hasResults = latestMapping?.status === 'completed' && latestMapping?.target_companies?.length > 0;
                              const hasFailed = latestMapping?.status === 'failed';
                              const targetCount = latestMapping?.target_companies?.length || 0;
                              const canRun = hasNCD || a.therapeutic_area || a.indication || a.molecule_description;
                              const isAssetExpanded = expandedAssetId === a.id;
                              const interests = a.deal_interests || c.deal_interests || [];
                              const showPharma = interests.includes('Out-Licensing') || interests.includes('Co-Development & R&D') || !interests.length;
                              const showInvestor = false; // Investor module deactivated (Feb 2026 · biotech↔pharma only)
                              const eKey = a.id;
                              const oSummary = outreachSummary[a.id] || {};
                              const pharmaSentViaOutreach = (oSummary.pharma_sent || 0) > 0;
                              const investorSentViaOutreach = (oSummary.investor_sent || 0) > 0;
                              const isPharmaComplete = latestMapping?.email_sent_pharma === 'complete' || latestMapping?.email_sent_pharma === true || pharmaSentViaOutreach;
                              const isPharmaInProgress = !isPharmaComplete && latestMapping?.email_sent_pharma === 'in_progress';
                              const isInvestorSent = latestMapping?.email_sent_investor || investorSentViaOutreach;
                              return (
                                <div key={a.id} className={`border-t border-slate-100 ${isRunning ? 'bg-violet-50/20' : ''}`} data-testid={`asset-row-${a.id}`}>
                                  <div className="flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-slate-50/30 transition-colors" onClick={() => setExpandedAssetId(isAssetExpanded ? null : a.id)}>
                                    <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${isRunning ? 'bg-violet-100' : hasResults ? 'bg-green-50' : 'bg-slate-50'}`}>
                                      {isRunning ? <Loader2 className="w-4 h-4 text-violet-500 animate-spin" /> : <FlaskConical className="w-4 h-4 text-violet-500" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold text-slate-800 cursor-pointer hover:text-violet-700 hover:underline" onClick={(e) => { e.stopPropagation(); openAssetDetail(a); }} title="View & copy asset details" data-testid={`asset-detail-link-${a.id}`}>{a.asset_name}</p>
                                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                        {a.therapeutic_area && <span className="text-[10px] text-slate-500">{a.therapeutic_area}</span>}
                                        <Badge variant="secondary" className={`text-[9px] ${STAGE_COLORS[a.stage] || 'bg-slate-100 text-slate-600'}`}>{a.stage}</Badge>
                                        {a.indication && <span className="text-[10px] text-slate-400">{a.indication}</span>}
                                        {isPharmaComplete ? (
                                          <Badge className="bg-green-100 text-green-700 text-[8px] px-1.5 py-0">Pharma Sent</Badge>
                                        ) : isPharmaInProgress ? (
                                          <Badge className="bg-amber-100 text-amber-700 text-[8px] px-1.5 py-0">In Progress</Badge>
                                        ) : null}
                                        {isInvestorSent && (
                                          <Badge className="bg-violet-100 text-violet-700 text-[8px] px-1.5 py-0">Investor Sent</Badge>
                                        )}
                                        {latestMapping?.region && (
                                          <Badge className="bg-orange-100 text-orange-700 text-[8px] px-1.5 py-0">{latestMapping.region}</Badge>
                                        )}
                                        {isRunning && <span className="text-[10px] text-violet-500 font-medium">AI searching...</span>}
                                        {hasFailed && <span className="text-[10px] text-red-500 flex items-center gap-0.5"><AlertCircle className="w-3 h-3" /> Failed</span>}
                                      </div>
                                    </div>
                                    <div className="flex flex-col gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                                      {hasResults && (
                                        <div className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-50 rounded-md border border-slate-100" data-testid={`pharma-group-${a.id}`}>
                                          <button onClick={() => openOutreach(c.id, c.company_name, a.id, a.asset_name, latestMapping.target_companies, latestMapping.id)}
                                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors cursor-pointer ${
                                              isPharmaComplete
                                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                : isPharmaInProgress
                                                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                                  : 'bg-sky-100 text-sky-700 hover:bg-sky-200'
                                            }`}
                                            data-testid={`send-outreach-btn-${a.id}`}
                                          >
                                            {outreachPreparing === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                                            {outreachPreparing === a.id
                                              ? 'Preparing…'
                                              : isPharmaComplete
                                                ? 'Sent'
                                                : isPharmaInProgress
                                                  ? 'In Progress'
                                                  : 'Outreach'}
                                          </button>
                                          {latestMapping?._is_union && (
                                            <Badge
                                              className="bg-violet-100 text-violet-700 text-[9px] h-5"
                                              title={`Combined view across ${latestMapping._runs_count} campaigns — deduplicated by company name. The N pharma count to the right is the total unique pharmas in the combined list.`}
                                              data-testid={`union-badge-header-${a.id}`}
                                            >
                                              ⊕ Combined ({latestMapping._runs_count})
                                            </Badge>
                                          )}
                                          <Badge className="bg-violet-100 text-violet-700 text-[9px] h-5" title="Total buyer targets in the latest mapping (mix of pharma / biotech / distributors / regional partners)">{targetCount} targets</Badge>
                                        </div>
                                      )}
                                      {showInvestor && canRun && (
                                        <div className="flex items-center gap-0.5">
                                          <button onClick={() => openInvestorOutreach(c.id, c.company_name, a.id, a.asset_name)}
                                            disabled={generatingEmail[`${eKey}_investor`]}
                                            className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border transition-colors ${
                                              isInvestorSent
                                                ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
                                                : 'bg-white border-amber-200 text-amber-700 hover:bg-amber-50'
                                            }`}
                                            data-testid={`investor-outreach-btn-${a.id}`}
                                          >
                                            {generatingEmail[`${eKey}_investor`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
                                            {isInvestorSent ? 'Investor Sent' : 'Investor Outreach'}
                                          </button>
                                          {!isInvestorSent && (
                                            <button
                                              onClick={(e) => { e.stopPropagation(); dismissInvestorOutreach(a.id); }}
                                              className="w-5 h-5 flex items-center justify-center rounded text-amber-500 hover:bg-amber-100 hover:text-amber-700"
                                              title="Hide Investor Outreach for this asset (you can re-enable it later from the asset's edit dialog)"
                                              data-testid={`dismiss-investor-outreach-${a.id}`}
                                            >
                                              <X className="w-3 h-3" />
                                            </button>
                                          )}
                                        </div>
                                      )}
                                      {isRunning ? (
                                        <Badge className="bg-violet-100 text-violet-600 text-xs animate-pulse">Processing...</Badge>
                                      ) : canRun ? (
                                        <div className="flex items-center gap-1.5">
                                          <RegionPicker
                                            value={regionFilter[a.id]}
                                            onChange={v => setRegionFilter(prev => ({ ...prev, [a.id]: v }))}
                                            testId={`region-select-${a.id}`}
                                          />
                                          <Button size="sm" className="bg-violet-600 text-white hover:bg-violet-700 text-xs h-7" onClick={() => handleRunTargets(c.id, a.id)} data-testid={`run-targets-${a.id}`}>
                                            <Search className="w-3 h-3 mr-1" />{hasResults ? 'Re-run' : 'Find Pharma'}
                                          </Button>
                                          {hasResults && (
                                            <Button
                                              size="sm" variant="ghost"
                                              className="text-violet-600 hover:bg-violet-50 text-[10px] h-7 px-2"
                                              onClick={() => handleRunTargets(c.id, a.id, { forceRefresh: true })}
                                              title="Bypass the 30-day web-discovery cache — Claude will re-scan the internet for net-new buyers. Use when news / M&A landscape has shifted."
                                              data-testid={`refresh-discovery-${a.id}`}
                                            >
                                              🔄 Refresh discovery
                                            </Button>
                                          )}
                                          {hasResults && latestMapping?.id && (
                                            <Button
                                              size="sm" variant="ghost"
                                              className="text-amber-600 hover:bg-amber-50 text-[10px] h-7 px-2"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                // Union view: collect every completed campaign's id so we
                                                // purge the underlying storage, not just the latest run.
                                                const ids = (assetMappings || [])
                                                  .filter(mm => mm?.status === 'completed' && Array.isArray(mm?.target_companies))
                                                  .map(mm => mm.id);
                                                handlePurgeWeakRationale(ids.length ? ids : [latestMapping.id], a.asset_name);
                                              }}
                                              title="Retroactively drop targets from this mapping whose rationale is thin, generic, or TA-only. Uses the same v11 quality gate that new discovery runs use. Applies across every campaign in the combined view."
                                              data-testid={`purge-weak-rationale-${a.id}`}
                                            >
                                              🧹 Purge weak
                                            </Button>
                                          )}
                                          {latestMapping?.status === 'completed' && (
                                            <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 text-[10px] h-7 px-2"
                                              onClick={(e) => { e.stopPropagation(); handleSignalAudit(latestMapping.id, a.asset_name); }}
                                              title="Re-check every mapped company against the signals logged on its profile (Emerging / Signalled Interests). A conflict — e.g. 'focused on medical dermatology' vs a topical asset — moves it to 'needs your call' with the dated quote."
                                              data-testid={`signal-audit-${a.id}`}>
                                              ⚠ Check signals
                                            </Button>
                                          )}
                                        </div>
                                      ) : (
                                        <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-200">Add Details</Badge>
                                      )}
                                    </div>
                                    {isAssetExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                                  </div>
                                  {isAssetExpanded && assetMappings.length > 0 && (() => {
                                    const latest = buildUnionMapping(assetMappings);
                                    // For history: show every individual completed run as before
                                    // (so users can audit per-run differences). The latest entry
                                    // in `assetMappings` is shown via the unioned view above; we
                                    // keep its raw self in history too so users can see the
                                    // single-run count vs the cumulative count.
                                    const completedRuns = assetMappings.filter(mm => mm?.status === 'completed' && Array.isArray(mm?.target_companies));
                                    const older = latest?._is_union
                                      ? assetMappings // when union is shown, every individual run is "older" history
                                      : assetMappings.slice(1);
                                    const showHistory = showHistoryAssetIds.has(a.id);
                                    // "Previous run" diff baseline = the latest single run (i.e.
                                    // raw assetMappings[0]), so net-new-vs-prior comparisons stay
                                    // intuitive even when union view is active.
                                    const previousLatest = latest?._is_union ? assetMappings[1] : assetMappings[1];
                                    const previousTargetNames = previousLatest?.target_companies
                                      ? new Set((previousLatest.target_companies || []).map(t => (t.company_name || '').trim().toLowerCase()))
                                      : null;
                                    return (
                                      <div className="border-t border-slate-50 bg-white">
                                        {renderMappingResults(latest, eKey, c.id, a.id, `${c.company_name} — ${a.asset_name}`, { isLatest: true, previousTargetNames, runsCount: completedRuns.length })}
                                        {older.length > 0 && (
                                          <>
                                            <div className="px-5 py-2.5 bg-slate-50 border-t-2 border-violet-200 flex items-center justify-between">
                                              <span className="text-[11px] font-semibold text-slate-600 inline-flex items-center gap-1.5">
                                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                                Individual campaigns ({older.length})
                                              </span>
                                              <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setShowHistoryAssetIds(prev => {
                                                  const next = new Set(prev);
                                                  next.has(a.id) ? next.delete(a.id) : next.add(a.id);
                                                  return next;
                                                })}
                                                className="h-6 text-[10px] cursor-pointer border-slate-300 hover:bg-slate-100"
                                                data-testid={`toggle-history-${a.id}`}
                                              >
                                                {showHistory ? (<><ChevronUp className="w-3 h-3 mr-1" />Hide campaigns</>) : (<><ChevronDown className="w-3 h-3 mr-1" />Show {older.length} individual campaign{older.length === 1 ? '' : 's'}</>)}
                                              </Button>
                                            </div>
                                            {showHistory && (
                                              <div className="bg-slate-50/40">
                                                {/* Show latest-first (as ordered by API). Sequence numbers
                                                    are based on creation order: oldest = #1. */}
                                                {older.map((om, oi) => (
                                                  <div key={om.id} className={`border-t-2 ${oi === 0 ? 'border-violet-100' : 'border-slate-200'}`}>
                                                    {renderMappingResults(om, eKey, c.id, a.id, `${c.company_name} — ${a.asset_name}`, {
                                                      isLatest: false,
                                                      campaignNumber: older.length - oi,
                                                      totalCampaigns: older.length,
                                                    })}
                                                  </div>
                                                ))}
                                                <div className="px-5 py-2.5 border-t-2 border-violet-200 bg-slate-50 flex justify-center">
                                                  <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setShowHistoryAssetIds(prev => {
                                                      const next = new Set(prev);
                                                      next.delete(a.id);
                                                      return next;
                                                    })}
                                                    className="h-6 text-[10px] cursor-pointer border-slate-300 hover:bg-slate-100"
                                                    data-testid={`collapse-history-bottom-${a.id}`}
                                                  >
                                                    <ChevronUp className="w-3 h-3 mr-1" />
                                                    Hide campaigns
                                                  </Button>
                                                </div>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    );
                                  })()}
                                  {isAssetExpanded && assetMappings.length === 0 && (
                                    <div className="px-5 py-6 text-center border-t border-slate-50">
                                      <Brain className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                                      <p className="text-[10px] text-slate-400">No mapping yet. Click "Run" to search for targets.</p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {legacyMappings.length > 0 && (
                              <div className="border-t border-slate-200">
                                <div className="px-5 py-2 bg-amber-50/50">
                                  <p className="text-[10px] text-amber-600 font-medium">Previous company-level mappings</p>
                                </div>
                                {legacyMappings.map(m => renderMappingResults(m, c.id, c.id, '', c.company_name))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          ) : (
          /* ═══ CARD VIEW ═══ */
          <div className="space-y-3" data-testid="company-mapping-list">
          {filteredCompanies.map((c) => {
            const companyStatus = c._status;
            const isExpanded = expandedId === c.id;
            const companyAssets = assetsByCompany[c.id] || [];
            const hasNCD = companiesWithNCD.has(c.id);
            const totalTargets = companyAssets.reduce((sum, a) => {
              const aMappings = mappingsByKey[a.id] || [];
              const latest = buildUnionMapping(aMappings);
              return sum + (latest?.status === 'completed' ? (latest.target_companies?.length || 0) : 0);
            }, 0);
            // Also count legacy company-level mappings
            const legacyMappings = mappingsByKey[c.id] || [];
            const legacyLatest = buildUnionMapping(legacyMappings);
            const legacyTargets = legacyLatest?.status === 'completed' ? (legacyLatest.target_companies?.length || 0) : 0;

            return (
              <Card key={c.id} className={`border border-slate-200 overflow-hidden ${STATUS_BORDER[companyStatus]}`} data-testid={`company-card-${c.id}`}>
                <div className={`flex items-center gap-4 px-5 py-4 ${isSingleView ? '' : 'cursor-pointer hover:bg-slate-50/50'} transition-colors`}
                  onClick={() => {
                    if (isSingleView) return;
                    navigate(`/ai-mapping/${c.id}`);
                  }}
                  data-testid={`company-row-${c.id}`}>
                  <a href={`/biotech/${c.id}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-slate-100 hover:bg-slate-200 transition-colors" onClick={e => e.stopPropagation()} data-testid={`company-icon-link-${c.id}`}>
                    <Building2 className="w-5 h-5 text-slate-500" />
                  </a>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900 truncate">{c.company_name}</p>
                      <a href={`/biotech/${c.id}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-sky-600 hover:text-sky-700">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {companyAssets.length > 0 && <Badge variant="secondary" className="text-[10px]">{companyAssets.map(a => a.asset_name).join(', ')}</Badge>}
                      <Badge variant="secondary" className="text-[10px]">{companyAssets.length} asset{companyAssets.length !== 1 ? 's' : ''}</Badge>
                      {(totalTargets + legacyTargets) > 0 && <Badge className="bg-violet-100 text-violet-700 text-[10px]">{totalTargets + legacyTargets} targets</Badge>}
                      {(() => {
                        const sentAssets = companyAssets.filter(a => {
                          const m = (mappingsByKey[a.id] || [])[0];
                          const o = outreachSummary[a.id] || {};
                          return m?.email_sent_pharma === 'complete' || m?.email_sent_pharma === true || (o.pharma_sent || 0) > 0;
                        });
                        const pendingAssets = companyAssets.filter(a => {
                          const m = (mappingsByKey[a.id] || [])[0];
                          return m?.status === 'completed' && m?.email_sent_pharma !== 'complete' && m?.email_sent_pharma !== true;
                        });
                        return (
                          <>
                            {sentAssets.map(a => (
                              <span key={a.id} className="inline-flex items-center gap-1 text-[9px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />{a.asset_name}{a.indication ? ` (${a.indication})` : ''} sent
                              </span>
                            ))}
                            {pendingAssets.map(a => (
                              <span key={a.id} className="inline-flex items-center gap-1 text-[9px] bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded border border-orange-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />{a.asset_name}{a.indication ? ` (${a.indication})` : ''} pending
                              </span>
                            ))}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-100">
                    {companyAssets.length === 0 && legacyMappings.length === 0 ? (
                      <div className="px-5 py-8 text-center">
                        <FlaskConical className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-xs text-slate-500">No assets added yet. <a href={`/biotech/${c.id}`} className="text-sky-600 hover:underline">Add molecules</a> to run per-asset target mapping.</p>
                      </div>
                    ) : (
                      <>
                        {/* Per-asset rows */}
                        {companyAssets.map(a => {
                          const assetMappings = mappingsByKey[a.id] || [];
                          const latestMapping = buildUnionMapping(assetMappings);
                          const isRunning = runningIds.has(a.id);
                          const hasResults = latestMapping?.status === 'completed' && latestMapping?.target_companies?.length > 0;
                          const hasFailed = latestMapping?.status === 'failed';
                          const targetCount = latestMapping?.target_companies?.length || 0;
                          const canRun = hasNCD || a.therapeutic_area || a.indication || a.molecule_description;
                          const isAssetExpanded = expandedAssetId === a.id;
                          const interests = a.deal_interests || c.deal_interests || [];
                          const showPharma = interests.includes('Out-Licensing') || interests.includes('Co-Development & R&D') || !interests.length;
                          const showInvestor = false; // Investor module deactivated (Feb 2026 · biotech↔pharma only)
                          const eKey = a.id;
                          const oSummary = outreachSummary[a.id] || {};
                          const pharmaSentViaOutreach = (oSummary.pharma_sent || 0) > 0;
                          const investorSentViaOutreach = (oSummary.investor_sent || 0) > 0;
                          const isPharmaComplete = latestMapping?.email_sent_pharma === 'complete' || latestMapping?.email_sent_pharma === true || pharmaSentViaOutreach;
                          const isPharmaInProgress = !isPharmaComplete && latestMapping?.email_sent_pharma === 'in_progress';
                          const isInvestorSent = latestMapping?.email_sent_investor || investorSentViaOutreach;

                          return (
                            <div key={a.id} className={`border-t border-slate-100 ${isRunning ? 'bg-violet-50/20' : ''}`} data-testid={`asset-row-${a.id}`}>
                              <div className="flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-slate-50/30 transition-colors" onClick={() => setExpandedAssetId(isAssetExpanded ? null : a.id)}>
                                <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${isRunning ? 'bg-violet-100' : hasResults ? 'bg-green-50' : 'bg-slate-50'}`}>
                                  {isRunning ? <Loader2 className="w-4 h-4 text-violet-500 animate-spin" /> : <FlaskConical className="w-4 h-4 text-violet-500" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-slate-800 cursor-pointer hover:text-violet-700 hover:underline" onClick={(e) => { e.stopPropagation(); openAssetDetail(a); }} title="View & copy asset details" data-testid={`asset-detail-link-${a.id}`}>{a.asset_name}</p>
                                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                    {a.therapeutic_area && <span className="text-[10px] text-slate-500">{a.therapeutic_area}</span>}
                                    <Badge variant="secondary" className={`text-[9px] ${STAGE_COLORS[a.stage] || 'bg-slate-100 text-slate-600'}`}>{a.stage}</Badge>
                                    {a.indication && <span className="text-[10px] text-slate-400">{a.indication}</span>}
                                    {isPharmaComplete ? (
                                      <Badge className="bg-green-100 text-green-700 text-[8px] px-1.5 py-0">Pharma Sent</Badge>
                                    ) : isPharmaInProgress ? (
                                      <Badge className="bg-amber-100 text-amber-700 text-[8px] px-1.5 py-0">In Progress</Badge>
                                    ) : null}
                                    {isInvestorSent && (
                                      <Badge className="bg-violet-100 text-violet-700 text-[8px] px-1.5 py-0">Investor Sent</Badge>
                                    )}
                                    {latestMapping?.region && (
                                      <Badge className="bg-orange-100 text-orange-700 text-[8px] px-1.5 py-0">{latestMapping.region}</Badge>
                                    )}
                                    {(() => {
                                      const indiaExc = latestMapping?.exclusions?.indian_pharmas_pre_phase_2 || [];
                                      if (!indiaExc.length) return null;
                                      const list = indiaExc.slice(0, 12).map(x => `• ${x.company_name}`).join('\n');
                                      const more = indiaExc.length > 12 ? `\n…and ${indiaExc.length - 12} more` : '';
                                      return (
                                        <Badge
                                          className="bg-amber-100 text-amber-800 text-[8px] px-1.5 py-0 cursor-help border border-amber-300"
                                          title={`Indian pharmas excluded — asset hasn't completed Phase 2:\n${list}${more}`}
                                          data-testid={`india-excluded-badge-${a.id}`}
                                        >
                                          🇮🇳 {indiaExc.length} excluded (pre-Phase 2)
                                        </Badge>
                                      );
                                    })()}
                                    {isRunning && <span className="text-[10px] text-violet-500 font-medium">AI searching...</span>}
                                    {hasFailed && <span className="text-[10px] text-red-500 flex items-center gap-0.5"><AlertCircle className="w-3 h-3" /> Failed</span>}
                                  </div>
                                </div>
                                <div className="flex flex-col gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                                  {/* ── Pharma group ── */}
                                  {hasResults && (
                                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-50 rounded-md border border-slate-100" data-testid={`pharma-group-${a.id}`}>
                                      <button onClick={() => openOutreach(c.id, c.company_name, a.id, a.asset_name, latestMapping.target_companies, latestMapping.id)}
                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors cursor-pointer ${
                                          isPharmaComplete
                                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                            : isPharmaInProgress
                                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                              : 'bg-sky-100 text-sky-700 hover:bg-sky-200'
                                        }`}
                                        data-testid={`send-outreach-btn-${a.id}`}
                                      >
                                        {outreachPreparing === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                                        {outreachPreparing === a.id
                                          ? 'Preparing…'
                                          : isPharmaComplete
                                            ? 'Sent'
                                            : isPharmaInProgress
                                              ? 'In Progress'
                                              : 'Outreach'}
                                      </button>
                                      <Badge className="bg-violet-100 text-violet-700 text-[9px] h-5">{targetCount} pharma</Badge>
                                    </div>
                                  )}

                                  {/* ── Investor group ── */}
                                  {showInvestor && canRun && (
                                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50/50 rounded-md border border-amber-100" data-testid={`investor-group-${a.id}`}>
                                      <button onClick={() => openInvestorOutreach(c.id, c.company_name, a.id, a.asset_name)}
                                        disabled={generatingEmail[`${eKey}_investor`]}
                                        className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border transition-colors ${
                                          isInvestorSent
                                            ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
                                            : 'bg-white border-amber-200 text-amber-700 hover:bg-amber-50'
                                        }`}
                                        data-testid={`investor-outreach-btn-${a.id}`}
                                      >
                                        {generatingEmail[`${eKey}_investor`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
                                        {isInvestorSent ? 'Investor Sent' : 'Investor Outreach'}
                                      </button>
                                      {!isInvestorSent && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); dismissInvestorOutreach(a.id); }}
                                          className="w-5 h-5 flex items-center justify-center rounded text-amber-500 hover:bg-amber-100 hover:text-amber-700"
                                          title="Hide Investor Outreach for this asset"
                                          data-testid={`dismiss-investor-outreach-expanded-${a.id}`}
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  )}

                                  {/* Run / Re-run Pharma */}
                                  {isRunning ? (
                                    <Badge className="bg-violet-100 text-violet-600 text-xs animate-pulse">Processing...</Badge>
                                  ) : canRun ? (
                                    <div className="flex items-center gap-1.5">
                                      <RegionPicker
                                        value={regionFilter[a.id]}
                                        onChange={v => setRegionFilter(prev => ({ ...prev, [a.id]: v }))}
                                        testId={`region-select-${a.id}`}
                                      />
                                      <Button size="sm" className="bg-violet-600 text-white hover:bg-violet-700 text-xs h-7" onClick={() => handleRunTargets(c.id, a.id)} data-testid={`run-targets-${a.id}`}>
                                        <Search className="w-3 h-3 mr-1" />{hasResults ? 'Re-run' : 'Find Pharma'}
                                      </Button>
                                      {hasResults && (
                                        <Button
                                          size="sm" variant="ghost"
                                          className="text-violet-600 hover:bg-violet-50 text-[10px] h-7 px-2"
                                          onClick={() => handleRunTargets(c.id, a.id, { forceRefresh: true })}
                                          title="Bypass the 30-day web-discovery cache — Claude will re-scan the internet for net-new buyers. Use when news / M&A landscape has shifted."
                                          data-testid={`refresh-discovery-${a.id}`}
                                        >
                                          🔄 Refresh discovery
                                        </Button>
                                      )}
                                      {hasResults && latestMapping?.id && (
                                        <Button
                                          size="sm" variant="ghost"
                                          className="text-amber-600 hover:bg-amber-50 text-[10px] h-7 px-2"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const ids = (assetMappings || [])
                                              .filter(mm => mm?.status === 'completed' && Array.isArray(mm?.target_companies))
                                              .map(mm => mm.id);
                                            handlePurgeWeakRationale(ids.length ? ids : [latestMapping.id], a.asset_name);
                                          }}
                                          title="Retroactively drop targets from this mapping whose rationale is thin, generic, or TA-only. Uses the same v11 quality gate that new discovery runs use. Applies across every campaign in the combined view."
                                          data-testid={`purge-weak-rationale-${a.id}`}
                                        >
                                          🧹 Purge weak
                                        </Button>
                                      )}
                                      {latestMapping?.status === 'completed' && (
                                        <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 text-[10px] h-7 px-2"
                                          onClick={(e) => { e.stopPropagation(); handleSignalAudit(latestMapping.id, a.asset_name); }}
                                          title="Re-check every mapped company against the signals logged on its profile. A conflict moves it to 'needs your call' with the dated quote."
                                          data-testid={`signal-audit-${a.id}`}>
                                          ⚠ Check signals
                                        </Button>
                                      )}
                                    </div>
                                  ) : (
                                    <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-200">Add Details</Badge>
                                  )}
                                </div>
                                {isAssetExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                              </div>
                              {isAssetExpanded && assetMappings.length > 0 && (() => {
                                const latest = buildUnionMapping(assetMappings);
                                const completedRuns = assetMappings.filter(mm => mm?.status === 'completed' && Array.isArray(mm?.target_companies));
                                const older = latest?._is_union ? assetMappings : assetMappings.slice(1);
                                const showHistory = showHistoryAssetIds.has(a.id);
                                const previousLatest = assetMappings[1];
                                const previousTargetNames = previousLatest?.target_companies
                                  ? new Set((previousLatest.target_companies || []).map(t => (t.company_name || '').trim().toLowerCase()))
                                  : null;
                                return (
                                  <div className="border-t border-slate-50 bg-white">
                                    {renderMappingResults(latest, eKey, c.id, a.id, `${c.company_name} — ${a.asset_name}`, { isLatest: true, previousTargetNames, runsCount: completedRuns.length })}
                                    {older.length > 0 && (
                                      <>
                                        <div className="px-5 py-2.5 bg-slate-50 border-t-2 border-violet-200 flex items-center justify-between">
                                          <span className="text-[11px] font-semibold text-slate-600 inline-flex items-center gap-1.5">
                                            <Clock className="w-3.5 h-3.5 text-slate-400" />
                                            Individual campaigns ({older.length})
                                          </span>
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setShowHistoryAssetIds(prev => {
                                              const next = new Set(prev);
                                              next.has(a.id) ? next.delete(a.id) : next.add(a.id);
                                              return next;
                                            })}
                                            className="h-6 text-[10px] cursor-pointer border-slate-300 hover:bg-slate-100"
                                            data-testid={`toggle-history-${a.id}`}
                                          >
                                            {showHistory ? (<><ChevronUp className="w-3 h-3 mr-1" />Hide campaigns</>) : (<><ChevronDown className="w-3 h-3 mr-1" />Show {older.length} individual campaign{older.length === 1 ? '' : 's'}</>)}
                                          </Button>
                                        </div>
                                        {showHistory && (
                                          <div className="bg-slate-50/40">
                                            {older.map((om, oi) => (
                                              <div key={om.id} className={`border-t-2 ${oi === 0 ? 'border-violet-100' : 'border-slate-200'}`}>
                                                {renderMappingResults(om, eKey, c.id, a.id, `${c.company_name} — ${a.asset_name}`, {
                                                  isLatest: false,
                                                  campaignNumber: older.length - oi,
                                                  totalCampaigns: older.length,
                                                })}
                                              </div>
                                            ))}
                                            <div className="px-5 py-2.5 border-t-2 border-violet-200 bg-slate-50 flex justify-center">
                                              <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setShowHistoryAssetIds(prev => {
                                                  const next = new Set(prev);
                                                  next.delete(a.id);
                                                  return next;
                                                })}
                                                className="h-6 text-[10px] cursor-pointer border-slate-300 hover:bg-slate-100"
                                                data-testid={`collapse-history-bottom-${a.id}`}
                                              >
                                                <ChevronUp className="w-3 h-3 mr-1" />
                                                Hide campaigns
                                              </Button>
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                );
                              })()}
                              {isAssetExpanded && assetMappings.length === 0 && (
                                <div className="px-5 py-6 text-center border-t border-slate-50">
                                  <Brain className="w-6 h-6 text-slate-300 mx-auto mb-1" />
                                  <p className="text-[10px] text-slate-400">No mapping yet. Click "Run" to search for targets.</p>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Legacy company-level mappings */}
                        {legacyMappings.length > 0 && (
                          <div className="border-t border-slate-200">
                            <div className="px-5 py-2 bg-amber-50/50">
                              <p className="text-[10px] text-amber-600 font-medium">Previous company-level mappings</p>
                            </div>
                            {legacyMappings.map(m => renderMappingResults(m, c.id, c.id, '', c.company_name))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
        )}
        </>
        );
      })()}

      {/* Sep 2026 · Asset detail popup — view + copy asset details */}
      <Dialog open={!!assetDetail} onOpenChange={(open) => { if (!open) setAssetDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="asset-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="flex items-center gap-2"><FlaskConical className="w-4 h-4 text-violet-600" />{assetDetail?.asset?.asset_name}</span>
              <Button size="sm" variant="outline" onClick={copyAssetDetails} disabled={assetDetail?.loading} className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="copy-asset-details-btn">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy details
              </Button>
            </DialogTitle>
          </DialogHeader>
          {assetDetail?.loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /></div>
          ) : (
            <div className="space-y-2.5">
              {ASSET_DETAIL_FIELDS.map(([label, get]) => {
                const v = (get(assetDetail?.asset || {}) || '').toString().trim();
                if (!v) return null;
                return (
                  <div key={label}>
                    <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">{label}</p>
                    <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{v}</p>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Delete Mapping</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this target mapping for <span className="font-semibold text-slate-700">{deleteTarget?.source_company}{deleteTarget?.asset_name ? ` — ${deleteTarget.asset_name}` : ''}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteTarget?.id)} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Warm Leads Dialog */}
      <Dialog open={warmLeadsDialog.open} onOpenChange={(open) => !open && setWarmLeadsDialog({ open: false, label: '', contacts: [] })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="warm-leads-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <User className="w-5 h-5 text-emerald-600" />Warm Leads — {warmLeadsDialog.label}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between mt-2 mb-3">
            <p className="text-sm text-slate-500">{warmLeadsDialog.contacts.length} contact{warmLeadsDialog.contacts.length !== 1 ? 's' : ''} matched</p>
            <Button size="sm" variant="outline" className="h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={copyWarmLeads} data-testid="copy-warm-leads-btn">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Table
            </Button>
          </div>
          {warmLeadsDialog.contacts.length > 0 ? (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[1fr_1fr_1fr_1.2fr] px-4 py-2.5 bg-emerald-50 border-b border-slate-200">
                <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Name</span>
                <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Designation</span>
                <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Company</span>
                <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Email</span>
              </div>
              <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                {warmLeadsDialog.contacts.map((ct, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1.2fr] px-4 py-3 items-center hover:bg-slate-50/50">
                    <p className="text-xs font-medium text-slate-900">{ct.name || ''}</p>
                    <p className="text-xs text-slate-600">{ct.designation || ''}</p>
                    <p className="text-xs text-slate-600">{ct.company_name || ct.matched_company || ''}</p>
                    <p className="text-xs text-sky-700">{ct.email || ''}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-8">No matching contacts found</p>
          )}
        </DialogContent>
      </Dialog>
      {/* Outreach Email Dialog */}
      <Dialog open={outreachDialog.open} onOpenChange={open => { if (!open) setOutreachDialog(p => ({ ...p, open: false })); }}>
        <DialogContent className="max-w-6xl w-[min(96vw,1280px)] max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="outreach-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Mail className="w-5 h-5 text-sky-600" />Send Outreach — {outreachDialog.companyName}
              {outreachDialog.assetName && <Badge variant="secondary" className="text-[10px]">{outreachDialog.assetName}</Badge>}
            </DialogTitle>
          </DialogHeader>

          {/* Aug 2026 · Founder Context box at the TOP of the Send Outreach
              modal. Pastes here → injected as top-priority HONOR-THIS block
              into every per-company email generated in this batch. Persists
              to the asset on "Save & Apply" so future batches for the same
              asset re-use it automatically. */}
          {outreachDialog.assetId && (
            <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3 mb-2" data-testid="outreach-dialog-founder-context">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-semibold text-slate-800 uppercase tracking-wider">
                  Founder Context (optional)
                </span>
                <span className="text-[10px] text-slate-500 italic">
                  paste founder&apos;s asks / timelines / deck-share intent — every email in this batch will honor it
                </span>
              </div>
              <textarea
                value={outreachDialogContext}
                onChange={(e) => setOutreachDialogContext(e.target.value)}
                rows={4}
                maxLength={3000}
                placeholder={`e.g. "We are looking for investors and partners to help us continue our new topical drug development. Animal trials should be done by end of August; then pre-clinical/clinical trials. We need investment and/or a partner with know-how. Sending our non-confidential deck. Available for an online meeting if necessary."`}
                className="w-full text-xs text-slate-700 p-2.5 border border-slate-200 rounded-md focus:border-blue-400 focus:outline-none bg-white"
                data-testid="outreach-dialog-founder-context-textarea"
              />
              <div className="flex items-center gap-2 mt-2">
                <Button
                  size="sm"
                  onClick={async () => {
                    if (!outreachDialog.assetId) return;
                    setOutreachContextSaving(true);
                    try {
                      await axios.patch(
                        `${API}/assets/${outreachDialog.assetId}/outreach-context`,
                        { outreach_context: outreachDialogContext || '' },
                        { withCredentials: true },
                      );
                      toast.success(
                        `Founder context saved. Regenerating ${Object.keys(outreachEmails).length} email(s) so they honor the new context…`,
                        { duration: 6000 },
                      );
                      // Wipe existing generated emails so the loop regenerates
                      // them with the new context. `generateAllEmails` picks
                      // up only rows where `.email` is empty.
                      setOutreachEmails(prev => {
                        const next = {};
                        for (const k of Object.keys(prev)) {
                          next[k] = { ...prev[k], email: '', generating: false, error: null };
                        }
                        return next;
                      });
                      setGenAllProgress(null);
                      // Fire the regeneration loop.
                      setTimeout(() => { generateAllEmails(); }, 300);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Failed to save context');
                    } finally {
                      setOutreachContextSaving(false);
                    }
                  }}
                  disabled={outreachContextSaving}
                  className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="outreach-dialog-save-context-btn"
                >
                  {outreachContextSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                  Save &amp; regenerate all emails
                </Button>
                {outreachDialogContext.trim() && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setOutreachDialogContext('')}
                    className="h-7 text-[11px]"
                    data-testid="outreach-dialog-clear-context-btn"
                  >
                    Clear
                  </Button>
                )}
                <span className="text-[10px] text-slate-500">
                  {outreachDialogContext.length}/3000 chars
                </span>
              </div>
            </div>
          )}

          {rateLimitPause && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="rate-limit-banner">
              <Clock className="w-4 h-4 shrink-0" />
              <span>Gmail rate limit reached — sending is <strong>paused</strong> and will <strong>auto-resume at {rateLimitPause.until.toLocaleTimeString()}</strong>. Keep this tab open; already-sent emails will not be re-sent.</span>
            </div>
          )}

          <SkippedPanel skipped={skippedTargets} exportMeta={{ asset_name: outreachDialog.assetName || '', biotech_company_name: outreachDialog.companyName || '' }} />

          {outreachDialog.assetId && Object.keys(outreachEmails).length > 0 && (
            <SmartRoutingPreviewPanel
              assetId={outreachDialog.assetId}
              pharmaCompanies={Object.keys(outreachEmails)}
            />
          )}

          {Object.keys(outreachEmails).length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No warm leads with contacts found for this mapping.</p>
          ) : (
            <>
              {/* Established-relationship auto-exclusion summary */}
              {(() => {
                const excludedKeys = Object.keys(outreachEmails).filter(k => (outreachEmails[k].exclusionReasons || []).length > 0);
                if (excludedKeys.length === 0) return null;
                // Labels + plain-language tooltips for each exclusion reason.
                // The umbrella header just says "auto-excluded" now — the prior
                // wording ("established relationship detected") was inaccurate
                // for reasons like Do-Not-Contact flags or own-portfolio overlap.
                const reasonLabels = {
                  already_contacted_this_asset: { label: 'Already emailed for this asset', tip: 'You already sent outreach to this pharma for the SAME asset in a prior campaign. Re-emailing would be duplicate outreach.' },
                  already_contacted_other_asset: { label: 'Emailed for a prior asset', tip: 'You emailed this pharma earlier — for a different asset. The contact has heard from you recently; cooldown applies.' },
                  replied_historically: { label: 'Has replied to past outreach', tip: 'This pharma replied to a previous email from you (positive or negative). Sending another cold pitch would feel tone-deaf — check the response history first.' },
                  meeting_logged: { label: 'Meeting / call logged', tip: 'You have a meeting or call logged with this pharma in the past. Treat as a warm follow-up, not a fresh cold pitch.' },
                  dnc_flagged: { label: 'Marked Do-Not-Contact', tip: 'This pharma is flagged Do-Not-Contact at the COMPANY level (on the pharma profile or biotech record). Individual contacts flagged DNC do NOT auto-exclude the whole company anymore — only the flagged contact is blocked on the contact row.' },
                  commercialization_partner: { label: 'Distributor / partner', tip: 'This company is already a commercialization partner / distributor — not a typical in-licensing target.' },
                  my_company: { label: 'Your own portfolio company', tip: 'This company is in your OWN biotech portfolio. The system prevents you from emailing your own roster.' },
                  not_buyer_eligible: { label: 'Handshake OFF \u2014 not buyer-eligible', tip: 'The buyer handshake indicator is OFF on this company profile, so generation and sending are both refused. Toggle it ON on the company profile if this has changed.' },
                  client_already_in_touch: { label: 'Biotech is already in touch', tip: 'This biotech client has confirmed they are already in active discussion with this pharma — re-introducing them via ForesightRx would create duplicate outreach. Manage via Client Excluded Pharmas on the biotech profile.' },
                  manually_excluded: { label: 'Excluded via Exclude List', tip: 'You (or someone on your team) explicitly added this company to the Exclude List for this asset.' },
                };
                const counts = {};
                excludedKeys.forEach(k => {
                  (outreachEmails[k].exclusionReasons || []).forEach(r => { counts[r] = (counts[r] || 0) + 1; });
                });
                return (
                  <div className="mt-2 mb-3 p-3 rounded-md border border-amber-200 bg-amber-50/70" data-testid="exclusion-banner">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-amber-900 mb-1">
                          {excludedKeys.length} compan{excludedKeys.length === 1 ? 'y' : 'ies'} auto-excluded · hover each tag for the reason
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(counts).map(([reason, n]) => {
                            const info = reasonLabels[reason] || { label: reason, tip: '' };
                            return (
                              <Badge
                                key={reason}
                                className="bg-white border border-amber-300 text-amber-800 text-[10px] px-1.5 py-0 cursor-help"
                                title={info.tip || ''}
                                data-testid={`exclusion-reason-${reason}`}
                              >
                                {info.label}: {n}
                              </Badge>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-amber-700 mt-1.5">
                          These are kept in the list (greyed out) so you can see them. Use the checkbox next to each company to include it individually, or tick <span className="font-semibold">Include anyway</span> to include all at once.
                        </p>
                        {/* Per-company breakdown so user can audit each exclusion individually
                            and override on a per-row basis (not just the global "Include anyway" toggle). */}
                        <div className="mt-2 pt-2 border-t border-amber-200/70 space-y-1 max-h-[200px] overflow-y-auto" data-testid="exclusion-detail-list">
                          {excludedKeys.sort((a, b) => a.localeCompare(b)).map((cname, rowIdx) => {
                            const reasons = outreachEmails[cname].exclusionReasons || [];
                            const details = outreachEmails[cname].exclusionDetails || {};
                            const included = selectedCompanies.has(cname);
                            return (
                              <div key={cname} className="flex items-center gap-2 text-[10px]" data-testid={`exclusion-row-${cname}`}>
                                <span className="w-6 text-right tabular-nums text-amber-600/80 shrink-0" data-testid={`exclusion-row-num-${cname}`}>{rowIdx + 1}.</span>
                                <input
                                  type="checkbox"
                                  checked={included}
                                  onChange={() => {
                                    setSelectedCompanies(prev => {
                                      const next = new Set(prev);
                                      if (next.has(cname)) next.delete(cname);
                                      else next.add(cname);
                                      return next;
                                    });
                                  }}
                                  className="w-3.5 h-3.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500 shrink-0"
                                  data-testid={`exclusion-toggle-${cname}`}
                                  title={included ? 'Click to exclude' : 'Click to include in this send'}
                                />
                                <span className="font-semibold text-amber-900 shrink-0 min-w-[120px] max-w-[180px] truncate" title={cname}>{cname}</span>
                                <div className="flex flex-wrap gap-1">
                                  {reasons.map(r => {
                                    const info = reasonLabels[r] || { label: r, tip: '' };
                                    const specific = (details[r] || '').trim();
                                    const tooltip = specific ? `${info.tip}\n\nDetails: ${specific}` : (info.tip || '');
                                    return (
                                      <span
                                        key={r}
                                        className="px-1.5 py-0.5 rounded bg-white border border-amber-200 text-amber-800 cursor-help"
                                        title={tooltip}
                                        data-testid={`exclusion-detail-${cname}-${r}`}
                                      >
                                        {info.label}{specific ? ` · ${specific.length > 40 ? specific.slice(0, 40) + '…' : specific}` : ''}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <label className="flex items-center gap-1.5 cursor-pointer select-none shrink-0" data-testid="toggle-show-excluded">
                        <input type="checkbox"
                          checked={showExcludedRelationships}
                          onChange={e => {
                            setShowExcludedRelationships(e.target.checked);
                            if (e.target.checked) {
                              // Re-enable selection for excluded ones
                              setSelectedCompanies(prev => {
                                const next = new Set(prev);
                                excludedKeys.forEach(k => next.add(k));
                                return next;
                              });
                            } else {
                              // Re-exclude
                              setSelectedCompanies(prev => {
                                const next = new Set(prev);
                                excludedKeys.forEach(k => next.delete(k));
                                return next;
                              });
                            }
                          }}
                          className="w-3.5 h-3.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                        />
                        <span className="text-[11px] text-amber-900 font-medium">Include anyway</span>
                      </label>
                    </div>
                  </div>
                );
              })()}
              <div className="flex items-center justify-between mt-2 mb-3">
                <div className="flex items-center gap-3">
                  {/* Feb 2026 (v12) — "Select All" respects the "New
                      companies only" toggle so hidden already-emailed
                      companies never get accidentally selected + sent. */}
                  {(() => {
                    const selectable = Object.keys(outreachEmails).filter(k => {
                      if (showNewCompaniesOnly && contactedCompanies.has(k.trim().toLowerCase())) return false;
                      const _i = outreachEmails[k] || {};
                      const _emailed = (_i.exclusionReasons || []).includes('already_contacted_this_asset') || (_i.sent || []).length > 0;
                      return !_emailed || expandedEmailed.has(k);
                    });
                    const allSelected = selectable.length > 0 && selectable.every(k => selectedCompanies.has(k));
                    const someSelected = selectable.some(k => selectedCompanies.has(k));
                    return (
                      <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="select-all-companies">
                        <input type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={e => setSelectedCompanies(e.target.checked ? new Set(selectable) : new Set())}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        <span className="text-xs text-slate-600 font-medium">All{showNewCompaniesOnly ? ' (new only)' : ''}</span>
                      </label>
                    );
                  })()}
                  <p className="text-xs text-slate-500">
                    {selectedCompanies.size} of {Object.keys(outreachEmails).length} selected
                    {contactedCompanies.size > 0 && (() => {
                      const prevCount = Object.keys(outreachEmails).filter(k => contactedCompanies.has(k.trim().toLowerCase())).length;
                      return prevCount > 0 ? <span className="ml-1 text-amber-600 font-medium">({prevCount} previously contacted)</span> : null;
                    })()}
                  </p>
                  {/* "New companies only" filter — hides every target that
                      already received outreach for any asset of this biotech.
                      Default off; flipping it on lets the user see only the
                      net-new targets from this re-mapping (e.g. the 48 net-new
                      out of 62 when re-mapping a biotech with 51 prior
                      targets). */}
                  {(() => {
                    const prevCount = Object.keys(outreachEmails).filter(k => contactedCompanies.has(k.trim().toLowerCase())).length;
                    if (prevCount === 0) return null;
                    return (
                      <label className="flex items-center gap-1.5 cursor-pointer select-none border-l border-slate-200 pl-3" data-testid="toggle-new-companies-only">
                        <input type="checkbox"
                          checked={showNewCompaniesOnly}
                          onChange={e => setShowNewCompaniesOnly(e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        <span className="text-xs text-slate-700 font-medium">New companies only</span>
                      </label>
                    );
                  })()}
                </div>
                <div className="relative" data-testid="jump-to-company">
                  <div className="flex items-center gap-1.5 h-7 px-2 rounded-md border border-slate-200 bg-white focus-within:ring-1 focus-within:ring-sky-300">
                    <Search className="w-3 h-3 text-slate-400 shrink-0" />
                    <input
                      value={jumpQuery}
                      onChange={e => setJumpQuery(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && jumpMatches[0]) jumpToCompany(jumpMatches[0].name); if (e.key === 'Escape') setJumpQuery(''); }}
                      placeholder="Jump to company or #"
                      className="text-xs w-44 bg-transparent outline-none placeholder:text-slate-400"
                      data-testid="jump-to-company-input"
                    />
                  </div>
                  {jumpQuery.trim() && (
                    <div className="absolute z-30 mt-1 w-72 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg py-1" data-testid="jump-to-company-results">
                      {jumpMatches.length === 0 ? (
                        <p className="px-3 py-1.5 text-xs text-slate-400">No company matches</p>
                      ) : jumpMatches.map(m => (
                        <button key={m.name} type="button" onClick={() => jumpToCompany(m.name)}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-sky-50 flex items-center gap-2"
                          data-testid={`jump-to-company-result-${m.name}`}>
                          <span className="tabular-nums text-slate-400 w-7 shrink-0">{m.idx}.</span>
                          <span className="truncate text-slate-800">{m.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={generateAllEmails}
                    disabled={selectedCompanies.size === 0 || !!genAllProgress || sendingAll || Object.values(outreachEmails).some(v => v.generating)}
                    data-testid="generate-all-emails-btn">
                    {genAllProgress
                      ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Generating {genAllProgress.done}/{genAllProgress.total}…</>
                      : <><Brain className="w-3 h-3 mr-1" /> Generate Emails ({selectedCompanies.size})</>}
                  </Button>
                  {Object.entries(outreachEmails).some(([name, v]) => selectedCompanies.has(name) && v.email && !v.contacts.every(c => v.sent?.includes(c.email))) && (
                    <Button size="sm" className="h-7 text-xs bg-green-600 text-white hover:bg-green-700" onClick={sendAllCompanies}
                      disabled={sendingAll || selectedCompanies.size === 0 || !!genAllProgress || sentHistoryFailed}
                      title={sentHistoryFailed ? 'Sent history could not be loaded — reopen the dialog to retry before sending' : genAllProgress ? 'Wait for email generation to finish first' : undefined}
                      data-testid="send-all-companies-btn">
                      {sendingAll
                        ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> {sendAllProgress ? `Sending ${sendAllProgress.done}/${sendAllProgress.total} · ${sendAllProgress.sent} sent` : 'Sending...'}</>
                        : <><Mail className="w-3 h-3 mr-1" /> Send All ({selectedCompanies.size})</>}
                    </Button>
                  )}
                </div>
              </div>
              {sentHistoryFailed && (
                <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-md bg-rose-50 border border-rose-300" data-testid="sent-history-failed-banner">
                  <p className="text-[11px] text-rose-800">
                    <span className="font-semibold">Sent history could not be loaded</span> — contacts already emailed for this asset may wrongly show as unsent. Sending is locked until it loads (the server still refuses duplicate sends).
                  </p>
                  <Button size="sm" variant="outline" className="h-6 text-[11px] border-rose-400 text-rose-800 hover:bg-rose-100 shrink-0"
                    onClick={() => openOutreach(outreachDialog.companyId, outreachDialog.companyName, outreachDialog.assetId, outreachDialog.assetName, outreachDialog.targets, outreachDialog.mappingId || '')}
                    data-testid="retry-sent-history-btn">Retry</Button>
                </div>
              )}
              {activeQueue && (
                <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200" data-testid="send-queue-status">
                  <div className="flex items-center gap-2 min-w-0">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600 shrink-0" />
                    <span className="text-xs text-emerald-800 truncate" title="The batch runs on the ForesightRx server. Progress is saved after every email; reopen this dialog any time to see it.">
                      {activeQueue.status === 'paused_rate_limit'
                        ? 'Paused — Gmail rate limit hit, auto-resuming in 60s'
                        : `Sending on server — ${activeQueue.counts.sent} sent · ${activeQueue.counts.failed} refused · ${activeQueue.counts.pending} left. Safe to close this tab.`}
                    </span>
                  </div>
                  <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2 text-rose-600 hover:bg-rose-50 shrink-0" onClick={cancelActiveQueue} data-testid="cancel-send-queue-btn">Stop batch</Button>
                </div>
              )}
              <div className="space-y-3">
                {/* Bulk deliverability triage — scoped to the CURRENT selection
                    (and the "New companies only" filter) so clean + flagged +
                    "not generated yet" always reconciles with the selected
                    count. Previously it scanned every generated email, which
                    made the numbers look mismatched (e.g. 54 scanned vs 79
                    selected). */}
                {(() => {
                  const visible = Object.entries(outreachEmails).filter(([name]) => {
                    if (!selectedCompanies.has(name)) return false;
                    if (showNewCompaniesOnly && contactedCompanies.has(name.trim().toLowerCase())) return false;
                    return true;
                  });
                  const withEmail = visible.filter(([, v]) => v.email);
                  if (withEmail.length === 0) return null;
                  const pending = visible.length - withEmail.length;
                  return (
                    <SpamTriagePanel
                      items={withEmail.map(([name, v]) => {
                        const { subject, body } = splitEmail(v.email);
                        return { id: name, subject, body, label: name };
                      })}
                      pendingNote={pending > 0 ? `${pending} selected — email not generated yet` : ''}
                      onChange={(name, { subject, body }) => {
                        const joined = joinEmail(subject, body);
                        setOutreachEmails(prev => ({
                          ...prev,
                          [name]: { ...prev[name], email: joined },
                        }));
                        axios.put(`${API}/ai/email-drafts`, { biotech_company_id: outreachDialog.companyId, asset_id: outreachDialog.assetId || '', target_company_name: name, email: joined }, { withCredentials: true }).catch(() => {});
                      }}
                      testId="aimapping-spam-triage"
                    />
                  );
                })()}
                {(() => {
                  if (!showNewCompaniesOnly) return null;
                  const newCount = Object.keys(outreachEmails).filter(k => !contactedCompanies.has(k.trim().toLowerCase())).length;
                  if (newCount > 0) return null;
                  return (
                    <div className="border border-dashed border-slate-300 rounded-lg p-6 text-center bg-slate-50/50" data-testid="new-companies-empty">
                      <p className="text-sm text-slate-600 font-medium mb-1">No new companies to show</p>
                      <p className="text-xs text-slate-500">Every target in this mapping has already received outreach for this biotech. Untick "New companies only" above to see the full list.</p>
                    </div>
                  );
                })()}
                {Object.entries(outreachEmails)
                  .filter(([targetName]) => {
                    if (!showNewCompaniesOnly) return true;
                    return !contactedCompanies.has(targetName.trim().toLowerCase());
                  })
                  // Sep 2026 · fresh companies first; "Emailed already" rows sink to the bottom.
                  .sort(([, a], [, b]) => {
                    const em = (i) => {
                      const prior = (i.priorSent || []).length;
                      const fresh = (i.sent || []).length - prior;
                      return (((i.exclusionReasons || []).includes('already_contacted_this_asset') || prior > 0) && fresh <= 0) ? 1 : 0;
                    };
                    return em(a) - em(b);
                  })
                  .map(([targetName, info], cardIdx) => {
                  const allSent = info.contacts.length > 0 && info.contacts.every(c => info.sent?.includes(c.email));
                  const alreadyContacted = contactedCompanies.has(targetName.trim().toLowerCase());
                  const policy = getRoutingPolicy(targetName);
                  const isPortalOnly = policy && policy.mode === 'portal_only';
                  const pocEmail = policy && policy.mode === 'single_poc' ? (policy.poc_email || '').toLowerCase() : '';
                  // Resolve the asset's therapeutic_area at render time so the
                  // "X contacts hidden — preferences don't include {TA}" badge
                  // can label which TA filtered them out.
                  const _renderAssetTA = (() => {
                    const a = (assets || []).find(x => x.id === outreachDialog.assetId);
                    return (a && a.therapeutic_area) || '';
                  })();
                  const relReasons = info.exclusionReasons || [];
                  const hasRelExclusion = relReasons.length > 0;
                  const reasonLabelShort = {
                    already_contacted_this_asset: 'Emailed already',
                    already_contacted_other_asset: 'Prior asset email',
                    replied_historically: 'Has replied',
                    meeting_logged: 'Meeting logged',
                    dnc_flagged: 'DNC',
                    commercialization_partner: 'Distributor',
                    my_company: 'My Company',
                    manually_excluded: 'Excluded',
                    client_already_in_touch: 'Biotech in touch',
                    not_buyer_eligible: 'Handshake OFF',
                  };
                  // Sep 2026 · companies already emailed for THIS asset render
                  // as a compact greyed-out row (no body, no Send to All) so a
                  // campaign re-run isn't confusing. "Show anyway" expands it.
                  const sentCount = info.contacts.filter(c => info.sent?.includes(c.email)).length;
                  const priorSentCount = info.contacts.filter(c => (info.priorSent || []).includes(c.email)).length;
                  const sentThisSession = sentCount - priorSentCount;
                  // Collapse ONLY companies emailed in an earlier session. Anything sent
                  // during this dialog (manual or server queue) stays visible, in place,
                  // with green Sent badges — user report (HB0056 / Shanghai Huaota, Sep 2026).
                  const emailedAlready = sentThisSession <= 0 && (relReasons.includes('already_contacted_this_asset') || priorSentCount > 0);
                  if (emailedAlready && !expandedEmailed.has(targetName)) {
                    return (
                      <div key={targetName} className={`border rounded-lg min-w-0 max-w-full opacity-60 border-slate-200 bg-slate-100/70 scroll-mt-3 ${jumpHighlight === targetName ? '!border-sky-500 !bg-sky-50 ring-2 ring-sky-300 opacity-100' : ''}`} data-testid={`outreach-company-${targetName}`}>
                        <div className="px-4 py-2.5 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <input type="checkbox" checked={false}
                              onChange={() => { setExpandedEmailed(prev => new Set(prev).add(targetName)); setSelectedCompanies(prev => new Set(prev).add(targetName)); }}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                              data-testid={`select-company-${targetName}`} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-xs font-semibold text-slate-500 line-through decoration-slate-400"><span className="tabular-nums text-slate-400 font-medium mr-1.5 no-underline" data-testid={`company-card-num-${targetName}`}>{cardIdx + 1}.</span>{targetName}</p>
                                <Badge className="bg-slate-200 text-slate-600 border border-slate-300 text-[9px] px-1.5 py-0" data-testid={`emailed-already-chip-${targetName}`}>Emailed already</Badge>
                              </div>
                              <p className="text-[10px] text-slate-500">
                                {sentCount > 0 ? `${sentCount} of ${info.contacts.length} contact${info.contacts.length !== 1 ? 's' : ''} already sent this asset` : 'This company has already received outreach for this asset'}{info.country && ` · ${info.country}`}
                              </p>
                            </div>
                          </div>
                          <button type="button"
                            onClick={() => setExpandedEmailed(prev => new Set(prev).add(targetName))}
                            className="shrink-0 text-[11px] font-medium text-slate-500 hover:text-sky-700 underline underline-offset-2"
                            data-testid={`show-emailed-${targetName}`}>
                            Show anyway
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={targetName} className={`border rounded-lg overflow-hidden min-w-0 max-w-full transition-colors scroll-mt-3 ${jumpHighlight === targetName ? '!border-sky-500 !bg-sky-50 ring-2 ring-sky-300' : ''} ${info.signalAudit?.verdict === 'conflict' && !info.reinstated ? 'opacity-90 border-rose-300 bg-rose-50/20' : info.softReason && !info.reinstated ? 'opacity-90 border-amber-300 bg-amber-50/20' : !selectedCompanies.has(targetName) ? 'opacity-50 border-slate-100 bg-slate-50/30' : allSent ? 'border-green-200 bg-green-50/30' : policy ? 'border-amber-300 bg-amber-50/20' : alreadyContacted ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200'}`} data-testid={`outreach-company-${targetName}`}>
                      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <input type="checkbox"
                            checked={selectedCompanies.has(targetName)}
                            onChange={e => setSelectedCompanies(prev => {
                              const next = new Set(prev);
                              e.target.checked ? next.add(targetName) : next.delete(targetName);
                              return next;
                            })}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                            data-testid={`select-company-${targetName}`}
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-xs font-semibold text-slate-900"><span className="tabular-nums text-slate-400 font-medium mr-1.5" data-testid={`company-card-num-${targetName}`}>{cardIdx + 1}.</span>{targetName}</p>
                              {isPortalOnly && <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[9px] px-1.5 py-0" title={policy.note || ''}>🌐 Portal Only</Badge>}
                              {policy && policy.mode === 'single_poc' && <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[9px] px-1.5 py-0" title={policy.note || ''}>🔒 Single POC</Badge>}
                              {alreadyContacted && <Badge className="bg-amber-100 text-amber-700 text-[9px] px-1.5 py-0">Previously Contacted</Badge>}
                              {info.reinstated && <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[9px] px-1.5 py-0" title={info.reinstatedTier ? `Tier: ${info.reinstatedTier}` : ''} data-testid={`reinstated-chip-${targetName}`}>Manually reinstated{info.reinstatedTier ? ` · ${info.reinstatedTier}` : ''}</Badge>}
                              {info.softReason && !info.reinstated && (
                                <>
                                  <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[9px] px-1.5 py-0" title="The AI was unsure — reinstate with a rationale to include it in outreach." data-testid={`needs-call-chip-${targetName}`}>Needs your call · {info.signalAudit?.verdict === 'conflict' ? 'logged signal conflict' : info.softReason}</Badge>
                                  <button type="button" onClick={() => openReinstateFor(targetName, info)} className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 hover:underline" data-testid={`reinstate-btn-${targetName}`}>Reinstate</button>
                                </>
                              )}
                              {hasRelExclusion && relReasons.map(r => (
                                <Badge key={r} className="bg-rose-100 text-rose-700 border border-rose-200 text-[9px] px-1.5 py-0" title={(info.exclusionDetails || {})[r] || ''} data-testid={`exclusion-chip-${targetName}-${r}`}>
                                  {reasonLabelShort[r] || r}
                                </Badge>
                              ))}
                            </div>
                            <p className="text-[10px] text-slate-500">{info.contacts.length} contact{info.contacts.length !== 1 ? 's' : ''} {info.country && `· ${info.country}`}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isPortalOnly ? (
                            <a
                              href={policy.portal_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-7 inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium px-3 rounded-md transition-colors"
                              data-testid={`portal-submit-btn-${targetName}`}
                            >
                              🌐 Submit via {policy.portal_name || 'Portal'}
                            </a>
                          ) : allSent ? (
                            <Badge className="bg-green-100 text-green-700 text-[10px]" data-testid={`all-sent-badge-${targetName}`}>✓ All Sent ({sentCount}/{info.contacts.length})</Badge>
                          ) : selectedCompanies.has(targetName) ? (
                            <>
                              {sentCount > 0 && (
                                <Badge className="bg-green-100 text-green-700 text-[10px]" data-testid={`partial-sent-badge-${targetName}`}>✓ Sent {sentCount}/{info.contacts.length}</Badge>
                              )}
                              {!info.email ? (
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => generateCompanyEmail(targetName)} disabled={info.generating}>
                                  {info.generating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Brain className="w-3 h-3 mr-1" />} Generate Email
                                </Button>
                              ) : (
                                <Button size="sm" className="h-7 text-xs bg-sky-600 text-white hover:bg-sky-700" onClick={() => sendAllForCompanyWithResume(targetName)} disabled={sentHistoryFailed} title={sentHistoryFailed ? 'Sent history could not be loaded — sending locked' : undefined}>
                                  <Mail className="w-3 h-3 mr-1" /> {policy && policy.mode === 'single_poc' ? 'Send to POC' : 'Send to All'}
                                </Button>
                              )}
                              {/* Feb 2026 v18 · surface per-row rationale-gate rejection */}
                              {!info.email && info.error && (
                                <div className="mt-1 text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 max-w-md space-y-1" data-testid={`email-gate-error-${targetName}`}>
                                  <div><span className="font-semibold">Blocked: </span>{info.error}</div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {outreachDialog.mappingId && (
                                      <button
                                        type="button"
                                        onClick={() => openReinstateFor(targetName, info)}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700"
                                        data-testid={`override-generate-btn-${targetName}`}
                                        disabled={info.generating}
                                      >
                                        {info.generating ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Zap className="w-2.5 h-2.5" />}
                                        Reinstate &amp; Generate
                                      </button>
                                    )}
                                    {outreachDialog.mappingId && (
                                      <button
                                        type="button"
                                        onClick={() => openDecisionSummary(outreachDialog.mappingId, targetName)}
                                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-800 hover:text-rose-900 underline underline-offset-2"
                                        data-testid={`why-btn-${targetName}`}
                                      >
                                        🧠 Why? · View AI Decision Summary
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          ) : null}
                        </div>
                      </div>
                      {!info.reinstated && (
                        <HeldBackBanner companyName={targetName} softReason={info.softReason || (info.hardRefusal ? `refused — ${info.hardRefusal}` : '')} signalAudit={info.signalAudit} profileId={info.profileId} onReinstate={() => openReinstateFor(targetName, info)} />
                      )}
                      {/* BD Routing Rule banner — surfaced when a per-TA
                          routing rule fired for this pharma/asset combo.
                          Shows who replaced whom + the source sender so the
                          BD lead can defend the action ("Per Dov's May 2026
                          email, Immunology goes to Kalpana"). */}
                      {info.bdRoutingRule && (
                        <div className="px-4 py-2 bg-violet-50 border-b border-violet-200 text-[11px] text-violet-900" data-testid={`bd-routing-banner-${targetName}`}>
                          <div className="flex items-start gap-1.5">
                            <Zap className="w-3.5 h-3.5 text-violet-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <div>
                                <span className="font-semibold">BD Routing Rule active:</span>{' '}
                                {(info.bdRoutingRule.therapeutic_areas || []).join(', ') || 'this TA'} routes {(info.bdRoutingRule.mode || 'exclusive') === 'additive' ? 'to' : 'ONLY to'}{' '}
                                {(() => {
                                  const rt = info.bdRoutingRule.route_to;
                                  const list = Array.isArray(rt) ? rt : (rt && (rt.name || rt.email) ? [rt] : []);
                                  if (list.length === 0) return <span className="font-semibold">designated contact</span>;
                                  return list.map((p, i) => (
                                    <React.Fragment key={i}>
                                      {i > 0 && (i === list.length - 1 ? ' and ' : ', ')}
                                      <span className="font-semibold">{p.name || p.email}</span>
                                      {p.designation && <span className="text-violet-700"> ({p.designation})</span>}
                                    </React.Fragment>
                                  ));
                                })()}
                                {(info.bdRoutingRule.mode || 'exclusive') === 'additive' && (
                                  <span className="text-emerald-700 font-medium"> + contacts with a matching stated preference</span>
                                )}
                                {(info.bdRoutingSuppressed || []).length > 0 && (
                                  <>
                                    {' — '}
                                    <span className="font-medium">{info.bdRoutingSuppressed.length} other contact{info.bdRoutingSuppressed.length > 1 ? 's' : ''} hidden</span>
                                    <span className="text-violet-700"> ({info.bdRoutingSuppressed.map(b => b.name || b.email).filter(Boolean).join(', ')})</span>
                                  </>
                                )}
                                .
                              </div>
                              {(info.bdRoutingRule.source_sender_name || info.bdRoutingRule.source_sender_email || info.bdRoutingRule.source_sender_date) && (
                                <div className="italic text-violet-700 mt-0.5">
                                  Per {info.bdRoutingRule.source_sender_name || info.bdRoutingRule.source_sender_email}
                                  {info.bdRoutingRule.source_sender_date && ` (${info.bdRoutingRule.source_sender_date})`}
                                </div>
                              )}
                              {info.bdRoutingRule.note && (
                                <div className="italic text-violet-700 mt-0.5">“{info.bdRoutingRule.note}”</div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {isPortalOnly && (
                        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900" data-testid={`portal-banner-${targetName}`}>
                          <div className="flex items-start gap-1.5">
                            <span>🌐</span>
                            <div className="flex-1">
                              <div>
                                <span className="font-semibold">Portal Submission Only:</span> {targetName} does not accept BD email.
                                Submit via{' '}
                                <a href={policy.portal_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-sky-700 underline break-all">
                                  {policy.portal_name || policy.portal_url}
                                </a>.
                                {policy.poc_name && <span className="text-amber-700"> Informed by {policy.poc_name}.</span>}
                              </div>
                              {policy.note && <div className="italic text-amber-700 mt-0.5">“{policy.note}”</div>}
                            </div>
                          </div>
                        </div>
                      )}
                      {policy && policy.mode === 'single_poc' && (
                        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900" data-testid={`routing-policy-banner-${targetName}`}>
                          <span className="font-semibold">🔒 BD Routing Policy:</span>{' '}
                          {targetName} routes all partnering outreach to <span className="font-semibold">{policy.poc_name || policy.poc_email}</span> ({policy.poc_email}) only.
                          {' '}Other contacts are disabled.
                          {policy.note && <span className="italic text-amber-700"> — “{policy.note}”</span>}
                        </div>
                      )}
                      {info.email && (
                        <div className="px-4 py-3 border-b border-slate-100">
                          <textarea
                            className="w-full max-w-full text-xs text-slate-700 font-mono bg-white border border-slate-200 rounded-md p-3 resize-y leading-relaxed focus:outline-none focus:ring-1 focus:ring-sky-300"
                            rows={8}
                            value={info.email}
                            onChange={e => setOutreachEmails(prev => ({ ...prev, [targetName]: { ...prev[targetName], email: e.target.value } }))}
                            data-testid={`email-editor-${targetName}`}
                          />
                          <div className="flex justify-between items-center mt-1.5 gap-2 flex-wrap">
                            <SpamCheckChip
                              subject={splitEmail(info.email).subject}
                              bodyHtml={splitEmail(info.email).body}
                              onChange={(s, b) => {
                                const joined = joinEmail(s, b);
                                setOutreachEmails(prev => ({
                                  ...prev,
                                  [targetName]: { ...prev[targetName], email: joined },
                                }));
                                axios.put(`${API}/ai/email-drafts`, { biotech_company_id: outreachDialog.companyId, asset_id: outreachDialog.assetId || '', target_company_name: targetName, email: joined }, { withCredentials: true }).catch(() => {});
                              }}
                              testId={`spamcheck-${targetName}`}
                            />
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="ghost" className="h-6 text-[10px] text-slate-500" onClick={() => generateCompanyEmail(targetName)} disabled={info.generating} data-testid={`regenerate-email-btn-${targetName}`}>
                                {info.generating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Brain className="w-3 h-3 mr-1" />} Regenerate
                              </Button>
                              <Button size="sm" variant="ghost" className="h-6 text-[10px] text-slate-500" onClick={async () => {
                                const lines = info.email.split('\n');
                                const subjectLine = lines.find(l => l.toLowerCase().startsWith('subject:'));
                                const subject = subjectLine ? subjectLine.replace(/^subject:\s*/i, '').trim() : 'Test Email';
                                const body = lines.filter(l => !l.toLowerCase().startsWith('subject:')).join('\n').trim();
                                try {
                                  await axios.post(`${API}/ai/send-test-email`, { subject, email_body: body }, { withCredentials: true });
                                  toast.success('Test email sent to amit.dang@foresightrx.com');
                                } catch { toast.error('Failed to send test'); }
                              }} data-testid={`test-email-btn-${targetName}`}>
                                Send test to self
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="divide-y divide-slate-100">
                        {info.contacts.map((ct, ci) => {
                          const isSent = info.sent?.includes(ct.email);
                          const isSending = sendingEmails[`${targetName}_${ct.email}`];
                          const sendError = sendErrors[`${targetName}_${ct.email}`];
                          const isPocMatch = policy && policy.mode === 'single_poc' && pocEmail && (ct.email || '').toLowerCase() === pocEmail;
                          const blockedByPolicy = !!policy && policy.mode === 'single_poc' && !isPocMatch;
                          const blockedByPortal = !!policy && policy.mode === 'portal_only';
                          const dncEntry = dncContactEmails[(ct.email || '').trim().toLowerCase()];
                          const blockedByDnc = !!dncEntry;
                          const blockedByStage = !!ct.__stage_gate;
                          const blocked = blockedByPolicy || blockedByPortal || blockedByDnc || blockedByStage;
                          const hardDomainBlock = !!sendError && !isSent && /Email not sent\. (If .* is part of|Fix the contact's company)/i.test(sendError);
                          return (
                            <div key={ci} className={`px-4 py-2 flex items-center justify-between ${blocked ? 'bg-slate-50/80 opacity-60' : 'hover:bg-slate-50/50'}`} data-testid={`contact-row-${targetName}-${ct.email}`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className={`text-xs font-medium ${blocked ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{ct.name}</p>
                                  {isPocMatch && <Badge className="bg-amber-100 text-amber-800 text-[9px] px-1.5 py-0">Designated POC</Badge>}
                                  {ct.__specialist && !blocked && <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] px-1.5 py-0" title={`Tagged ${(ct.interest_areas || []).slice(0, 3).join(', ')} — stated interest matches this asset`} data-testid={`specialist-badge-${ct.email}`}>★ Tagged {(ct.interest_areas || []).slice(0, 2).join(', ')}</Badge>}
                                  {!ct.__specialist && !blocked && !ct.__commercial_partner && !(Array.isArray(ct.interest_areas) && ct.interest_areas.length) && <Badge className="bg-slate-100 text-slate-500 text-[9px] px-1.5 py-0" title="No stated preferences yet — receives until they tell us their remit" data-testid={`no-preference-badge-${ct.email}`}>No stated preference</Badge>}
                                  {blockedByPolicy && <Badge className="bg-slate-200 text-slate-500 text-[9px] px-1.5 py-0" title={`Routing locked to ${policy.poc_name || policy.poc_email}`}>🔒 Blocked</Badge>}
                                  {blockedByPortal && <Badge className="bg-slate-200 text-slate-500 text-[9px] px-1.5 py-0" title="Portal-only submission">🌐 Portal Only</Badge>}
                                  {blockedByDnc && <Badge className="bg-rose-100 text-rose-700 text-[9px] px-1.5 py-0" title={dncEntry.reason ? `DNC: ${dncEntry.reason}` : 'Contact is flagged Do-Not-Contact'} data-testid={`dnc-badge-${ct.email}`}>🚫 DNC</Badge>}
                                  {blockedByStage && <Badge className="bg-indigo-100 text-indigo-700 text-[9px] px-1.5 py-0" title={`Only accepts ${ct.__stage_gate.join(', ')} assets — this asset is ${ct.__asset_stage || 'earlier stage'}. Hard gate; edit the contact's Preferred Stages to change.`} data-testid={`stage-gate-badge-${ct.email}`}>⛔ Accepts {ct.__stage_gate.join('/')} only</Badge>}
                                </div>
                                <p className={`text-[10px] break-words ${blocked ? 'text-slate-400' : 'text-slate-500'}`}>{ct.designation || ''} · {ct.email}</p>
                                {sendError && !isSent && !/designated specialist|routing rule|stated interests/i.test(sendError) && (
                                  <p className="text-[10px] text-rose-600 mt-0.5" data-testid={`send-error-${ct.email}`}>✕ Not sent — {sendError}</p>
                                )}
                                {sendError && !isSent && /designated specialist|routing rule|stated interests/i.test(sendError) && (
                                  <p className="text-[10px] text-slate-400 mt-0.5" data-testid={`send-skipped-${ct.email}`}>Skipped by routing — not a designated specialist for this asset</p>
                                )}
                              </div>
                              <div className="shrink-0 ml-3">
                                {isSent ? (
                                  <Badge className="bg-green-100 text-green-700 text-[10px]">Sent</Badge>
                                ) : sendError && /designated specialist|routing rule|stated interests/i.test(sendError) ? (
                                  <span className="text-[10px] text-slate-400 italic" data-testid={`blocked-routing-${ct.email}`}>Routing — skipped</span>
                                ) : hardDomainBlock ? (
                                  <span className="text-[10px] text-rose-500 italic whitespace-nowrap" title="Recipient's company/domain does not belong to this target. Fix the contact's company or corporate group — retrying will fail again." data-testid={`blocked-domain-${ct.email}`}>Blocked — wrong company domain</span>
                                ) : sendError && !blocked && info.email ? (
                                  <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 bg-rose-600 text-white border-rose-600 hover:bg-rose-700 hover:text-white" onClick={() => sendEmailToContact(targetName, ct)} disabled={isSending} data-testid={`retry-send-${ct.email}`}>
                                    {isSending ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Mail className="w-2.5 h-2.5 mr-1" />} Retry
                                  </Button>
                                ) : blockedByDnc ? (
                                  <span className="text-[10px] text-rose-500 italic" data-testid={`blocked-dnc-${ct.email}`}>DNC — skipped</span>
                                ) : blockedByStage ? (
                                  <span className="text-[10px] text-indigo-500 italic" data-testid={`blocked-stage-${ct.email}`}>Stage gate — skipped</span>
                                ) : blockedByPortal ? (
                                  <span className="text-[10px] text-slate-400 italic" data-testid={`blocked-portal-${ct.email}`}>Use portal</span>
                                ) : blockedByPolicy ? (
                                  <span className="text-[10px] text-slate-400 italic" data-testid={`blocked-by-policy-${ct.email}`}>Route via POC</span>
                                ) : info.email ? (
                                  <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => sendEmailToContact(targetName, ct)} disabled={isSending || sentHistoryFailed} data-testid={`send-contact-${ct.email}`}>
                                    {isSending ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Mail className="w-2.5 h-2.5 mr-1" />} Send
                                  </Button>
                                ) : (
                                  <span className="text-[10px] text-slate-400">Generate email first</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Stated-interests filter — show contacts hidden because their
                          shared preferences don't include this asset's TA. Folded by
                          default; click to reveal with per-row "Send anyway" override. */}
                      {(() => {
                        const gap = (coverageGaps || []).find(g => (g.pharma_company_name || '').trim().toLowerCase() === targetName.trim().toLowerCase() && g.asset_id === outreachDialog?.assetId);
                        if (!gap) return null;
                        return (
                          <div className="px-4 py-2 bg-rose-50 border-t border-rose-200 text-[11px] text-rose-900 flex items-center justify-between gap-3" data-testid={`coverage-gap-${targetName}`}>
                            <span><b>Coverage gap</b> — the only person this asset went to, {gap.contact_name}, {gap.status === 'left_company' ? 'has left the company' : 'bounced'}{gap.interest_areas?.length ? ` (was covering ${gap.interest_areas.slice(0, 2).join(', ')})` : ''}. Nobody at {targetName} has received it — the specialist lock is released, colleagues below are reachable.</span>
                            <button type="button" className="shrink-0 underline underline-offset-2 hover:text-rose-700" data-testid={`coverage-gap-resolve-${targetName}`}
                              onClick={async () => { try { await axios.post(`${API}/coverage-gaps/${gap.id}/resolve`, {}, { withCredentials: true }); setCoverageGaps(gs => gs.filter(x => x.id !== gap.id)); } catch {} }}>
                              Mark handled
                            </button>
                          </div>
                        );
                      })()}
                      {(info.hiddenByInterest || []).length > 0 && (
                        <details className="group bg-amber-50/40 border-t border-amber-100" data-testid={`hidden-by-interest-${targetName}`}>
                          <summary className="px-4 py-2 cursor-pointer flex items-center justify-between text-[11px] text-amber-800 hover:bg-amber-50">
                            <span className="flex items-center gap-1.5">
                              <ShieldOff className="w-3 h-3" />
                              <span className="font-medium">{info.hiddenByInterest.length}</span>
                              {(() => {
                                const anySpec = (info.hiddenByInterest || []).some(c => c.__specialist_routing);
                                return anySpec ? (
                                  <span>
                                    {' '}contact{info.hiddenByInterest.length === 1 ? '' : 's'} hidden — {targetName} routes{' '}
                                    <span className="font-medium">{_renderAssetTA || 'this asset'}</span>
                                    {' '}to its {(info.contacts || []).length} designated specialist{(info.contacts || []).length === 1 ? '' : 's'}
                                    {(() => { const tags = [...new Set((info.contacts || []).flatMap(c => c.interest_areas || []))].slice(0, 3); return tags.length ? <> (tagged <span className="font-medium">{tags.join(', ')}</span>)</> : null; })()}
                                    : <span className="font-medium">{(info.contacts || []).map(c => c.name || c.email).filter(Boolean).join(', ')}</span>.
                                    {' '}Nobody else at {targetName} is emailed.
                                  </span>
                                ) : (
                                  <span>
                                    {' '}contact{info.hiddenByInterest.length === 1 ? '' : 's'} hidden — stated interests don&apos;t include{' '}
                                    <span className="font-medium">{_renderAssetTA || 'this TA'}</span>
                                  </span>
                                );
                              })()}
                            </span>
                            <span className="text-amber-600/80 group-open:rotate-180 transition-transform">
                              <ChevronDown className="w-3 h-3" />
                            </span>
                          </summary>
                          <div className="divide-y divide-amber-100/60">
                            {info.hiddenByInterest.map((ct, ci) => {
                              const isSent = info.sent?.includes(ct.email);
                              const isSending = sendingEmails[`${targetName}_${ct.email}`];
                              const isSpecialistBlock = !!ct.__specialist_routing;
                              const blockLabelSpecialists = isSpecialistBlock
                                ? (ct.__specialists || []).map(s => s.name || s.email).filter(Boolean).join(', ')
                                : '';
                              return (
                                <div key={`hidden-${ci}`} className="px-4 py-2 flex items-center justify-between hover:bg-amber-50/60" data-testid={`hidden-row-${targetName}-${ct.email}`}>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <p className="text-xs font-medium text-slate-700">{ct.name}</p>
                                      {ct.__unreachable ? (
                                        <Badge className="bg-rose-100 text-rose-800 text-[9px] px-1.5 py-0" title={ct.bounce_reason || ''} data-testid={`unreachable-${targetName}-${ct.email}`}>
                                          {ct.__unreachable === 'left_company' ? 'Left company' : 'Bounced'}{(ct.__stated_interests || []).length ? ` · was covering ${(ct.__stated_interests || []).slice(0, 2).join(', ')}` : ''}
                                        </Badge>
                                      ) : isSpecialistBlock ? (
                                        <Badge className="bg-amber-100 text-amber-800 text-[9px] px-1.5 py-0" title={`Not a designated specialist. Designated: ${blockLabelSpecialists}`}>
                                          Skipped — {blockLabelSpecialists} cover{(ct.__specialists || []).length === 1 ? 's' : ''} {_renderAssetTA || 'this TA'}{(ct.__stated_interests || []).length ? ` · tagged ${(ct.__stated_interests || []).slice(0, 3).join(', ')}` : ' · no stated preferences'}
                                        </Badge>
                                      ) : ct.__excluded_ta ? (
                                        <Badge className="bg-rose-100 text-rose-800 text-[9px] px-1.5 py-0" title={`Recorded as outside their remit: ${(ct.excluded_therapeutic_areas || []).join(', ')}`}>
                                          Excludes: {(ct.excluded_therapeutic_areas || []).slice(0, 3).join(', ')}
                                        </Badge>
                                      ) : (
                                        <Badge className="bg-amber-100 text-amber-800 text-[9px] px-1.5 py-0" title={`Stated interests: ${(ct.__stated_interests || []).join(', ')}`}>
                                          Prefers: {summarizeInterests(ct.__stated_interests)}
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-[10px] text-slate-500">{ct.designation || ''} · {ct.email}</p>
                                  </div>
                                  <div className="shrink-0 ml-3">
                                    {isSent ? (
                                      <Badge className="bg-green-100 text-green-700 text-[10px]">Sent</Badge>
                                    ) : info.email ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-6 text-[10px] px-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                                        disabled={isSending}
                                        data-testid={`send-anyway-${targetName}-${ct.email}`}
                                        onClick={() => {
                                          const msg = isSpecialistBlock
                                            ? `Send anyway to ${ct.name}?\n\nThis pharma has designated specialists for ${_renderAssetTA || 'this TA'}: ${blockLabelSpecialists}.`
                                            : `Send anyway to ${ct.name}?\n\nTheir stated interests (${(ct.__stated_interests || []).join(', ')}) don't include ${_renderAssetTA || 'this TA'}.`;
                                          if (window.confirm(msg)) {
                                            sendEmailToContact(targetName, {
                                              ...ct,
                                              __override_interest_mismatch: !isSpecialistBlock || true,
                                              __override_specialist_routing: isSpecialistBlock,
                                            });
                                          }
                                        }}
                                      >
                                        {isSending ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <AlertTriangle className="w-2.5 h-2.5 mr-1" />} Send anyway
                                      </Button>
                                    ) : (
                                      <span className="text-[10px] text-slate-400">Generate email first</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Mark Outreach Complete footer — only shows after at least one email sent */}
              {(() => {
                const totalSent = Object.values(outreachEmails).reduce((sum, info) => sum + (info.sent?.length || 0), 0);
                const eKey = outreachDialog.assetId || outreachDialog.companyId;
                const latestMapping = (mappingsByKey[eKey] || [])[0];
                const pharmaStatus = latestMapping?.email_sent_pharma;
                const isComplete = pharmaStatus === 'complete' || pharmaStatus === true;
                const isInProgress = pharmaStatus === 'in_progress';
                if (!latestMapping) return null;
                return (
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200" data-testid="outreach-complete-footer">
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-slate-500">
                        {totalSent} email{totalSent !== 1 ? 's' : ''} sent for this asset (incl. earlier sessions)
                      </p>
                      {isComplete && <Badge className="bg-green-100 text-green-700 text-[9px]">Outreach Complete</Badge>}
                      {isInProgress && !isComplete && <Badge className="bg-amber-100 text-amber-700 text-[9px]">In Progress</Badge>}
                    </div>
                    {!isComplete && latestMapping && (
                      <Button
                        size="sm"
                        className="h-8 text-xs bg-green-600 text-white hover:bg-green-700"
                        data-testid="mark-pharma-outreach-complete-btn"
                        onClick={async () => {
                          try {
                            await axios.patch(`${API}/ai/map-targets/${latestMapping.id}/email-status`, { email_type: 'pharma', status: 'complete' }, { withCredentials: true });
                            setMappings(prev => prev.map(m => m.id === latestMapping.id ? { ...m, email_sent_pharma: 'complete' } : m));
                            toast.success('Pharma outreach marked as complete');
                            setOutreachDialog(p => ({ ...p, open: false }));
                          } catch {
                            toast.error('Failed to update status');
                          }
                        }}
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Mark Outreach Complete
                      </Button>
                    )}
                    {isComplete && (
                      <Button
                        size="sm"
                        className="h-8 text-xs bg-green-600 text-white hover:bg-green-700"
                        data-testid="pharma-outreach-done-btn"
                        onClick={() => setOutreachDialog(p => ({ ...p, open: false }))}
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Done
                      </Button>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* v33 (Feb 2026) — Explainable-AI Decision Summary drawer.
          Fetches from /api/ai/mapping/{id}/target/{name}/decision-summary
          (no fresh LLM call — assembled from row data). Same drawer for
          High / Medium / Low / Excluded rows. */}
      <Dialog open={decisionSummary.open} onOpenChange={open => { if (!open) setDecisionSummary(p => ({ ...p, open: false })); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="decision-summary-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Brain className="w-5 h-5 text-indigo-600" />
              AI Decision Summary — {decisionSummary.companyName}
            </DialogTitle>
          </DialogHeader>
          {decisionSummary.loading && (
            <div className="flex items-center gap-2 text-sm text-slate-600 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Assembling the decision…</div>
          )}
          {decisionSummary.error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
              {decisionSummary.error}
            </div>
          )}
          {decisionSummary.data && (() => {
            const d = decisionSummary.data;
            const scorePct = Math.max(0, Math.min(100, d.score || 0));
            const scoreColor = scorePct >= 75 ? 'bg-emerald-500' : scorePct >= 50 ? 'bg-amber-500' : 'bg-rose-500';
            return (
              <div className="space-y-4">
                {/* Header — overall decision + score */}
                <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Overall Decision</div>
                    <div className="text-lg font-bold text-slate-900">{d.overall_decision_label}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-semibold uppercase text-slate-500">Score</div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-slate-900">{d.score}</span>
                      <span className="text-xs text-slate-500">/ {d.score_max}</span>
                    </div>
                    <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden mt-1">
                      <div className={`h-full ${scoreColor}`} style={{ width: `${scorePct}%` }} />
                    </div>
                  </div>
                </div>

                {/* Primary reasons */}
                {(d.primary_reasons || []).length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      {d.excluded ? 'Primary Reason(s) for Exclusion' : 'Key Factors'}
                    </div>
                    <ul className="space-y-1.5">
                      {d.primary_reasons.map((r, i) => (
                        <li key={i} className="text-[12px] text-slate-800 flex items-start gap-1.5">
                          <span className="text-rose-500 shrink-0 mt-0.5">❌</span>
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Positive signals */}
                {(d.positive_signals || []).length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Positive Signals Found</div>
                    <ul className="space-y-1.5">
                      {d.positive_signals.map((s, i) => (
                        <li key={i} className="text-[12px] text-slate-800 flex items-start gap-1.5">
                          <span className="text-emerald-500 shrink-0 mt-0.5">✓</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Missing evidence */}
                {(d.missing_evidence || []).length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Missing Evidence</div>
                    <ul className="space-y-1.5">
                      {d.missing_evidence.map((m, i) => (
                        <li key={i} className="text-[12px] text-slate-600 flex items-start gap-1.5">
                          <span className="text-slate-400 shrink-0">◦</span>
                          <span>{m}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* AI recommendation */}
                <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700 mb-0.5">AI Recommendation</div>
                  <div className="text-sm text-indigo-900 font-medium">{d.recommendation}</div>
                </div>

                {/* Structured rationale (collapsible) */}
                {Object.keys(d.structured_rationale || {}).length > 0 && (
                  <details className="border border-slate-200 rounded-lg" data-testid="decision-summary-rationale">
                    <summary className="cursor-pointer px-3 py-2 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 select-none">
                      View Full AI Rationale (5-question)
                    </summary>
                    <div className="p-3 space-y-2 border-t border-slate-200">
                      {Object.entries(d.structured_rationale).map(([k, v]) => (
                        <div key={k}>
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{k.replace(/_/g, ' ')}</div>
                          <div className="text-[12px] text-slate-800">{typeof v === 'string' ? v : JSON.stringify(v)}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
                  {d.excluded && (
                    <Button
                      size="sm"
                      className="h-8 text-xs bg-emerald-600 text-white hover:bg-emerald-700"
                      onClick={submitOverride}
                      data-testid="decision-summary-override-btn"
                    >
                      Reinstate & Generate Email
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    onClick={() => setDecisionSummary(p => ({ ...p, feedbackOpen: !p.feedbackOpen }))}
                    data-testid="decision-summary-feedback-btn"
                  >
                    Report Incorrect {d.excluded ? 'Exclusion' : 'Decision'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs text-slate-600"
                    onClick={() => setDecisionSummary(p => ({ ...p, open: false }))}
                  >
                    Close
                  </Button>
                </div>

                {decisionSummary.feedbackOpen && (
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
                    <div className="text-[11px] font-semibold text-slate-700">What did the AI get wrong?</div>
                    <textarea
                      value={decisionSummary.feedbackComment}
                      onChange={e => setDecisionSummary(p => ({ ...p, feedbackComment: e.target.value }))}
                      placeholder="e.g., They actually have a wound-care franchise via their Ethicon business — the mapping missed it."
                      className="w-full text-[12px] p-2 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 min-h-[80px]"
                      data-testid="decision-summary-feedback-textarea"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="h-7 text-[11px] bg-indigo-600 text-white hover:bg-indigo-700"
                        onClick={submitFeedback}
                        disabled={decisionSummary.feedbackSubmitting || !decisionSummary.feedbackComment.trim()}
                        data-testid="decision-summary-submit-feedback"
                      >
                        {decisionSummary.feedbackSubmitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                        Submit Feedback
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-[11px]"
                        onClick={() => setDecisionSummary(p => ({ ...p, feedbackOpen: false, feedbackComment: '' }))}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Unified Investor Outreach Dialog: Auto-match → Email → Send */}
      <Dialog open={investorDialog.open} onOpenChange={open => {
        if (!open) {
          setInvestorDialog(p => ({ ...p, open: false }));
          if (investorPollRef.current) { clearInterval(investorPollRef.current); investorPollRef.current = null; }
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="investor-outreach-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <DollarSign className="w-5 h-5 text-amber-600" />
              Investor Outreach — {investorDialog.companyName}
              {investorDialog.assetName && <Badge variant="secondary" className="text-[10px]">{investorDialog.assetName}</Badge>}
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: AI Matching (auto-triggered) */}
          {investorMatchLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              <p className="text-sm text-slate-500">AI is matching investors from your database...</p>
              <p className="text-[10px] text-slate-400">Analyzing 1,200+ investor profiles against this asset</p>
            </div>
          ) : investorMatchResult?.investors?.length > 0 ? (
            <div className="space-y-4 mt-2">
              {/* Match summary */}
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  <span className="font-semibold text-amber-700">{investorMatchResult.total_matched}</span> investor{investorMatchResult.total_matched !== 1 ? 's' : ''} matched
                </p>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs text-slate-500" onClick={() => handleInvestorMatch(investorDialog.companyId, investorDialog.assetId)} data-testid="investor-rematch-btn">
                    <Search className="w-3 h-3 mr-1" /> Re-match
                  </Button>
                </div>
              </div>

              {/* Matched investors table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[30vh] overflow-y-auto">
                  <table className="w-full text-sm" data-testid="investor-match-table">
                    <thead className="sticky top-0">
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider w-8">#</th>
                        <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Investor</th>
                        <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                        <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Stage Pref</th>
                        <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Match Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {investorMatchResult.investors.map((inv, i) => (
                        <tr key={inv.id} className="hover:bg-slate-50/50" data-testid={`investor-match-row-${i}`}>
                          <td className="px-3 py-2 text-[10px] text-slate-400">{i + 1}</td>
                          <td className="px-3 py-2">
                            <p className="text-xs font-medium text-slate-800">{inv.name}</p>
                            {inv.email && <p className="text-[10px] text-slate-400">{inv.email}</p>}
                          </td>
                          <td className="px-3 py-2">
                            <Badge className={`text-[9px] ${inv.classification === 'pharma_partner' ? 'bg-sky-100 text-sky-700' : 'bg-violet-100 text-violet-700'}`}>
                              {inv.classification === 'pharma_partner' ? 'Pharma' : 'Investor'}
                            </Badge>
                            <p className="text-[9px] text-slate-400 mt-0.5">{inv.investor_type || '-'}</p>
                          </td>
                          <td className="px-3 py-2 text-[10px] text-slate-600">{inv.investment_stage || '-'}</td>
                          <td className="px-3 py-2 text-[10px] text-slate-500 max-w-[300px]">{inv.match_reason || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Step 2: Email generation + editing */}
              <div className="border-t border-slate-200 pt-4">
                {generatingEmail[`${investorDialog.assetId || investorDialog.companyId}_investor`] ? (
                  <div className="flex items-center justify-center py-8 gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                    <span className="text-sm text-slate-500">Generating investor email...</span>
                  </div>
                ) : investorEmailText ? (
                  <div className="space-y-3">
                    <div className="border border-amber-200 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-amber-50/50 border-b border-amber-100">
                        <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">Email to {investorMatchResult.total_matched} Matched Investors</span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-amber-600 hover:bg-amber-100 px-2"
                            onClick={() => {
                              const plain = investorEmailText.replace(/\*\*/g, '');
                              const htmlBody = investorEmailText.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br>');
                              const html = `<div style="font-family: Calibri, 'Segoe UI', Arial, sans-serif; font-size: 11pt; color: #000000; line-height: 1.6;">${htmlBody}</div>`;
                              try { navigator.clipboard.write([new ClipboardItem({ 'text/html': new Blob([html], { type: 'text/html' }), 'text/plain': new Blob([plain], { type: 'text/plain' }) })]); }
                              catch { navigator.clipboard.writeText(plain); }
                              toast.success('Copied');
                            }}
                            data-testid="investor-copy-email-btn">
                            <Copy className="w-3 h-3 mr-1" /> Copy
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-slate-500 hover:bg-slate-100 px-2"
                            onClick={() => generateInvestorEmailForDialog(investorDialog.companyId, investorDialog.assetId)}
                            disabled={generatingEmail[`${investorDialog.assetId || investorDialog.companyId}_investor`]}
                            data-testid="investor-regenerate-btn">
                            Regenerate
                          </Button>
                        </div>
                      </div>
                      <textarea
                        className="w-full text-xs text-slate-700 bg-white p-3 resize-y leading-relaxed focus:outline-none focus:ring-1 focus:ring-amber-300 border-0"
                        rows={10}
                        value={investorEmailText}
                        onChange={e => setInvestorEmailText(e.target.value)}
                        style={{ fontFamily: 'Public Sans, sans-serif' }}
                        data-testid="investor-email-editor"
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between">
                      <Button variant="ghost" size="sm" className="text-xs text-slate-500"
                        onClick={async () => {
                          const lines = investorEmailText.split('\n');
                          const subjectLine = lines.find(l => l.toLowerCase().startsWith('subject:'));
                          const subject = subjectLine ? subjectLine.replace(/^subject:\s*/i, '').trim() : 'Test Email';
                          const body = lines.filter(l => !l.toLowerCase().startsWith('subject:')).join('\n').trim();
                          try {
                            await axios.post(`${API}/ai/send-test-email`, { subject, email_body: body }, { withCredentials: true });
                            toast.success('Test email sent to amit.dang@foresightrx.com');
                          } catch { toast.error('Failed to send test'); }
                        }}
                        data-testid="investor-send-test-btn">
                        <Mail className="w-3.5 h-3.5 mr-1.5" /> Send test to self
                      </Button>

                      {!investorBulkTask || ['completed', 'cancelled', 'failed'].includes(investorBulkTask.status) ? (
                        <Button className="bg-amber-600 text-white hover:bg-amber-700 text-xs h-8"
                          onClick={sendBulkInvestorEmails}
                          data-testid="investor-send-matched-btn">
                          <Send className="w-3.5 h-3.5 mr-1.5" /> Send to {investorMatchResult.total_matched} Matched Investors
                        </Button>
                      ) : null}
                    </div>

                    {/* Progress Section */}
                    {investorBulkTask && (
                      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50 space-y-3" data-testid="investor-bulk-progress">
                        <BulkSendProgress
                          progress={{
                            status: investorBulkTask.status === 'running' ? 'running' : investorBulkTask.status,
                            sent: investorBulkTask.sent || 0,
                            total: investorBulkTask.total || 0,
                            failed: investorBulkTask.failed || 0,
                          }}
                          label="Pitching investors…"
                          testId="investor-bulk-progress-bar"
                        />

                        <div className="flex items-center justify-end gap-2">
                          {investorBulkTask.status === 'running' && (
                            <Button variant="ghost" size="sm" className="h-7 text-[10px] text-red-500 hover:bg-red-50"
                              onClick={cancelBulkSend} data-testid="investor-cancel-btn">
                              <Ban className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                          )}
                          {['completed', 'failed', 'cancelled'].includes(investorBulkTask.status) && (
                            <Button variant="default" size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => { setInvestorBulkTask(null); setInvestorDialog(p => ({ ...p, open: false })); toast.success('Done'); }}
                              data-testid="investor-done-btn">
                              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Done
                            </Button>
                          )}
                        </div>

                        {investorBulkTask.skipped > 0 && (
                          <div className="flex justify-center">
                            <div className="bg-white rounded-md px-3 py-1.5 border border-slate-200 inline-flex items-center gap-2">
                              <span className="text-sm font-bold text-sky-600">{investorBulkTask.skipped}</span>
                              <span className="text-[10px] text-slate-500">already sent</span>
                            </div>
                          </div>
                        )}

                        {investorBulkTask.error && (
                          <p className="text-xs text-red-600 bg-red-50 p-2 rounded">{investorBulkTask.error}</p>
                        )}
                      </div>
                    )}

                    {/* Email Tracking Stats */}
                    {trackingStats && investorBulkTask?.status === 'completed' && (
                      <div className="border border-slate-200 rounded-lg p-4 bg-white space-y-3" data-testid="email-tracking-stats">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Email Delivery Tracking</span>
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] text-slate-500"
                            onClick={() => fetchTrackingStats(investorBulkTask.task_id)} data-testid="refresh-tracking-btn">
                            Refresh
                          </Button>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div className="bg-green-50 rounded-md p-2 border border-green-100">
                            <p className="text-lg font-bold text-green-700">{trackingStats.delivered || 0}</p>
                            <p className="text-[10px] text-green-600">Delivered</p>
                          </div>
                          <div className="bg-blue-50 rounded-md p-2 border border-blue-100">
                            <p className="text-lg font-bold text-blue-700">{trackingStats.opened || 0}</p>
                            <p className="text-[10px] text-blue-600">Opened</p>
                          </div>
                          <div className="bg-red-50 rounded-md p-2 border border-red-100">
                            <p className="text-lg font-bold text-red-700">{trackingStats.bounced || 0}</p>
                            <p className="text-[10px] text-red-600">Bounced</p>
                          </div>
                          <div className="bg-amber-50 rounded-md p-2 border border-amber-100">
                            <p className="text-lg font-bold text-amber-700">{trackingStats.complained || 0}</p>
                            <p className="text-[10px] text-amber-600">Complaints</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Button className="bg-amber-600 text-white hover:bg-amber-700 text-xs" onClick={() => generateInvestorEmailForDialog(investorDialog.companyId, investorDialog.assetId)}
                      data-testid="investor-generate-email-btn">
                      <Brain className="w-3.5 h-3.5 mr-1.5" /> Generate Email for {investorMatchResult.total_matched} Matched Investors
                    </Button>
                    <p className="text-[10px] text-slate-400 mt-2">One email will be sent to each matched investor with [Investor Name] personalized</p>
                  </div>
                )}
              </div>
            </div>
          ) : investorMatchResult && investorMatchResult.total_matched === 0 ? (
            <div className="text-center py-12">
              <Brain className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No matching investors found for this asset profile.</p>
              <p className="text-[10px] text-slate-400 mt-1">This may mean no investors in your database align with this company's therapeutic area, stage, and modality.</p>
              <Button size="sm" variant="outline" className="mt-3 text-xs" onClick={() => handleInvestorMatch(investorDialog.companyId, investorDialog.assetId)} data-testid="investor-retry-btn">
                <Search className="w-3 h-3 mr-1" /> Try Again
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Share Mapping Dialog */}
      <Dialog open={shareDialog.open} onOpenChange={(open) => !open && setShareDialog({ open: false, mapping: null })}>
        <DialogContent className="max-w-md" data-testid="share-mapping-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Share Mapping for Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {shareDialog.mapping && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-slate-700">{shareDialog.mapping.asset_name}</p>
                <p className="text-[10px] text-slate-500">{shareDialog.mapping.target_companies?.length || 0} target companies</p>
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-slate-600">Recipient Email(s)</Label>
              <Input
                value={shareEmails}
                onChange={(e) => setShareEmails(e.target.value)}
                placeholder="email@company.com, another@company.com"
                className="text-xs mt-1"
                data-testid="share-emails-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">Separate multiple emails with commas</p>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Message (optional)</Label>
              <Textarea
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                placeholder="Please review the target companies for our upcoming outreach..."
                className="text-xs mt-1 min-h-[80px] resize-none"
                data-testid="share-message-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setShareDialog({ open: false, mapping: null })}>
                Cancel
              </Button>
              <Button variant="outline" size="sm" className="text-xs text-slate-600 border-slate-200 hover:bg-slate-50"
                disabled={sharing}
                data-testid="share-test-btn"
                onClick={async () => {
                  setSharing(true);
                  try {
                    const res = await axios.post(`${API}/ai/mappings/${shareDialog.mapping.id}/share`, {
                      recipients: [{ email: 'me' }],
                      message: shareMessage || 'This is a test email — preview of the share link.',
                      test_mode: true,
                    }, { withCredentials: true });
                    toast.success('Test email sent to your Gmail. Check your inbox.');
                    navigator.clipboard.writeText(res.data.share_url);
                    toast.info('Share link copied to clipboard');
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Failed to send test email');
                  } finally { setSharing(false); }
                }}
              >
                <Mail className="w-3 h-3 mr-1" /> Test Email
              </Button>
              <Button size="sm" className="text-xs bg-[#00709B] hover:bg-[#005a7d] text-white"
                disabled={!shareEmails.trim() || sharing}
                data-testid="share-send-btn"
                onClick={async () => {
                  setSharing(true);
                  try {
                    const recipients = shareEmails.split(',').map(e => ({ email: e.trim() })).filter(r => r.email);
                    const res = await axios.post(`${API}/ai/mappings/${shareDialog.mapping.id}/share`, {
                      recipients,
                      message: shareMessage,
                    }, { withCredentials: true });
                    toast.success(`Shared with ${recipients.length} recipient(s). Review link sent via email.`);
                    try { navigator.clipboard.writeText(res.data.share_url); toast.info('Share link also copied to clipboard'); } catch {}
                    setShareDialog({ open: false, mapping: null });
                    fetchData();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Failed to share mapping');
                  } finally { setSharing(false); }
                }}
              >
                {sharing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                {sharing ? 'Sending...' : 'Send for Review'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk-exclude dialog — paste an exclusion list from a pharma */}
      <Dialog
        open={bulkExcludeDialog.open}
        onOpenChange={(open) => { if (!open) setBulkExcludeDialog({ open: false, mapping: null }); }}
      >
        <DialogContent className="sm:max-w-xl" data-testid="bulk-exclude-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Ban className="w-4 h-4 text-rose-600" />
              Exclude companies from {bulkExcludeDialog.mapping?.asset_name || 'mapping'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <p className="text-slate-500">
              Paste the exclusion list — names are alias-resolved (Janssen → J&J, Vifor → CSL, suffix variants) and the matched targets are marked Rejected in one click. Anything that doesn't match shows up below so you can fix typos.
            </p>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Exclusion list</Label>
              <Textarea
                value={bulkExcludeNames}
                onChange={(e) => setBulkExcludeNames(e.target.value)}
                placeholder="Solventum, ConvaTec, J&J, Sino BioPharm, Fosun Pharma, China Medical Systems (CMS), Lilly Asia Ventures (LAV), Genertec Meheco Kangli, Sinomunepharma, SaniMed Science Group"
                rows={4}
                className="text-xs"
                data-testid="bulk-exclude-names-input"
              />
              <p className="text-[10px] text-slate-400">Separate with commas, newlines, or semicolons.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Note (optional)</Label>
              <Input
                value={bulkExcludeComment}
                onChange={(e) => setBulkExcludeComment(e.target.value)}
                placeholder="e.g. Excluded by Pharma X per email on 12 May"
                className="h-8 text-xs"
                data-testid="bulk-exclude-comment-input"
              />
            </div>

            {/* Persist-across-runs toggle — flips the submit from per-mapping
                bulk exclude to asset-level persistent exclusion. */}
            <label className="flex items-start gap-2 cursor-pointer rounded-md border border-slate-200 bg-slate-50/50 px-2.5 py-2" data-testid="bulk-exclude-persistent-toggle">
              <input
                type="checkbox"
                checked={bulkExcludePersistent}
                onChange={(e) => setBulkExcludePersistent(e.target.checked)}
                disabled={!bulkExcludeDialog.mapping?.asset_id}
                className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-rose-600 focus:ring-rose-500"
              />
              <div className="text-[11px] flex-1">
                <span className="font-medium text-slate-800">Save as permanent exclusion for this asset</span>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  When ticked, these names persist across re-runs of the AI mapping for this asset and
                  retroactively reject matches in every existing mapping. Untick for a one-time per-mapping exclusion.
                </p>
                {!bulkExcludeDialog.mapping?.asset_id && (
                  <p className="text-[10px] text-amber-600 mt-0.5">
                    Disabled — this mapping is biotech-wide (no specific asset). Persistent exclusions require an asset-scoped mapping.
                  </p>
                )}
              </div>
            </label>

            {/* "Already in touch with" reason — bidirectional bridge between AI Mapping's
                Exclude action and the asset's `client_excluded_pharmas` field. When ticked,
                the names are ALSO written onto the asset profile so the AI mapping suppresses
                them on future runs AND they feed the global Known Active Licensees signal. */}
            <label className="flex items-start gap-2 cursor-pointer rounded-md border border-orange-200 bg-orange-50/50 px-2.5 py-2" data-testid="bulk-exclude-already-in-touch-toggle">
              <input
                type="checkbox"
                checked={bulkExcludeAlreadyInTouch}
                onChange={(e) => setBulkExcludeAlreadyInTouch(e.target.checked)}
                disabled={!bulkExcludeDialog.mapping?.asset_id}
                className="mt-0.5 w-3.5 h-3.5 rounded border-orange-300 text-orange-600 focus:ring-orange-500"
              />
              <div className="text-[11px] flex-1">
                <span className="font-medium text-orange-900">Reason: biotech is already in touch with these pharmas</span>
                <p className="text-[10px] text-orange-700/80 mt-0.5">
                  Tick this when the biotech client told you they're already in active discussion with these pharmas directly.
                  The names are ALSO added to the asset profile (the "Biotech is already in touch with" section) — they get suppressed from future AI mapping runs for THIS asset AND feed the global Known Active Licensees signal for OTHER biotechs.
                </p>
                {!bulkExcludeDialog.mapping?.asset_id && (
                  <p className="text-[10px] text-amber-600 mt-0.5">
                    Disabled — this mapping is biotech-wide (no specific asset).
                  </p>
                )}
              </div>
            </label>

            {persistentExclusions.length > 0 && (
              <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2 space-y-1.5" data-testid="persistent-exclusions-list">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Already permanently excluded for this asset ({persistentExclusions.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {persistentExclusions.map((e, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-rose-50 text-rose-700 border border-rose-200 rounded px-1.5 py-0.5" data-testid={`persistent-exclusion-chip-${i}`}>
                      {e.name}
                      <button
                        className="hover:text-rose-900"
                        title={`Remove ${e.name}`}
                        data-testid={`persistent-exclusion-remove-${i}`}
                        onClick={async () => {
                          const aid = bulkExcludeDialog.mapping?.asset_id;
                          if (!aid) return;
                          try {
                            await axios.delete(`${API}/ai/asset-exclusions/${aid}/${encodeURIComponent(e.name)}`, { withCredentials: true });
                            setPersistentExclusions(prev => prev.filter((_, j) => j !== i));
                            toast.success(`Removed ${e.name} from permanent exclusions`);
                          } catch { toast.error('Could not remove'); }
                        }}
                      >×</button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {bulkExcludeResult && (
              <div className="border-t border-slate-100 pt-2 space-y-1.5" data-testid="bulk-exclude-result">
                {bulkExcludeResult.matched_count > 0 && (
                  <div className="bg-rose-50 border border-rose-200 rounded-md p-2">
                    <p className="text-[11px] font-semibold text-rose-800 mb-0.5">
                      Marked {bulkExcludeResult.matched_count} target{bulkExcludeResult.matched_count === 1 ? '' : 's'} as excluded:
                    </p>
                    <p className="text-[10px] text-rose-700">{bulkExcludeResult.matched.join(', ')}</p>
                  </div>
                )}
                {bulkExcludeResult.not_found_count > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-2">
                    <p className="text-[11px] font-semibold text-amber-800 mb-0.5">
                      {bulkExcludeResult.not_found_count} name{bulkExcludeResult.not_found_count === 1 ? ' was' : 's were'} not in this mapping:
                    </p>
                    <p className="text-[10px] text-amber-700">{bulkExcludeResult.not_found.join(', ')}</p>
                    <p className="text-[9px] text-amber-600 mt-1">These could be typos or genuinely outside the AI's target list. No action taken on them.</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setBulkExcludeDialog({ open: false, mapping: null })} disabled={bulkExcludeRunning}>
                Close
              </Button>
              <Button
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5"
                disabled={bulkExcludeRunning || !bulkExcludeNames.trim()}
                data-testid="bulk-exclude-submit"
                onClick={async () => {
                  const mid = bulkExcludeDialog.mapping?.id;
                  if (!mid) return;
                  setBulkExcludeRunning(true);
                  try {
                    const assetId = bulkExcludeDialog.mapping?.asset_id;
                    if (bulkExcludePersistent && assetId) {
                      // Persist + retroactively reject across every existing mapping for this asset.
                      const { data } = await axios.post(
                        `${API}/ai/asset-exclusions/${assetId}`,
                        { names: bulkExcludeNames, comment: bulkExcludeComment.trim() || undefined },
                        { withCredentials: true },
                      );
                      // Adapt persistent-mode response to the same shape the result panel expects.
                      setBulkExcludeResult({
                        matched_count: data.retroactively_rejected_count,
                        matched: data.added,
                        not_found_count: 0,
                        not_found: [],
                        persistent: true,
                        added_count: data.added_count,
                        skipped_duplicate_count: data.skipped_duplicate_count,
                        total_persistent_exclusions: data.total_persistent_exclusions,
                      });
                      // Refresh local persistent list
                      try {
                        const { data: latest } = await axios.get(`${API}/ai/asset-exclusions/${assetId}`, { withCredentials: true });
                        setPersistentExclusions(latest.names || []);
                      } catch { /* non-fatal */ }
                      if (data.added_count > 0) {
                        toast.success(`Added ${data.added_count} permanent exclusion${data.added_count === 1 ? '' : 's'} (also excluded ${data.retroactively_rejected_count} matching target${data.retroactively_rejected_count === 1 ? '' : 's'} across runs)`);
                        fetchData();
                      } else {
                        toast.info('All names were already on the permanent list');
                      }
                    } else {
                      // Legacy per-mapping bulk exclude.
                      const { data } = await axios.post(
                        `${API}/ai/map-targets/${mid}/bulk-exclude`,
                        { names: bulkExcludeNames, comment: bulkExcludeComment.trim() || undefined },
                        { withCredentials: true },
                      );
                      setBulkExcludeResult(data);
                      if (data.matched_count > 0) {
                        toast.success(`Excluded ${data.matched_count} target${data.matched_count === 1 ? '' : 's'}`);
                        fetchData();
                      } else {
                        toast.warning('No targets matched the exclusion list');
                      }
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Failed to exclude');
                  } finally {
                    setBulkExcludeRunning(false);
                  }

                  // Bidirectional side-effect: if "already in touch" was ticked,
                  // ALSO union the names into the asset's `client_excluded_pharmas`
                  // so the AI Mapping → Asset Profile bridge is symmetric.
                  const assetIdForKal = bulkExcludeDialog.mapping?.asset_id;
                  if (bulkExcludeAlreadyInTouch && assetIdForKal) {
                    try {
                      // Parse the same names as the bulk exclude flow uses (commas/newlines/semicolons)
                      const parsed = bulkExcludeNames
                        .split(/[,\n;]+/)
                        .map(s => s.trim())
                        .filter(Boolean);
                      // Get current list, union, save back
                      const { data: assetData } = await axios.get(`${API}/assets/${assetIdForKal}`, { withCredentials: true });
                      const existing = assetData.client_excluded_pharmas || [];
                      const seen = new Set(existing.map(s => s.toLowerCase()));
                      const merged = [...existing];
                      for (const p of parsed) {
                        if (!seen.has(p.toLowerCase())) {
                          merged.push(p);
                          seen.add(p.toLowerCase());
                        }
                      }
                      const { data: saveRes } = await axios.patch(
                        `${API}/assets/${assetIdForKal}/client-excluded-pharmas`,
                        { pharmas: merged },
                        { withCredentials: true }
                      );
                      const addedNow = saveRes.count - existing.length;
                      if (addedNow > 0) {
                        toast.success(`Also added ${addedNow} pharma${addedNow === 1 ? '' : 's'} to "Already in touch with" on the asset profile`);
                      }
                    } catch (e) {
                      toast.error('Could not update asset "Already in touch with" list: ' + (e?.response?.data?.detail || e?.message || 'unknown'));
                    }
                  }
                }}
              >
                {bulkExcludeRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />}
                {bulkExcludeRunning ? 'Excluding…' : 'Apply Exclusions'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Excluded-list audit dialog — opens when user clicks the "X excluded" pill
          on a campaign header. Lists every excluded pharma for that mapping plus
          the reason, so the user can audit the campaign after-the-fact. */}
      <FunnelListDialog data={funnelDialog} onClose={() => setFunnelDialog(null)} />
      <ReinstateDialog data={reinstateDlg} onClose={() => setReinstateDlg(null)} onDone={onReinstated} />
      <Dialog open={!!excludedDialogMapping} onOpenChange={(open) => { if (!open) setExcludedDialogMapping(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="excluded-list-dialog">
          <DialogHeader>
            <DialogTitle>Excluded pharmas — {excludedDialogMapping?.asset_name || excludedDialogMapping?.company_name || 'campaign'}</DialogTitle>
          </DialogHeader>
          {excludedDialogMapping && (() => {
            const m = excludedDialogMapping;
            const rejectedByName = new Map();
            (m.target_companies || []).forEach(tc => {
              if (tc.approval_status !== 'rejected') return;
              const nm = (tc.company_name || '').trim();
              if (!nm) return;
              rejectedByName.set(nm.toLowerCase(), { name: nm, reason: tc.approval_comment || '', source: 'manual' });
            });
            const reasonLabels = {
              dnc_flagged: 'Marked Do-Not-Contact',
              ma_target_or_my_co: 'M&A target / your portfolio',
              ma_history: 'Past M&A history',
              my_company: 'Your own portfolio company',
              client_already_in_touch: 'Biotech is already in touch',
              not_buyer_eligible: 'Handshake OFF \u2014 not buyer-eligible',
              manually_excluded: 'Excluded via Exclude List',
            };
            const autoByName = new Map();
            Object.keys(outreachEmails || {}).forEach(k => {
              const e = outreachEmails[k];
              const reasons = e?.exclusionReasons || [];
              if (!reasons.length) return;
              const nm = (e.company_name || k).trim();
              autoByName.set(nm.toLowerCase(), {
                name: nm,
                reasons,
                details: e.exclusionDetails || {},
              });
            });
            const allNames = new Set([...rejectedByName.keys(), ...autoByName.keys()]);
            const rows = Array.from(allNames).sort().map(key => {
              const manual = rejectedByName.get(key);
              const auto = autoByName.get(key);
              return { name: (manual?.name) || (auto?.name) || key, manual, auto };
            });

            if (rows.length === 0) {
              return (
                <p className="text-sm text-slate-500 py-4" data-testid="excluded-empty">
                  No exclusions recorded for this campaign yet.
                </p>
              );
            }

            return (
              <div className="space-y-2 py-2">
                <p className="text-[11px] text-slate-500 mb-2">
                  {rows.length} pharma{rows.length === 1 ? '' : 's'} excluded — manual exclusions via the Exclude List + auto-exclusions from CRM data (DNC, your portfolio, biotech-already-in-touch, etc.).
                </p>
                {rows.map((r, i) => (
                  <div key={i} className="border border-slate-200 rounded-md p-2.5 hover:border-slate-300" data-testid={`excluded-row-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-900" data-testid={`excluded-name-${i}`}>{r.name}</span>
                      {r.manual && r.auto && (
                        <span className="text-[9px] text-slate-400 italic">Both manual + auto</span>
                      )}
                    </div>
                    {r.manual && (
                      <div className="mt-1.5 flex items-start gap-1.5">
                        <span className="px-1.5 py-0.5 rounded bg-rose-50 border border-rose-200 text-[10px] text-rose-700 font-medium shrink-0">Manual</span>
                        <p className="text-[11px] text-slate-700">{r.manual.reason || <em className="text-slate-400">no reason recorded</em>}</p>
                      </div>
                    )}
                    {r.auto && r.auto.reasons.map(rsn => (
                      <div key={rsn} className="mt-1.5 flex items-start gap-1.5">
                        <span className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-[10px] text-amber-700 font-medium shrink-0">{reasonLabels[rsn] || rsn}</span>
                        {r.auto.details[rsn] && (
                          <p className="text-[11px] text-slate-700">{r.auto.details[rsn]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })()}
          <div className="flex justify-end pt-3 border-t border-slate-200">
            <Button variant="outline" size="sm" onClick={() => setExcludedDialogMapping(null)} data-testid="excluded-dialog-close">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
